/**
 * Einstellungen — App-weite Konfiguration mit Tabs für verschiedene Bereiche.
 *
 * Features:
 * - Tabs: Allgemein, Berechnung, Data Hub, Darstellung, Benachrichtigungen, SLA, System
 * - Toggle-, Select-, Number- und Text-Eingaben je Setting
 * - System-Info-Anzeige (Version, Server, RAM, DB-Größe)
 * - SLA-Klassen CRUD, Geschäftszeiten, Pause-Stages, Bulk-Zuweisung
 *
 * @page /einstellungen
 * @roles admin
 * @i18n settings, sla, common
 * @endpoints GET /api/einstellungen, PUT /api/einstellungen/:key, GET /api/system-info,
 * GET /api/sla-klassen/alle, POST /api/sla-klassen, PUT /api/sla-klassen/:id, DELETE /api/sla-klassen/:id,
 * POST /api/kunden/sla-bulk
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, Calculator, RefreshCw, Palette, Bell, Monitor, Upload, X, Image, Paintbrush, RotateCcw, AlertTriangle, MapPin, BarChart3, Users, Clock, CalendarOff, CalendarCheck, MessageSquare, TestTube2, ShieldAlert, ShieldCheck, LayoutDashboard, GripVertical, ChevronUp, ChevronDown, Plus, Trash2, Pencil, Check, FileText, Search, Send, Phone, Copy, Download, Building2, ChevronRight, ExternalLink, FolderTree, Key, Lock, Bot, Sliders } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { ToggleSwitch } from '../components/ui';
import KioskGeraeteVerwaltung from '../components/KioskGeraeteVerwaltung';

const API = import.meta.env.VITE_API_URL || '';

const TAB_DEFS = [
 { key: 'allgemein', labelKey: 'settings.tab.general', Icon: Settings },
 { key: 'datahub', labelKey: 'settings.tab.dataHub', Icon: RefreshCw },
 { key: 'darstellung', labelKey: 'settings.tab.appearance', Icon: Palette },
 { key: 'benachrichtigungen', labelKey: 'settings.tab.notifications', Icon: Bell },
 { key: 'system', labelKey: 'settings.tab.system', Icon: Monitor },
 { key: 'design', labelKey: 'settings.design', Icon: Paintbrush },
 { key: 'adressklaerung', labelKey: 'adressklaerung.config', Icon: MapPin },
 { key: 'kunden', labelKey: 'settings.tab.customers', Icon: Users },
 { key: 'abwesenheitsarten', labelKey: 'settings.tab.absenceTypes', Icon: CalendarOff },
 { key: 'urlaub', labelKey: 'settings.tab.urlaub', Icon: CalendarCheck },
 { key: 'sms', labelKey: 'settings.tab.sms', Icon: MessageSquare },
 { key: 'home_desktop', labelKey: 'settings.tab.homeDesktop', Icon: LayoutDashboard },
 { key: 'stempeluhr', labelKey: 'settings.tab.stempeluhr', Icon: Clock },
 { key: 'chatter', labelKey: 'settings.tab.chatter', Icon: MessageSquare },
 { key: 'abteilungen', labelKey: 'settings.tab.departments', Icon: Building2 },
 { key: 'system_audit', labelKey: 'settings.tab.systemAudit', Icon: ShieldCheck },
 { key: 'pack_timer', labelKey: 'settings.tab.packTimer', Icon: Clock },
 { key: 'telegram_routing', labelKey: 'settings.tab.telegramRouting', Icon: Bot },
];

function PasswordModal({ settingKey, label, onClose, onSave }) {
 const [val, setVal] = useState('');
 const [confirm, setConfirm] = useState('');
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const submit = async () => {
 if (val.length < 1) { setError('Wert darf nicht leer sein'); return; }
 if (val !== confirm) { setError('Bestätigung stimmt nicht überein'); return; }
 setSaving(true);
 try { await onSave(val); onClose(); }
 catch (e) { setError(String(e?.message || e)); setSaving(false); }
 };
 return (
 <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onClose}>
 <div className="bg-surface-elevated border border-line rounded-card p-5 w-full max-w-md shadow-elevated" onClick={(e) => e.stopPropagation()}>
 <div className="flex items-center gap-2 mb-3">
 <Lock className="w-4 h-4 text-[var(--text-secondary)]" />
 <h3 className="text-sm font-semibold text-content">{label}</h3>
 </div>
 <p className="text-xs text-[var(--text-secondary)] mb-4">Neuer Wert wird sofort übernommen. Der alte Wert wird überschrieben.</p>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Neuer Wert</label>
 <input
 type="password"
 value={val}
 onChange={(e) => { setVal(e.target.value); setError(''); }}
 autoFocus
 className="w-full px-3 py-2 mb-3 text-sm rounded-lg border border-line bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Bestätigung</label>
 <input
 type="password"
 value={confirm}
 onChange={(e) => { setConfirm(e.target.value); setError(''); }}
 onKeyDown={(e) => e.key === 'Enter' && submit()}
 className="w-full px-3 py-2 mb-3 text-sm rounded-lg border border-line bg-surface text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 {error && <div className="text-xs text-red-500 mb-3">{error}</div>}
 <div className="flex justify-end gap-2 pt-1">
 <button onClick={onClose} disabled={saving} className="px-3 py-1.5 text-sm rounded-lg border border-line text-[var(--text-secondary)] hover:bg-surface-hover">Abbrechen</button>
 <button onClick={submit} disabled={saving || !val || !confirm} className="px-3 py-1.5 text-sm rounded-lg bg-[#338edc] text-white hover:bg-[#2d7fc6] disabled:opacity-50">Speichern</button>
 </div>
 </div>
 </div>
 );
}

function inferEffectiveType(type, value, options) {
 if (type === 'toggle' || type === 'boolean') return 'toggle';
 if (type === 'password') return 'password';
 if (type === 'select') return 'select';
 if (type === 'multiselect') return 'multiselect';
 if (type === 'number') return 'number';
 if (type === 'json' || type === 'html') return 'textarea';
 if (typeof value === 'boolean') return 'toggle';
 if (typeof value === 'number' && (!options || options.length === 0)) return 'number';
 if (Array.isArray(value)) return 'textarea';
 if (value && typeof value === 'object') return 'textarea';
 return 'text';
}

function SettingRow({ setting, onChange, saving, t }) {
 const { key, value, label, type, options, description } = setting;
 const isSaving = saving === key;
 const [pwModalOpen, setPwModalOpen] = useState(false);
 const effType = inferEffectiveType(type, value, options);
 const boolValue = value === true || value === 'true';

 return (
 <div className="flex items-start justify-between gap-6 py-4 border-b border-line last:border-0">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-content">{label}</div>
 {description && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{description}</div>}
 </div>
 <div className="flex-shrink-0 w-56">
 {effType === 'toggle' ? (
 <ToggleSwitch checked={boolValue} onChange={() => onChange(key, !boolValue)} disabled={isSaving} />
 ) : effType === 'select' ? (
 <select
 value={typeof value === 'string' ? value : String(value ?? '')}
 onChange={(e) => onChange(key, e.target.value)}
 disabled={isSaving}
 className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 >
 {(options || []).map(opt => (
 <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
 ))}
 </select>
 ) : effType === 'number' ? (
 <input
 type="number"
 value={value ?? ''}
 onChange={(e) => onChange(key, Number(e.target.value))}
 disabled={isSaving}
 className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 ) : effType === 'multiselect' ? (
 <div className="flex flex-wrap gap-1.5">
 {(options || []).map(opt => {
 const selected = Array.isArray(value) && value.includes(opt);
 return (
 <button
 key={opt}
 onClick={() => {
 const next = selected
 ? (value || []).filter(v => v !== opt)
 : [...(value || []), opt];
 onChange(key, next);
 }}
 disabled={isSaving}
 className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
 selected
 ? 'bg-primary-500/20 text-primary-400 border border-primary-500/40'
 : 'bg-surface-elevated text-[var(--text-secondary)] border border-line hover:border-line-hover'
 }`}
 >
 {opt}
 </button>
 );
 })}
 </div>
 ) : effType === 'password' ? (
 <div className="flex items-center gap-2">
 <div className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-line bg-surface text-[var(--text-tertiary)] font-mono tracking-widest select-none">
 {value ? '••••••••' : <span className="italic text-[var(--text-muted)]">nicht gesetzt</span>}
 </div>
 <button
 onClick={() => setPwModalOpen(true)}
 disabled={isSaving}
 className="px-3 py-1.5 text-xs rounded-lg border border-line text-[var(--text-secondary)] hover:bg-surface-hover inline-flex items-center gap-1"
 >
 <Key className="w-3 h-3" /> Ändern
 </button>
 {pwModalOpen && (
 <PasswordModal
 settingKey={key}
 label={label}
 onClose={() => setPwModalOpen(false)}
 onSave={(v) => onChange(key, v)}
 />
 )}
 </div>
 ) : effType === 'textarea' ? (
 <textarea
 value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
 onChange={(e) => {
 const raw = e.target.value;
 try { onChange(key, JSON.parse(raw)); }
 catch { onChange(key, raw); }
 }}
 disabled={isSaving}
 rows={4}
 className="w-full px-3 py-1.5 text-xs font-mono rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 ) : (
 <input
 type="text"
 value={value ?? ''}
 onChange={(e) => onChange(key, e.target.value)}
 disabled={isSaving}
 className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 )}
 {isSaving && (
 <div className="text-[10px] text-primary-400 mt-1">{t('common.saving')}</div>
 )}
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// BRANDING — Logo Upload (Light + Dark)
// ════════════════════════════════════════════════════════════════

function LogoUploadCard({ variant, label, description, bgClass, logoStatus, onUpload, onDelete, t }) {
 const inputRef = useRef(null);
 const [dragging, setDragging] = useState(false);
 const [uploading, setUploading] = useState(false);

 const handleFile = async (file) => {
 if (!file) return;
 const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
 if (!allowed.includes(file.type)) return;
 if (file.size > 5 * 1024 * 1024) return;
 setUploading(true);
 try {
 await onUpload(variant, file);
 } finally {
 setUploading(false);
 }
 };

 const handleDrop = (e) => {
 e.preventDefault();
 setDragging(false);
 const file = e.dataTransfer?.files?.[0];
 if (file) handleFile(file);
 };

 const info = logoStatus?.[variant];

 return (
 <div className="flex-1 min-w-[280px]">
 <div className="text-sm font-medium text-content mb-1">{label}</div>
 <div className="text-xs text-[var(--text-secondary)] mb-3">{description}</div>

 {/* Preview / Dropzone */}
 <div
 className={`relative rounded-lg border-2 border-dashed transition-colors ${
 dragging ? 'border-primary-500 bg-primary-500/5' : 'border-line hover:border-[#338edc]/40'
 } ${bgClass} flex items-center justify-center`}
 style={{ minHeight: '120px' }}
 onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
 onDragLeave={() => setDragging(false)}
 onDrop={handleDrop}
 onClick={() => !uploading && inputRef.current?.click()}
 role="button"
 tabIndex={0}
 >
 <input
 ref={inputRef}
 type="file"
 accept="image/png,image/jpeg,image/webp,image/svg+xml"
 className="hidden"
 onChange={(e) => handleFile(e.target.files?.[0])}
 />

 {uploading ? (
 <div className="flex flex-col items-center gap-2 py-4">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 <span className="text-xs text-[var(--text-secondary)]">{t('settings.branding.uploading')}</span>
 </div>
 ) : info?.exists ? (
 <div className="relative w-full flex items-center justify-center py-6 px-4">
 <img
 src={`${API}${info.url}`}
 alt={label}
 className="max-h-16 max-w-[200px] object-contain"
 />
 {/* Delete button */}
 <button
 onClick={(e) => {
 e.stopPropagation();
 if (confirm(t('settings.branding.deleteConfirm'))) onDelete(variant);
 }}
 className="absolute top-2 right-2 p-1 rounded-lg bg-surface border border-line hover:bg-red-50 hover:border-red-200 dark:hover:bg-red-900/20 dark:hover:border-red-800 transition-colors"
 title="Delete"
 >
 <X className="w-3.5 h-3.5 text-[var(--text-secondary)] hover:text-red-500" />
 </button>
 </div>
 ) : (
 <div className="flex flex-col items-center gap-2 py-6 cursor-pointer">
 <Upload className="w-6 h-6 text-[var(--text-tertiary)]" />
 <span className="text-xs text-[var(--text-secondary)]">{t('settings.branding.dropzone')}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">{t('settings.branding.formats')}</span>
 </div>
 )}
 </div>
 </div>
 );
}

function BrandingSection({ t, showToast }) {
 const [logoStatus, setLogoStatus] = useState(null);
 const [iconStatus, setIconStatus] = useState(null);
 const [iconUploading, setIconUploading] = useState(false);
 const iconInputRef = useRef(null);

 const loadStatus = useCallback(() => {
 fetch(`${API}/api/einstellungen/logo`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setLogoStatus(d))
 .catch(() => {});
 fetch(`${API}/api/einstellungen/sidebar-icon`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => setIconStatus(d))
 .catch(() => {});
 }, []);

 useEffect(() => { loadStatus(); }, [loadStatus]);

 const handleUpload = async (variant, file) => {
 const formData = new FormData();
 formData.append('file', file);
 formData.append('variant', variant);
 try {
 const res = await fetch(`${API}/api/einstellungen/logo`, {
 method: 'POST',
 credentials: 'include',
 body: formData,
 });
 if (res.ok) {
 showToast(t('settings.branding.uploadSuccess'));
 loadStatus();
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('settings.branding.uploadError'));
 }
 } catch {
 showToast(t('settings.branding.uploadError'));
 }
 };

 const handleDelete = async (variant) => {
 try {
 const res = await fetch(`${API}/api/einstellungen/logo?variant=${variant}`, {
 method: 'DELETE',
 credentials: 'include',
 });
 if (res.ok) {
 showToast(t('settings.branding.deleted'));
 loadStatus();
 }
 } catch {
 showToast(t('settings.branding.uploadError'));
 }
 };

 const handleIconUpload = async (file) => {
 if (!file) return;
 const allowed = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
 if (!allowed.includes(file.type)) return;
 if (file.size > 500 * 1024) { showToast('Max 500KB'); return; }
 setIconUploading(true);
 try {
 const formData = new FormData();
 formData.append('file', file);
 const res = await fetch(`${API}/api/einstellungen/sidebar-icon`, {
 method: 'POST', credentials: 'include', body: formData,
 });
 if (res.ok) {
 showToast(t('settings.branding.uploadSuccess'));
 loadStatus();
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('settings.branding.uploadError'));
 }
 } catch {
 showToast(t('settings.branding.uploadError'));
 }
 setIconUploading(false);
 };

 const handleIconDelete = async () => {
 try {
 const res = await fetch(`${API}/api/einstellungen/sidebar-icon`, {
 method: 'DELETE', credentials: 'include',
 });
 if (res.ok) {
 showToast(t('settings.branding.deleted'));
 loadStatus();
 }
 } catch {
 showToast(t('settings.branding.uploadError'));
 }
 };

 return (
 <div className="bg-surface-elevated rounded-card border border-line mb-4">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <Image className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('settings.branding.title')}</h2>
 </div>
 <div className="px-5 py-4">
 <p className="text-xs text-[var(--text-secondary)] mb-4">{t('settings.branding.subtitle')}</p>
 <div className="flex gap-6 flex-wrap">
 <LogoUploadCard
 variant="default"
 label={t('settings.branding.lightLogo')}
 description={t('settings.branding.lightDesc')}
 bgClass="bg-white"
 logoStatus={logoStatus}
 onUpload={handleUpload}
 onDelete={handleDelete}
 t={t}
 />
 <LogoUploadCard
 variant="dark"
 label={t('settings.branding.darkLogo')}
 description={t('settings.branding.darkDesc')}
 bgClass="bg-[#1A1A2E]"
 logoStatus={logoStatus}
 onUpload={handleUpload}
 onDelete={handleDelete}
 t={t}
 />
 </div>

 {/* Sidebar Icon (1:1) */}
 <div className="mt-6 pt-5 border-t border-line">
 <div className="text-sm font-medium text-[var(--text-primary)] mb-1">{t('settings.branding.sidebarIcon')}</div>
 <p className="text-xs text-[var(--text-tertiary)] mb-3">{t('settings.branding.sidebarIconDesc')}</p>
 <div className="flex items-center gap-4">
 {iconUploading ? (
 <div className="w-10 h-10 rounded-lg border border-[var(--border-card)] flex items-center justify-center">
 <div className="w-4 h-4 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 ) : iconStatus?.exists ? (
 <img
 src={`${API}${iconStatus.url}`}
 alt="Sidebar Icon"
 className="w-10 h-10 rounded-lg border border-[var(--border-card)] object-contain bg-white"
 />
 ) : (
 <div className="w-10 h-10 rounded-lg border border-dashed border-[var(--border-card)] flex items-center justify-center">
 <Upload className="w-4 h-4 text-[var(--text-tertiary)]" />
 </div>
 )}
 <div className="flex gap-2">
 <label className="text-xs font-medium cursor-pointer px-3 py-1.5 border border-[var(--border-card)] rounded-lg hover:bg-[var(--bg-card-hover)] text-[#338edc]">
 {t('settings.branding.change')}
 <input
 ref={iconInputRef}
 type="file"
 accept="image/png,image/svg+xml,image/jpeg,image/webp"
 className="hidden"
 onChange={e => handleIconUpload(e.target.files?.[0])}
 />
 </label>
 {iconStatus?.exists && (
 <button
 onClick={() => { if (confirm(t('settings.branding.deleteConfirm'))) handleIconDelete(); }}
 className="text-xs px-3 py-1.5 border border-[var(--border-card)] rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
 >
 {t('settings.branding.remove')}
 </button>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}

function BerechnungsgrundlagenCard({ t, showToast }) {
 const [value, setValue] = useState('');
 const [initialValue, setInitialValue] = useState('');
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');

 useEffect(() => {
 fetch(`${API}/api/einstellungen/jahres_arbeitsstunden`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => {
 const v = d?.value;
 const num = typeof v === 'string' ? Number(v.replace(/"/g, '')) : Number(v);
 const s = Number.isFinite(num) ? String(num) : '1738';
 setValue(s);
 setInitialValue(s);
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const save = async () => {
 const num = Number(value);
 if (!Number.isFinite(num) || num < 1) {
 setError(t('settings.calc.jahresstunden.invalidMin'));
 return;
 }
 if (String(num) === initialValue) return;
 setError('');
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/einstellungen/jahres_arbeitsstunden`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ value: num }),
 });
 if (res.ok) {
 setInitialValue(String(num));
 showToast(t('common.saved'));
 } else {
 const e = await res.json().catch(() => ({}));
 setError(e.error || t('common.saveError'));
 }
 } catch {
 setError(t('common.networkError'));
 } finally {
 setSaving(false);
 }
 };

 return (
 <div className="bg-surface-elevated rounded-card border border-line mb-4">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <Calculator className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('settings.calc.title')}</h2>
 </div>
 <div className="px-5 py-4">
 <div className="flex items-start justify-between gap-6">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-content">{t('settings.calc.jahresstunden.label')}</div>
 <div className="text-xs text-[var(--text-secondary)] mt-0.5">{t('settings.calc.jahresstunden.hint')}</div>
 </div>
 <div className="flex-shrink-0 w-56">
 <input
 type="number"
 min={1}
 step={1}
 value={value}
 onChange={(e) => { setValue(e.target.value); setError(''); }}
 onBlur={save}
 onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
 disabled={loading || saving}
 className={`w-full px-3 py-1.5 text-sm rounded-lg border bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 ${error ? 'border-red-500' : 'border-line'}`}
 />
 {saving && <div className="text-[10px] text-primary-400 mt-1">{t('common.saving')}</div>}
 {error && <div className="text-[10px] text-red-500 mt-1">{error}</div>}
 </div>
 </div>
 </div>
 </div>
 );
}

function SystemInfoTab({ t }) {
 const [info, setInfo] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`${API}/api/system-info`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setInfo(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 if (loading) {
 return (
 <div className="flex items-center justify-center h-48">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 if (!info) {
 return <div className="text-sm text-[var(--text-secondary)] py-8 text-center">{t('settings.system.loadError')}</div>;
 }

 const InfoBlock = ({ title, items }) => (
 <div className="bg-surface-elevated rounded-card border border-line p-5">
 <h3 className="text-sm font-semibold text-content mb-3">{title}</h3>
 <div className="space-y-2">
 {items.map(([k, v]) => (
 <div key={k} className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{k}</span>
 <span className="text-content font-mono">{v}</span>
 </div>
 ))}
 </div>
 </div>
 );

 const formatUptime = (sec) => {
 const d = Math.floor(sec / 86400);
 const h = Math.floor((sec % 86400) / 3600);
 const m = Math.floor((sec % 3600) / 60);
 if (d > 0) return `${d}d ${h}h ${m}m`;
 if (h > 0) return `${h}h ${m}m`;
 return `${m}m`;
 };

 return (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
 <InfoBlock
 title={t('settings.system.application')}
 items={[
 ['Version', `v${info.app.version}`],
 ['Node.js', info.app.node],
 ['Uptime', formatUptime(info.app.uptime_seconds)],
 ['RAM (RSS)', `${info.app.memory_mb} MB`],
 ['Timezone', info.app.timezone],
 ['Environment', info.app.env],
 ]}
 />
 <InfoBlock
 title={t('settings.system.server')}
 items={[
 ['Hostname', info.server.hostname],
 ['Platform', info.server.platform],
 ['CPUs', String(info.server.cpus)],
 [t('settings.system.ramTotal'), `${info.server.total_memory_gb} GB`],
 [t('settings.system.ramFree'), `${info.server.free_memory_gb} GB`],
 ['Load (1/5/15)', info.server.load_avg.join(' / ')],
 ]}
 />
 <InfoBlock
 title={t('settings.system.database')}
 items={[
 ['Version', info.database.version],
 [t('settings.system.size'), info.database.size],
 [t('settings.system.tables'), String(info.database.tables)],
 ...Object.entries(info.database.row_counts || {}).map(([t, c]) => [
 t, c !== null ? c.toLocaleString('de-AT') : '–',
 ]),
 ]}
 />
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// THEME EDITOR — Visual Theme Editor Tab
// ════════════════════════════════════════════════════════════════

const CATEGORY_ORDER = ['font', 'accent', 'backgrounds', 'text', 'borders', 'status'];

const FONT_OPTIONS = [
 { value: 'Inter', label: 'Inter', desc: 'Modern, clean' },
 { value: 'DM Sans', label: 'DM Sans', desc: 'Geometric, friendly' },
 { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans', desc: 'Geometric, friendly' },
 { value: 'Outfit', label: 'Outfit', desc: 'Modern, geometric' },
 { value: 'Nunito Sans', label: 'Nunito Sans', desc: 'Rounded, soft' },
 { value: 'Source Sans 3', label: 'Source Sans 3', desc: 'Classic, neutral' },
];

function FontField({ value, defaultValue, onChange, t }) {
 const current = value || defaultValue || 'Inter';
 const previewFont = `'${current}', sans-serif`;

 // Load font for preview
 useEffect(() => {
 const link = document.createElement('link');
 link.rel = 'stylesheet';
 link.href = `https://fonts.googleapis.com/css2?family=${current.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
 document.head.appendChild(link);
 return () => { try { document.head.removeChild(link); } catch {} };
 }, [current]);

 return (
 <div className="py-3 space-y-3">
 <select
 value={current}
 onChange={e => onChange('font-family', e.target.value)}
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 >
 {FONT_OPTIONS.map(f => (
 <option key={f.value} value={f.value}>{f.label} — {f.desc}</option>
 ))}
 </select>
 <div className="px-4 py-3 rounded-lg border border-[var(--border-card)] bg-[var(--bg-card)]" style={{ fontFamily: previewFont }}>
 <div className="text-lg font-semibold text-[var(--text-primary)] mb-1" style={{ fontFamily: previewFont }}>{current}</div>
 <div className="text-sm text-[var(--text-secondary)]" style={{ fontFamily: previewFont }}>{t('settings.fontPreview')}</div>
 <div className="text-xs text-[var(--text-tertiary)] mt-1" style={{ fontFamily: previewFont }}>ABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789</div>
 </div>
 </div>
 );
}

function ColorField({ token, label, value, defaultValue, onChange }) {
 const pickerRef = useRef(null);
 const [inputVal, setInputVal] = useState(value || defaultValue);
 const displayVal = value || defaultValue;
 const isHex = /^#[0-9A-Fa-f]{6}$/.test(displayVal);

 useEffect(() => {
 setInputVal(value || defaultValue);
 }, [value, defaultValue]);

 const handleHexInput = (raw) => {
 setInputVal(raw);
 // Auto-prepend # if user types 6 hex chars without it
 let val = raw.trim();
 if (/^[0-9A-Fa-f]{6}$/.test(val)) val = '#' + val;
 if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
 onChange(token, val);
 }
 };

 const handlePickerChange = (e) => {
 const val = e.target.value;
 setInputVal(val);
 onChange(token, val);
 };

 return (
 <div className="flex items-center gap-3 py-2.5 border-b border-[var(--border-subtle)] last:border-0">
 {/* Color preview swatch */}
 <div
 className="w-6 h-6 rounded-lg border border-[var(--border-card)] flex-shrink-0 cursor-pointer"
 style={{ backgroundColor: displayVal }}
 onClick={() => pickerRef.current?.click()}
 />
 {/* Label */}
 <div className="flex-1 min-w-0">
 <div className="text-sm text-[var(--text-primary)]">{label}</div>
 </div>
 {/* Hex input */}
 <input
 type="text"
 value={inputVal}
 onChange={(e) => handleHexInput(e.target.value)}
 className="w-[120px] px-2 py-1 text-xs font-mono rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 spellCheck={false}
 />
 {/* Native color picker (hidden behind icon) */}
 <div className="relative flex-shrink-0">
 <input
 ref={pickerRef}
 type="color"
 value={isHex ? displayVal : '#338edc'}
 onChange={handlePickerChange}
 className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
 />
 <Palette className="w-4 h-4 text-[var(--text-tertiary)] pointer-events-none" />
 </div>
 </div>
 );
}

function ThemePreview({ colors }) {
 const get = (token, fallback) => colors[token] || fallback;
 const fontName = get('font-family', 'Inter');
 const fontStyle = { fontFamily: `'${fontName}', -apple-system, BlinkMacSystemFont, sans-serif` };

 return (
 <div
 className="rounded-xl border overflow-hidden text-sm"
 style={{
 backgroundColor: get('bg-primary', '#F4F5FA'),
 borderColor: get('border-card', '#E5E7EB'),
 color: get('text-primary', '#1A1A2E'),
 ...fontStyle,
 }}
 >
 {/* Mini Sidebar */}
 <div className="flex">
 <div
 className="w-[140px] flex-shrink-0 p-3 space-y-1"
 style={{
 backgroundColor: get('sidebar-bg', '#FFFFFF'),
 borderRight: `1px solid ${get('border-card', '#E5E7EB')}`,
 }}
 >
 <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold"
 style={{
 color: get('sidebar-active', '#338edc'),
 borderLeft: `3px solid ${get('sidebar-active', '#338edc')}`,
 }}>
 Dashboard
 </div>
 <div className="px-2 py-1.5 text-xs" style={{ color: get('sidebar-text', '#6B7280') }}>
 Customers
 </div>
 <div className="px-2 py-1.5 text-xs" style={{ color: get('sidebar-text', '#6B7280') }}>
 Reports
 </div>
 </div>

 {/* Content area */}
 <div className="flex-1 p-3 space-y-3">
 {/* KPI Card */}
 <div className="rounded-lg p-3" style={{
 backgroundColor: get('bg-card', '#FFFFFF'),
 border: `1px solid ${get('border-card', '#E5E7EB')}`,
 }}>
 <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: get('text-secondary', '#6B7280') }}>
 Revenue
 </div>
 <div className="text-lg font-bold" style={{ color: get('text-primary', '#1A1A2E') }}>
 48.230 &euro;
 </div>
 <div className="text-[10px] mt-0.5" style={{ color: get('text-tertiary', '#9CA3AF') }}>
 +12.3% vs last month
 </div>
 </div>

 {/* Buttons */}
 <div className="flex gap-2">
 <button className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
 style={{ backgroundColor: get('primary', '#338edc') }}>
 Primary
 </button>
 <button className="px-3 py-1.5 rounded-lg text-xs font-medium"
 style={{
 border: `1px solid ${get('border-card', '#E5E7EB')}`,
 backgroundColor: get('bg-card', '#FFFFFF'),
 color: get('text-primary', '#1A1A2E'),
 }}>
 Ghost
 </button>
 </div>

 {/* Mini Table */}
 <div className="rounded-lg overflow-hidden" style={{
 backgroundColor: get('bg-card', '#FFFFFF'),
 border: `1px solid ${get('border-card', '#E5E7EB')}`,
 }}>
 <div className="flex text-[9px] uppercase tracking-wider px-3 py-1.5"
 style={{ color: get('text-tertiary', '#9CA3AF'), borderBottom: `1px solid ${get('border-subtle', '#F3F4F6')}` }}>
 <span className="flex-1">Name</span>
 <span className="w-16 text-right">Status</span>
 </div>
 {[
 { name: 'Arigato GmbH', status: 'Paid', color: get('status-paid', '#10B981') },
 { name: 'BrandHQ', status: 'Pending', color: get('status-pending', '#F59E0B') },
 { name: 'FreshCo', status: 'Error', color: get('status-error', '#EF4444') },
 ].map((row, i) => (
 <div key={i} className="flex items-center px-3 py-1.5 text-xs"
 style={{ borderBottom: `1px solid ${get('border-subtle', '#F3F4F6')}` }}>
 <span className="flex-1" style={{ color: get('text-primary', '#1A1A2E') }}>{row.name}</span>
 <span className="w-16 text-right text-[10px] font-medium" style={{ color: row.color }}>{row.status}</span>
 </div>
 ))}
 </div>

 {/* Mini Chart Bars */}
 <div className="flex items-end gap-1 h-10 px-1">
 {[40, 70, 55, 85, 60].map((h, i) => (
 <div key={i} className="flex-1 rounded-t-sm" style={{
 height: `${h}%`,
 backgroundColor: i % 2 === 0 ? get('primary', '#338edc') : get('accent-cyan', '#22D3EE'),
 opacity: 0.8,
 }} />
 ))}
 </div>

 {/* Input */}
 <div className="rounded-lg px-3 py-1.5 text-xs" style={{
 backgroundColor: get('bg-input', '#FFFFFF'),
 border: `1px solid ${get('border-input', '#E5E7EB')}`,
 color: get('text-tertiary', '#9CA3AF'),
 }}>
 Search...
 </div>
 </div>
 </div>
 </div>
 );
}

/**
 * ThemePresetPicker — v2.308.2 (#714) + Custom Theme Editor v2.310.0 (#715)
 * Zeigt alle Themes als Cards. Ermöglicht Auswahl, Duplizieren, Bearbeiten,
 * Löschen, Export und Import von Custom-Themes.
 */

// Variable-Schema für den Custom Theme Editor — gruppiert nach Kategorie
const THEME_VAR_GROUPS = [
 {
 key: 'accent',
 labelKey: 'settings.accentColors',
 fallbackLabel: 'Akzent',
 vars: [
 { key: '--primary', label: 'Primary' },
 { key: '--primary-hover', label: 'Primary Hover' },
 { key: '--primary-light', label: 'Primary Light' },
 { key: '--accent-blue', label: 'Accent Blue' },
 { key: '--accent-cyan', label: 'Accent Cyan' },
 ],
 },
 {
 key: 'backgrounds',
 labelKey: 'settings.backgrounds',
 fallbackLabel: 'Hintergründe',
 vars: [
 { key: '--bg-page', label: 'Page' },
 { key: '--bg-card', label: 'Card' },
 { key: '--bg-card-hover', label: 'Card Hover' },
 { key: '--bg-inset', label: 'Inset' },
 { key: '--bg-topbar', label: 'Topbar' },
 ],
 },
 {
 key: 'sidebar',
 labelKey: 'settings.sidebar',
 fallbackLabel: 'Sidebar',
 vars: [
 { key: '--sidebar-bg', label: 'Background' },
 { key: '--sidebar-text', label: 'Text' },
 { key: '--sidebar-active', label: 'Active' },
 { key: '--sidebar-bg-hover', label: 'BG Hover' },
 { key: '--sidebar-text-hover', label: 'Text Hover' },
 { key: '--sidebar-bg-active', label: 'BG Active' },
 { key: '--sidebar-text-active', label: 'Text Active' },
 { key: '--sidebar-border', label: 'Border' },
 { key: '--sidebar-category', label: 'Category' },
 { key: '--sidebar-group-title', label: 'Group Title' },
 { key: '--sidebar-logo-text', label: 'Logo Text' },
 { key: '--sidebar-version', label: 'Version' },
 { key: '--sidebar-version-text', label: 'Version Text' },
 { key: '--sidebar-focus-bg', label: 'Focus BG' },
 { key: '--sidebar-focus-text', label: 'Focus Text' },
 { key: '--sidebar-focus-active-bg', label: 'Focus Active BG' },
 { key: '--sidebar-focus-active-text', label: 'Focus Active Text' },
 { key: '--sidebar-footer-hover', label: 'Footer Hover' },
 { key: '--sidebar-footer-text', label: 'Footer Text' },
 ],
 },
 {
 key: 'text',
 labelKey: 'settings.textColors',
 fallbackLabel: 'Text',
 vars: [
 { key: '--text-primary', label: 'Primary' },
 { key: '--text-secondary', label: 'Secondary' },
 { key: '--text-tertiary', label: 'Tertiary' },
 { key: '--text-muted', label: 'Muted' },
 ],
 },
 {
 key: 'borders',
 labelKey: 'settings.borders',
 fallbackLabel: 'Borders',
 vars: [
 { key: '--border-card', label: 'Card' },
 { key: '--border-input', label: 'Input' },
 { key: '--border-table', label: 'Table' },
 ],
 },
];

const THEME_FONT_OPTIONS = [
 'DM Sans', 'Inter', 'Plus Jakarta Sans', 'Outfit', 'Nunito Sans', 'Source Sans 3', 'Roboto', 'Lato', 'Poppins',
];

// Default-Variablen für ein neues Theme (basieren auf WeShip Classic)
const DEFAULT_THEME_VARIABLES = {
 light: {
 '--font-family': 'DM Sans',
 '--primary': '#338edc',
 '--primary-hover': '#2b7bc4',
 '--primary-light': '#EBF4FD',
 '--accent-blue': '#6366F1',
 '--accent-cyan': '#22D3EE',
 '--bg-page': '#F4F5FA',
 '--bg-card': '#FFFFFF',
 '--bg-card-hover': '#FAFAFE',
 '--bg-inset': '#F8F9FC',
 '--bg-topbar': 'rgba(244,245,250,0.85)',
 '--sidebar-bg': '#FFFFFF',
 '--sidebar-text': '#4B5563',
 '--sidebar-active': '#338edc',
 '--sidebar-bg-hover': '#F9FAFB',
 '--sidebar-text-hover': '#1A1A2E',
 '--sidebar-bg-active': 'rgba(51,142,220,0.08)',
 '--sidebar-text-active': '#338edc',
 '--sidebar-border': '#F3F4F6',
 '--sidebar-category': '#6B7280',
 '--sidebar-group-title': '#6B7280',
 '--sidebar-logo-text': '#1A1A2E',
 '--sidebar-version': '#6B7280',
 '--sidebar-version-text': '#6B7280',
 '--sidebar-focus-bg': '#F3F4F6',
 '--sidebar-focus-text': '#4B5563',
 '--sidebar-focus-active-bg': '#EBF4FD',
 '--sidebar-focus-active-text': '#338edc',
 '--sidebar-footer-hover': '#1A1A2E',
 '--sidebar-footer-text': '#6B7280',
 '--text-primary': '#1A1A2E',
 '--text-secondary': '#4B5563',
 '--text-tertiary': '#6B7280',
 '--text-muted': '#9CA3AF',
 '--border-card': '#E5E7EB',
 '--border-input': '#E5E7EB',
 '--border-table': '#F3F4F6',
 },
 dark: {
 '--font-family': 'DM Sans',
 '--primary': '#60A5FA',
 '--primary-hover': '#3B82F6',
 '--primary-light': 'rgba(96,165,250,0.1)',
 '--accent-blue': '#818CF8',
 '--accent-cyan': '#22D3EE',
 '--bg-page': '#111317',
 '--bg-card': '#1A1D23',
 '--bg-card-hover': '#22262E',
 '--bg-inset': '#22262E',
 '--bg-topbar': 'rgba(17,19,23,0.85)',
 '--sidebar-bg': '#1A1D23',
 '--sidebar-text': '#9CA3AF',
 '--sidebar-active': '#60A5FA',
 '--sidebar-bg-hover': '#22262E',
 '--sidebar-text-hover': '#E5E7EB',
 '--sidebar-bg-active': 'rgba(96,165,250,0.12)',
 '--sidebar-text-active': '#60A5FA',
 '--sidebar-border': 'rgba(255,255,255,0.08)',
 '--sidebar-category': '#6B7280',
 '--sidebar-group-title': '#6B7280',
 '--sidebar-logo-text': '#FFFFFF',
 '--sidebar-version': '#6B7280',
 '--sidebar-version-text': '#6B7280',
 '--sidebar-focus-bg': '#22262E',
 '--sidebar-focus-text': '#9CA3AF',
 '--sidebar-focus-active-bg': '#2D3240',
 '--sidebar-focus-active-text': '#E5E7EB',
 '--sidebar-footer-hover': '#E5E7EB',
 '--sidebar-footer-text': '#6B7280',
 '--text-primary': '#F3F4F6',
 '--text-secondary': '#9CA3AF',
 '--text-tertiary': '#6B7280',
 '--text-muted': '#4B5563',
 '--border-card': 'rgba(255,255,255,0.08)',
 '--border-input': 'rgba(255,255,255,0.1)',
 '--border-table': 'rgba(255,255,255,0.05)',
 },
};

function getThemePreviewColors(theme, mode) {
 const vars = (theme?.variables?.[mode] || theme?.variables?.light || {});
 const get = (key) => vars[`--${key}`] || vars[key] || '#E5E7EB';
 return {
 primary: get('primary'),
 sidebar: get('sidebar-bg'),
 sidebarText: get('sidebar-text'),
 sidebarActive: get('sidebar-text-active'),
 content: get('bg-card'),
 page: get('bg-page'),
 textPrimary: get('text-primary'),
 textSecondary: get('text-secondary'),
 border: get('border-card'),
 };
}

function ThemePresetPicker({ t, showToast }) {
 const { theme, activeTheme, setActiveThemeById } = useTheme();
 const [themes, setThemes] = useState([]);
 const [loading, setLoading] = useState(true);
 const [switching, setSwitching] = useState(null);
 const [editingTheme, setEditingTheme] = useState(null); // { id?, name, variables, ... } or null
 const [busyId, setBusyId] = useState(null);
 const importInputRef = useRef(null);

 const loadThemes = useCallback(() => {
 setLoading(true);
 fetch(`${API}/api/themes`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d?.themes) setThemes(d.themes); })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { loadThemes(); }, [loadThemes]);

 const handleSelect = async (themeId) => {
 setSwitching(themeId);
 const ok = await setActiveThemeById(themeId);
 setSwitching(null);
 if (ok) {
 showToast(t('settings.themeApplied') || 'Theme aktiviert');
 } else {
 showToast(t('common.saveError') || 'Fehler');
 }
 };

 const handleNew = () => {
 setEditingTheme({
 id: null,
 name: '',
 description: '',
 variables: JSON.parse(JSON.stringify(DEFAULT_THEME_VARIABLES)),
 is_preset: false,
 });
 };

 const handleEdit = (th) => {
 if (th.is_preset) return;
 // Ensure both modes exist
 const variables = {
 light: { ...DEFAULT_THEME_VARIABLES.light, ...(th.variables?.light || {}) },
 dark: { ...DEFAULT_THEME_VARIABLES.dark, ...(th.variables?.dark || {}) },
 };
 setEditingTheme({
 id: th.id,
 name: th.name,
 description: th.description || '',
 variables,
 is_preset: false,
 });
 };

 const handleDuplicate = async (th) => {
 setBusyId(th.id);
 try {
 const res = await fetch(`${API}/api/themes/${th.id}/duplicate`, {
 method: 'POST',
 credentials: 'include',
 });
 if (res.ok) {
 const data = await res.json();
 showToast(t('settings.themeDuplicated') || 'Theme dupliziert');
 loadThemes();
 // Direkt in den Editor öffnen
 if (data?.theme) {
 handleEdit(data.theme);
 }
 } else {
 showToast(t('common.saveError') || 'Fehler');
 }
 } catch {
 showToast(t('common.networkError') || 'Netzwerkfehler');
 } finally {
 setBusyId(null);
 }
 };

 const handleDelete = async (th) => {
 if (th.is_preset) return;
 if (!confirm(t('settings.themeDeleteConfirm')?.replace('{name}', th.name) || `Theme "${th.name}" wirklich löschen?`)) return;
 setBusyId(th.id);
 try {
 const res = await fetch(`${API}/api/themes/${th.id}`, {
 method: 'DELETE',
 credentials: 'include',
 });
 if (res.ok) {
 showToast(t('settings.themeDeleted') || 'Theme gelöscht');
 loadThemes();
 } else {
 showToast(t('common.saveError') || 'Fehler');
 }
 } catch {
 showToast(t('common.networkError') || 'Netzwerkfehler');
 } finally {
 setBusyId(null);
 }
 };

 const handleExport = (th) => {
 const data = {
 name: th.name,
 description: th.description || '',
 variables: th.variables || {},
 exported_at: new Date().toISOString(),
 exported_from: 'WeShip v2',
 };
 const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `theme-${th.slug || th.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 URL.revokeObjectURL(url);
 showToast(t('settings.themeExported') || 'Theme exportiert');
 };

 const handleImport = () => {
 importInputRef.current?.click();
 };

 const handleImportFile = (e) => {
 const file = e.target.files?.[0];
 e.target.value = '';
 if (!file) return;
 const reader = new FileReader();
 reader.onload = (ev) => {
 try {
 const data = JSON.parse(ev.target.result);
 if (!data.name || !data.variables) {
 showToast(t('settings.themeImportInvalid') || 'Ungültige Theme-Datei');
 return;
 }
 // Open in editor for review
 setEditingTheme({
 id: null,
 name: data.name + ' (Import)',
 description: data.description || '',
 variables: {
 light: { ...DEFAULT_THEME_VARIABLES.light, ...(data.variables.light || {}) },
 dark: { ...DEFAULT_THEME_VARIABLES.dark, ...(data.variables.dark || {}) },
 },
 is_preset: false,
 });
 } catch {
 showToast(t('settings.themeImportInvalid') || 'Ungültige Theme-Datei');
 }
 };
 reader.readAsText(file);
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-32">
 <div className="animate-spin h-5 w-5 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
 </div>
 );
 }

 return (
 <>
 <input
 ref={importInputRef}
 type="file"
 accept="application/json,.json"
 className="hidden"
 onChange={handleImportFile}
 />
 <div className="bg-[var(--bg-card)] rounded-card border border-[var(--border-default)]">
 <div className="px-5 py-3 border-b border-[var(--border-default)] flex items-center justify-between gap-3">
 <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {t('settings.themeChoose') || 'Theme auswählen'}
 </h3>
 <div className="flex items-center gap-2">
 <span className="text-[10px] text-[var(--text-tertiary)]">{themes.length} {t('settings.themesAvailable') || 'verfügbar'}</span>
 <button
 onClick={handleImport}
 className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)] transition-colors"
 title={t('settings.themeImport') || 'Importieren'}
 >
 <Upload className="w-3 h-3" />
 {t('settings.themeImport') || 'Importieren'}
 </button>
 <button
 onClick={handleNew}
 className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-white bg-[var(--primary)] hover:opacity-90 transition-opacity"
 title={t('settings.themeNew') || 'Neues Theme'}
 >
 <Plus className="w-3 h-3" />
 {t('settings.themeNew') || 'Neues Theme'}
 </button>
 </div>
 </div>
 <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
 {themes.map(th => {
 const isActive = activeTheme?.id === th.id;
 const preview = getThemePreviewColors(th, theme);
 return (
 <div
 key={th.id}
 className={`group relative text-left p-3 rounded-card border transition-all ${
 isActive
 ? 'border-2 border-[var(--primary)]'
 : 'border border-[var(--border-default)] hover:border-[var(--primary)]'
 } ${busyId === th.id || switching === th.id ? 'opacity-50' : ''}`}
 >
 {/* Mini-Preview als Klick-Bereich */}
 <button
 type="button"
 onClick={() => handleSelect(th.id)}
 disabled={switching !== null}
 className="block w-full text-left"
 >
 <div className="rounded-lg overflow-hidden border border-[var(--border-default)] mb-2.5" style={{ height: 64 }}>
 <div className="flex h-full">
 <div style={{ width: '30%', backgroundColor: preview.sidebar }} />
 <div className="flex-1 flex flex-col" style={{ backgroundColor: preview.page }}>
 <div className="h-3" style={{ backgroundColor: preview.content, borderBottom: '1px solid rgba(0,0,0,0.05)' }} />
 <div className="flex-1 p-1 flex gap-1">
 <div className="flex-1 rounded-sm" style={{ backgroundColor: preview.content }} />
 <div className="w-3 rounded-sm" style={{ backgroundColor: preview.primary }} />
 </div>
 </div>
 </div>
 </div>
 <div className="flex items-start justify-between gap-2">
 <div className="flex-1 min-w-0">
 <div className="text-xs font-semibold text-[var(--text-primary)] truncate">{th.name}</div>
 {th.description && (
 <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5 line-clamp-2">{th.description}</div>
 )}
 </div>
 {isActive && (
 <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--primary)] flex items-center justify-center">
 <Check className="w-3 h-3 text-white" strokeWidth={3} />
 </div>
 )}
 </div>
 <div className="flex items-center gap-1 mt-2">
 <div className="w-3 h-3 rounded-full border border-[var(--border-default)]" style={{ backgroundColor: preview.primary }} title="Primary" />
 <div className="w-3 h-3 rounded-full border border-[var(--border-default)]" style={{ backgroundColor: preview.sidebar }} title="Sidebar" />
 <div className="w-3 h-3 rounded-full border border-[var(--border-default)]" style={{ backgroundColor: preview.content }} title="Card" />
 {th.is_preset && <span className="ml-auto text-[9px] uppercase tracking-wide text-[var(--text-muted)]">Preset</span>}
 </div>
 </button>
 {/* Action Toolbar */}
 <div className="mt-2 pt-2 border-t border-[var(--border-default)] flex items-center gap-1">
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); handleDuplicate(th); }}
 disabled={busyId !== null}
 className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
 title={t('settings.themeDuplicate') || 'Duplizieren'}
 >
 <Copy className="w-3.5 h-3.5" />
 </button>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); handleExport(th); }}
 className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
 title={t('settings.themeExport') || 'Exportieren'}
 >
 <Download className="w-3.5 h-3.5" />
 </button>
 {!th.is_preset && (
 <>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); handleEdit(th); }}
 className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
 title={t('common.edit') || 'Bearbeiten'}
 >
 <Pencil className="w-3.5 h-3.5" />
 </button>
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); handleDelete(th); }}
 disabled={busyId !== null}
 className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-[var(--text-tertiary)] hover:text-red-600 transition-colors ml-auto"
 title={t('common.delete') || 'Löschen'}
 >
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>

 {editingTheme && (
 <CustomThemeEditorModal
 t={t}
 showToast={showToast}
 initialTheme={editingTheme}
 onClose={() => setEditingTheme(null)}
 onSaved={() => { setEditingTheme(null); loadThemes(); }}
 />
 )}
 </>
 );
}

/**
 * CustomThemeEditorModal — v2.310.0 (#715)
 * Vollständiger Theme-Editor mit Live-Preview, Light/Dark-Toggle und Export.
 */
function CustomThemeEditorModal({ t, showToast, initialTheme, onClose, onSaved }) {
 const [name, setName] = useState(initialTheme.name || '');
 const [description, setDescription] = useState(initialTheme.description || '');
 const [variables, setVariables] = useState(initialTheme.variables);
 const [editMode, setEditMode] = useState('light');
 const [saving, setSaving] = useState(false);

 const isNew = !initialTheme.id;

 // Load font for live preview when font changes
 const currentFont = variables[editMode]?.['--font-family'] || 'DM Sans';
 useEffect(() => {
 const link = document.createElement('link');
 link.rel = 'stylesheet';
 link.href = `https://fonts.googleapis.com/css2?family=${currentFont.replace(/ /g, '+')}:wght@400;500;600;700&display=swap`;
 document.head.appendChild(link);
 return () => { try { document.head.removeChild(link); } catch {} };
 }, [currentFont]);

 const updateVar = (key, value) => {
 setVariables(prev => ({
 ...prev,
 [editMode]: { ...prev[editMode], [key]: value },
 }));
 };

 const handleSave = async () => {
 if (!name.trim()) {
 showToast(t('settings.themeNameRequired') || 'Name erforderlich');
 return;
 }
 setSaving(true);
 try {
 const url = isNew ? `${API}/api/themes` : `${API}/api/themes/${initialTheme.id}`;
 const res = await fetch(url, {
 method: isNew ? 'POST' : 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ name: name.trim(), description: description.trim() || null, variables }),
 });
 if (res.ok) {
 showToast(t('common.saved') || 'Gespeichert');
 onSaved();
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('common.saveError') || 'Fehler');
 }
 } catch {
 showToast(t('common.networkError') || 'Netzwerkfehler');
 } finally {
 setSaving(false);
 }
 };

 const handleExport = () => {
 const data = {
 name: name || 'unnamed',
 description: description || '',
 variables,
 exported_at: new Date().toISOString(),
 exported_from: 'WeShip v2',
 };
 const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `theme-${(name || 'unnamed').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 URL.revokeObjectURL(url);
 showToast(t('settings.themeExported') || 'Theme exportiert');
 };

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
 <div className="bg-[var(--bg-card)] rounded-card border border-[var(--border-default)] shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
 {/* Header */}
 <div className="px-5 py-3 border-b border-[var(--border-default)] flex items-center justify-between flex-shrink-0">
 <div className="flex items-center gap-3">
 <Paintbrush className="w-4 h-4 text-[var(--primary)]" />
 <h2 className="text-sm font-semibold text-[var(--text-primary)]">
 {isNew ? (t('settings.themeNew') || 'Neues Theme') : (t('settings.themeEdit') || 'Theme bearbeiten')}
 </h2>
 </div>
 <button
 onClick={onClose}
 className="p-1 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
 >
 <X className="w-4 h-4" />
 </button>
 </div>

 {/* Body — 2 columns */}
 <div className="flex-1 overflow-hidden flex">
 {/* Left: Editor */}
 <div className="flex-[3] min-w-0 overflow-y-auto p-5 space-y-4">
 {/* Name + Description */}
 <div className="space-y-3">
 <div>
 <label className="block text-[11px] font-medium uppercase tracking-wide text-[var(--text-secondary)] mb-1">
 {t('common.name') || 'Name'}
 </label>
 <input
 type="text"
 value={name}
 onChange={e => setName(e.target.value)}
 placeholder="Mein Theme"
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 />
 </div>
 <div>
 <label className="block text-[11px] font-medium uppercase tracking-wide text-[var(--text-secondary)] mb-1">
 {t('common.description') || 'Beschreibung'}
 </label>
 <input
 type="text"
 value={description}
 onChange={e => setDescription(e.target.value)}
 placeholder="Optional"
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 />
 </div>
 </div>

 {/* Mode Toggle */}
 <div className="flex items-center gap-1 bg-[var(--bg-inset)] rounded-[10px] p-[3px] border border-[var(--border-default)] w-fit">
 <button
 onClick={() => setEditMode('light')}
 className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
 editMode === 'light' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'
 }`}
 >
 {t('settings.lightMode') || 'Light'}
 </button>
 <button
 onClick={() => setEditMode('dark')}
 className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
 editMode === 'dark' ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'
 }`}
 >
 {t('settings.darkMode') || 'Dark'}
 </button>
 </div>

 {/* Font */}
 <div className="bg-[var(--bg-card)] rounded-card border border-[var(--border-default)]">
 <div className="px-4 py-2.5 border-b border-[var(--border-default)]">
 <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {t('settings.font') || 'Schriftart'}
 </h3>
 </div>
 <div className="p-4">
 <select
 value={currentFont}
 onChange={e => updateVar('--font-family', e.target.value)}
 className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 >
 {THEME_FONT_OPTIONS.map(f => (
 <option key={f} value={f}>{f}</option>
 ))}
 </select>
 </div>
 </div>

 {/* Color Groups */}
 {THEME_VAR_GROUPS.map(group => (
 <div key={group.key} className="bg-[var(--bg-card)] rounded-card border border-[var(--border-default)]">
 <div className="px-4 py-2.5 border-b border-[var(--border-default)]">
 <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {t(group.labelKey) || group.fallbackLabel}
 </h3>
 </div>
 <div className="px-4">
 {group.vars.map(v => (
 <ThemeColorRow
 key={v.key}
 varKey={v.key}
 label={v.label}
 value={variables[editMode]?.[v.key] || ''}
 onChange={updateVar}
 />
 ))}
 </div>
 </div>
 ))}
 </div>

 {/* Right: Live Preview */}
 <div className="flex-[2] min-w-0 border-l border-[var(--border-default)] overflow-y-auto p-5">
 <div className="sticky top-0 space-y-2">
 <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {t('settings.preview') || 'Live-Vorschau'}
 </h3>
 <ThemeLivePreview variables={variables[editMode] || {}} />
 </div>
 </div>
 </div>

 {/* Footer */}
 <div className="px-5 py-3 border-t border-[var(--border-default)] flex items-center justify-between gap-3 flex-shrink-0">
 <button
 onClick={handleExport}
 className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)] transition-colors"
 >
 <Download className="w-3.5 h-3.5" />
 {t('settings.themeExport') || 'Exportieren'}
 </button>
 <div className="flex items-center gap-2">
 <button
 onClick={onClose}
 className="px-4 py-2 rounded-lg text-xs font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
 >
 {t('common.cancel') || 'Abbrechen'}
 </button>
 <button
 onClick={handleSave}
 disabled={saving || !name.trim()}
 className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-[var(--primary)] hover:opacity-90 transition-opacity disabled:opacity-50"
 >
 {saving ? (t('common.saving') || 'Speichere...') : (t('common.save') || 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}

function ThemeColorRow({ varKey, label, value, onChange }) {
 const pickerRef = useRef(null);
 const [inputVal, setInputVal] = useState(value || '');
 const isHex = /^#[0-9A-Fa-f]{6}$/.test(value || '');

 useEffect(() => { setInputVal(value || ''); }, [value]);

 const handleHexInput = (raw) => {
 setInputVal(raw);
 const trimmed = raw.trim();
 onChange(varKey, trimmed);
 };

 const handlePickerChange = (e) => {
 const v = e.target.value;
 setInputVal(v);
 onChange(varKey, v);
 };

 return (
 <div className="flex items-center gap-3 py-2 border-b border-[var(--border-subtle,var(--border-default))] last:border-0">
 <div
 className="w-6 h-6 rounded-lg border border-[var(--border-default)] flex-shrink-0 cursor-pointer"
 style={{ backgroundColor: value || 'transparent' }}
 onClick={() => pickerRef.current?.click()}
 />
 <div className="flex-1 min-w-0">
 <div className="text-xs text-[var(--text-primary)] truncate">{label}</div>
 <div className="text-[9px] text-[var(--text-muted)] font-mono truncate">{varKey}</div>
 </div>
 <input
 type="text"
 value={inputVal}
 onChange={(e) => handleHexInput(e.target.value)}
 className="w-[140px] px-2 py-1 text-[11px] font-mono rounded-lg border border-[var(--border-input)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--primary)]"
 spellCheck={false}
 />
 <div className="relative flex-shrink-0">
 <input
 ref={pickerRef}
 type="color"
 value={isHex ? value : '#338edc'}
 onChange={handlePickerChange}
 className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
 />
 <Palette className="w-4 h-4 text-[var(--text-tertiary)] pointer-events-none" />
 </div>
 </div>
 );
}

function ThemeLivePreview({ variables }) {
 const get = (key, fallback) => variables[key] || fallback;
 const fontName = get('--font-family', 'DM Sans');
 const fontStyle = { fontFamily: `'${fontName}', -apple-system, sans-serif` };

 return (
 <div
 className="rounded-xl border overflow-hidden text-xs"
 style={{
 backgroundColor: get('--bg-page', '#F4F5FA'),
 borderColor: get('--border-card', '#E5E7EB'),
 color: get('--text-primary', '#1A1A2E'),
 ...fontStyle,
 }}
 >
 {/* Mock Sidebar + Content */}
 <div className="flex" style={{ minHeight: 320 }}>
 {/* Sidebar */}
 <div className="w-[110px] flex-shrink-0 p-2.5" style={{ backgroundColor: get('--sidebar-bg', '#FFFFFF'), borderRight: `1px solid ${get('--sidebar-border', '#F3F4F6')}` }}>
 <div className="text-[11px] font-bold mb-3" style={{ color: get('--sidebar-logo-text', '#1A1A2E') }}>WeShip</div>
 <div className="space-y-1">
 <div className="px-2 py-1 rounded text-[10px] font-medium" style={{ backgroundColor: get('--sidebar-bg-active', 'rgba(51,142,220,0.08)'), color: get('--sidebar-text-active', '#338edc') }}>Dashboard</div>
 <div className="px-2 py-1 rounded text-[10px]" style={{ color: get('--sidebar-text', '#4B5563') }}>Aufträge</div>
 <div className="px-2 py-1 rounded text-[10px]" style={{ color: get('--sidebar-text', '#4B5563') }}>Kunden</div>
 <div className="text-[8px] uppercase tracking-wide mt-3 px-2" style={{ color: get('--sidebar-category', '#6B7280') }}>Berichte</div>
 <div className="px-2 py-1 rounded text-[10px]" style={{ color: get('--sidebar-text', '#4B5563') }}>Performance</div>
 </div>
 </div>
 {/* Content */}
 <div className="flex-1 p-3 space-y-2.5 min-w-0">
 {/* KPI Cards */}
 <div className="grid grid-cols-3 gap-1.5">
 {['1.234', '567', '89%'].map((v, i) => (
 <div key={i} className="rounded-lg p-2 border" style={{ backgroundColor: get('--bg-card', '#FFFFFF'), borderColor: get('--border-card', '#E5E7EB') }}>
 <div className="text-[8px]" style={{ color: get('--text-tertiary', '#6B7280') }}>KPI {i + 1}</div>
 <div className="text-[13px] font-bold" style={{ color: get('--text-primary', '#1A1A2E') }}>{v}</div>
 </div>
 ))}
 </div>
 {/* Table */}
 <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: get('--bg-card', '#FFFFFF'), borderColor: get('--border-card', '#E5E7EB') }}>
 <div className="px-2 py-1.5 text-[9px] font-semibold uppercase border-b" style={{ color: get('--text-secondary', '#4B5563'), borderColor: get('--border-table', '#F3F4F6') }}>Letzte Aufträge</div>
 {['Auftrag #001', 'Auftrag #002', 'Auftrag #003'].map((row, i) => (
 <div key={i} className="px-2 py-1.5 text-[10px] flex items-center justify-between border-b last:border-0" style={{ borderColor: get('--border-table', '#F3F4F6'), color: get('--text-primary', '#1A1A2E') }}>
 <span>{row}</span>
 <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ backgroundColor: get('--primary-light', '#EBF4FD'), color: get('--primary', '#338edc') }}>OK</span>
 </div>
 ))}
 </div>
 {/* Buttons + Search */}
 <div className="flex items-center gap-1.5">
 <button className="px-2.5 py-1 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: get('--primary', '#338edc') }}>Primary</button>
 <button className="px-2.5 py-1 rounded text-[10px] font-medium border" style={{ borderColor: get('--border-card', '#E5E7EB'), color: get('--text-secondary', '#4B5563'), backgroundColor: get('--bg-card', '#FFFFFF') }}>Secondary</button>
 <div className="flex-1 px-2 py-1 text-[9px] rounded border" style={{ borderColor: get('--border-input', '#E5E7EB'), backgroundColor: get('--bg-card', '#FFFFFF'), color: get('--text-tertiary', '#9CA3AF') }}>Suche...</div>
 </div>
 </div>
 </div>
 </div>
 );
}

function ThemeEditorTab({ t, showToast }) {
 const { theme, reloadThemeSettings } = useTheme();
 const [settings, setSettings] = useState([]);
 const [editMode, setEditMode] = useState(theme === 'dark' ? 'dark' : 'light');
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [hasChanges, setHasChanges] = useState(false);

 // Track local edits (mode+token → value)
 const [localEdits, setLocalEdits] = useState({});

 const loadSettings = useCallback(() => {
 setLoading(true);
 fetch(`${API}/api/theme-settings`)
 .then(r => r.ok ? r.json() : null)
 .then(d => {
 if (d?.settings) setSettings(d.settings);
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { loadSettings(); }, [loadSettings]);

 const handleColorChange = (token, newValue) => {
 const key = `${editMode}:${token}`;
 setLocalEdits(prev => ({ ...prev, [key]: newValue }));
 setHasChanges(true);
 };

 const getEffectiveValue = (s) => {
 const key = `${s.mode}:${s.token}`;
 if (key in localEdits) return localEdits[key];
 return s.value;
 };

 // Build colors map for preview (merge DB values + local edits)
 const previewColors = {};
 for (const s of settings) {
 if (s.mode !== editMode && s.mode !== 'both') continue;
 const key = `${s.mode}:${s.token}`;
 previewColors[s.token] = (key in localEdits) ? localEdits[key] : (s.value || s.default_value);
 }

 const handleSave = async () => {
 setSaving(true);
 // Collect all changed settings for current editMode
 const updates = [];
 for (const s of settings) {
 const key = `${s.mode}:${s.token}`;
 if (key in localEdits) {
 updates.push({ mode: s.mode, token: s.token, value: localEdits[key] });
 }
 }
 if (updates.length === 0) {
 setSaving(false);
 return;
 }
 try {
 const res = await fetch(`${API}/api/theme-settings`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ settings: updates }),
 });
 if (res.ok) {
 showToast(t('common.saved'));
 setLocalEdits({});
 setHasChanges(false);
 loadSettings();
 reloadThemeSettings();
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('common.saveError'));
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSaving(false);
 }
 };

 const handleReset = async () => {
 if (!confirm(t('settings.resetConfirm'))) return;
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/theme-settings/reset`, {
 method: 'POST',
 credentials: 'include',
 });
 if (res.ok) {
 showToast(t('common.saved'));
 setLocalEdits({});
 setHasChanges(false);
 loadSettings();
 reloadThemeSettings();
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSaving(false);
 }
 };

 const categoryLabels = {
 font: t('settings.font'),
 accent: t('settings.accentColors'),
 backgrounds: t('settings.backgrounds'),
 text: t('settings.textColors'),
 borders: t('settings.borders'),
 status: t('settings.statusColors'),
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-48">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 const modeSettings = settings.filter(s => s.mode === editMode || s.mode === 'both');
 const grouped = {};
 for (const s of modeSettings) {
 if (!grouped[s.category]) grouped[s.category] = [];
 grouped[s.category].push(s);
 }

 return (
 <div className="space-y-4">
 {/* v2.308.2 — Theme-Preset Picker (#714) */}
 <ThemePresetPicker t={t} showToast={showToast} />

 {/* Warning */}
 <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30">
 <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
 <span className="text-xs text-amber-700 dark:text-amber-400">{t('settings.themeWarning')}</span>
 </div>

 {/* Light / Dark toggle */}
 <div className="flex items-center gap-1 bg-surface-elevated rounded-lg p-1 border border-line w-fit">
 <button
 onClick={() => setEditMode('light')}
 className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
 editMode === 'light' ? 'bg-surface text-content shadow-sm' : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 {t('settings.lightMode')}
 </button>
 <button
 onClick={() => setEditMode('dark')}
 className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
 editMode === 'dark' ? 'bg-surface text-content shadow-sm' : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 {t('settings.darkMode')}
 </button>
 </div>

 {/* 2-column: Editor | Preview */}
 <div className="flex gap-4">
 {/* Editor (left 60%) */}
 <div className="flex-[3] min-w-0 space-y-3">
 {CATEGORY_ORDER.map(cat => {
 const items = grouped[cat];
 if (!items) return null;
 return (
 <div key={cat} className="bg-surface-elevated rounded-card border border-line">
 <div className="px-4 py-2.5 border-b border-line">
 <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {categoryLabels[cat] || cat}
 </h3>
 </div>
 <div className="px-4">
 {cat === 'font' ? (
 items.map(s => (
 <FontField
 key={s.token}
 value={getEffectiveValue(s)}
 defaultValue={s.default_value}
 onChange={handleColorChange}
 t={t}
 />
 ))
 ) : (
 items.map(s => (
 <ColorField
 key={s.token}
 token={s.token}
 label={s.label}
 value={getEffectiveValue(s)}
 defaultValue={s.default_value}
 onChange={handleColorChange}
 />
 ))
 )}
 </div>
 </div>
 );
 })}

 {/* Actions */}
 <div className="flex items-center gap-3">
 <button
 onClick={handleSave}
 disabled={saving || !hasChanges}
 className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50"
 style={{ backgroundColor: '#338edc' }}
 >
 {saving ? t('common.saving') : t('common.save')}
 </button>
 <button
 onClick={handleReset}
 disabled={saving}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-line bg-surface-elevated text-[var(--text-secondary)] hover:text-content transition-colors disabled:opacity-50"
 >
 <RotateCcw className="w-3.5 h-3.5" />
 {t('settings.resetTheme')}
 </button>
 </div>
 </div>

 {/* Preview (right 40%) — hidden on mobile */}
 <div className="flex-[2] min-w-0 hidden lg:block">
 <div className="sticky top-4 space-y-2">
 <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
 {t('settings.preview')}
 </h3>
 <ThemePreview colors={previewColors} />
 </div>
 </div>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// ADRESSKLÄRUNGEN — SMTP/IMAP/Cronjob Configuration
// ════════════════════════════════════════════════════════════════

function ConfigInput({ label, value, onChange, type = 'text', placeholder, min, max }) {
 return (
 <div className="flex items-start justify-between gap-6 py-3 border-b border-line last:border-0">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-content">{label}</div>
 </div>
 <div className="flex-shrink-0 w-64">
 <input
 type={type}
 value={value ?? ''}
 onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
 placeholder={placeholder}
 min={min}
 max={max}
 className="w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 </div>
 </div>
 );
}

// ═══ Abwesenheitsarten-Verwaltung Tab (v2.239.0) ═══
function AbwesenheitsartenTab({ t, showToast }) {
 const { locale } = useLanguage();
 const [arten, setArten] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editId, setEditId] = useState(null);
 const [editForm, setEditForm] = useState({});
 const [saving, setSaving] = useState(false);

 const load = useCallback(() => {
 setLoading(true);
 fetch(`${API}/api/abwesenheitsarten?alle=true`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (d.data) setArten(d.data); })
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { load(); }, [load]);

 const startEdit = (a) => {
 setEditId(a.id);
 setEditForm({ label_de: a.label_de, label_en: a.label_en, kuerzel: a.kuerzel, farbe: a.farbe, max_tage_pro_jahr: a.max_tage_pro_jahr || '', bezahlt: a.bezahlt, braucht_genehmigung: a.braucht_genehmigung, automatisch_genehmigt: a.automatisch_genehmigt, aktiv: a.aktiv, sichtbar_im_portal: a.sichtbar_im_portal });
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const body = { ...editForm, max_tage_pro_jahr: editForm.max_tage_pro_jahr ? parseInt(editForm.max_tage_pro_jahr) : null };
 const res = await fetch(`${API}/api/abwesenheitsarten/${editId}`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
 });
 const data = await res.json();
 if (data.success) { setEditId(null); load(); showToast?.(t('common.saved')); }
 } finally { setSaving(false); }
 };

 const katLabels = { gesetzlich: t('absence.cat_legal') || 'Gesetzlich', bezahlt: t('absence.cat_paid') || 'Bezahlt', unbezahlt: t('absence.cat_unpaid') || 'Unbezahlt', sonderurlaub: t('absence.cat_special') || 'Sonderurlaub' };

 return (
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <h2 className="text-sm font-semibold text-content">
 <CalendarOff className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
 {t('settings.absenceTypes.title') || 'Abwesenheitsarten'}
 </h2>
 <span className="text-xs text-[var(--text-tertiary)]">{arten.length} {t('settings.absenceTypes.types') || 'Typen'}</span>
 </div>
 <div className="overflow-x-auto">
 {loading ? (
 <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>
 ) : (
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-line text-[var(--text-secondary)]">
 <th className="text-left px-4 py-2 font-semibold">{t('settings.absenceTypes.color') || 'Farbe'}</th>
 <th className="text-left px-4 py-2 font-semibold">{t('settings.absenceTypes.code') || 'Kürzel'}</th>
 <th className="text-left px-4 py-2 font-semibold">{t('settings.absenceTypes.name_de') || 'Name (DE)'}</th>
 <th className="text-left px-4 py-2 font-semibold">{t('settings.absenceTypes.name_en') || 'Name (EN)'}</th>
 <th className="text-left px-4 py-2 font-semibold">{t('settings.absenceTypes.category') || 'Kategorie'}</th>
 <th className="text-center px-4 py-2 font-semibold">{t('settings.absenceTypes.paid') || 'Bezahlt'}</th>
 <th className="text-center px-4 py-2 font-semibold">{t('settings.absenceTypes.approval') || 'Genehmigung'}</th>
 <th className="text-center px-4 py-2 font-semibold">{t('settings.absenceTypes.max_days') || 'Max Tage'}</th>
 <th className="text-center px-4 py-2 font-semibold">{t('settings.absenceTypes.active') || 'Aktiv'}</th>
 <th className="px-4 py-2"></th>
 </tr>
 </thead>
 <tbody>
 {arten.map(a => (
 <tr key={a.id} className={`border-b border-line/50 hover:bg-surface-hover ${!a.aktiv ? 'opacity-40' : ''}`}>
 {editId === a.id ? (
 <>
 <td className="px-4 py-2"><input type="color" value={editForm.farbe} onChange={e => setEditForm(p => ({...p, farbe: e.target.value}))} className="w-6 h-6 rounded cursor-pointer" /></td>
 <td className="px-4 py-2"><input value={editForm.kuerzel} onChange={e => setEditForm(p => ({...p, kuerzel: e.target.value}))} className="w-12 text-xs bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5" /></td>
 <td className="px-4 py-2"><input value={editForm.label_de} onChange={e => setEditForm(p => ({...p, label_de: e.target.value}))} className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-0.5" /></td>
 <td className="px-4 py-2"><input value={editForm.label_en} onChange={e => setEditForm(p => ({...p, label_en: e.target.value}))} className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-0.5" /></td>
 <td className="px-4 py-2 text-[var(--text-tertiary)]">{katLabels[a.kategorie] || a.kategorie}</td>
 <td className="px-4 py-2 text-center"><input type="checkbox" checked={editForm.bezahlt} onChange={e => setEditForm(p => ({...p, bezahlt: e.target.checked}))} /></td>
 <td className="px-4 py-2 text-center"><input type="checkbox" checked={editForm.braucht_genehmigung} onChange={e => setEditForm(p => ({...p, braucht_genehmigung: e.target.checked}))} /></td>
 <td className="px-4 py-2 text-center"><input type="number" value={editForm.max_tage_pro_jahr} onChange={e => setEditForm(p => ({...p, max_tage_pro_jahr: e.target.value}))} className="w-12 text-xs bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 text-center" placeholder="–" /></td>
 <td className="px-4 py-2 text-center"><input type="checkbox" checked={editForm.aktiv} onChange={e => setEditForm(p => ({...p, aktiv: e.target.checked}))} /></td>
 <td className="px-4 py-2 text-right">
 <button onClick={handleSave} disabled={saving} className="text-primary hover:underline mr-2">{saving ? '...' : t('common.save')}</button>
 <button onClick={() => setEditId(null)} className="text-[var(--text-tertiary)] hover:underline">{t('common.cancel')}</button>
 </td>
 </>
 ) : (
 <>
 <td className="px-4 py-2"><span className="w-4 h-4 rounded-full inline-block" style={{ backgroundColor: a.farbe }} /></td>
 <td className="px-4 py-2 font-mono font-bold">{a.kuerzel}</td>
 <td className="px-4 py-2 text-content">{a.label_de}</td>
 <td className="px-4 py-2 text-[var(--text-secondary)]">{a.label_en}</td>
 <td className="px-4 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded-full border border-line bg-[var(--bg-secondary)]">{katLabels[a.kategorie] || a.kategorie || '–'}</span></td>
 <td className="px-4 py-2 text-center">{a.bezahlt ? '✓' : '–'}</td>
 <td className="px-4 py-2 text-center">{a.braucht_genehmigung ? '✓' : a.automatisch_genehmigt ? 'Auto' : '–'}</td>
 <td className="px-4 py-2 text-center font-mono">{a.max_tage_pro_jahr || '–'}</td>
 <td className="px-4 py-2 text-center">{a.aktiv ? '✓' : '–'}</td>
 <td className="px-4 py-2 text-right">
 <button onClick={() => startEdit(a)} className="text-[var(--text-tertiary)] hover:text-primary transition-colors">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
 </button>
 </td>
 </>
 )}
 </tr>
 ))}
 </tbody>
 </table>
 )}
 </div>
 </div>
 );
}

function KundenKlassifizierungTab({ t, showToast }) {
 const [activeMon, setActiveMon] = useState(3);
 const [inactiveMon, setInactiveMon] = useState(6);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [hasChanges, setHasChanges] = useState(false);

 useEffect(() => {
 fetch(`${API}/api/kunden/klassifizierung/config`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : { active_months: 3, inactive_months: 6 })
 .then(d => { setActiveMon(d.active_months); setInactiveMon(d.inactive_months); })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const handleSave = async () => {
 if (activeMon >= inactiveMon) {
 showToast(t('customers.klassifizierungValidation'));
 return;
 }
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/kunden/klassifizierung/config`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ active_months: activeMon, inactive_months: inactiveMon }),
 });
 if (res.ok) {
 showToast(t('customers.klassifizierungSaved'));
 setHasChanges(false);
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || 'Error');
 }
 } catch { showToast('Error'); }
 finally { setSaving(false); }
 };

 if (loading) return <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">...</div>;

 return (
 <div className="space-y-6">
 <div className="border border-[var(--border-primary,#E5E7EB)] rounded-[16px] p-6 bg-[var(--bg-primary)]">
 <div className="flex items-center gap-2 mb-5">
 <Users className="w-5 h-5 text-[#338edc]" />
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('customers.klassifizierung')}</h3>
 </div>

 <div className="space-y-5">
 {/* Aktiv */}
 <div className="flex items-start justify-between gap-6">
 <div>
 <div className="text-sm font-medium text-[var(--text-primary)]">{t('customers.fulfillmentAktiv')}</div>
 <div className="text-xs text-[var(--text-secondary)] mt-0.5">
 {t('customers.aktivDesc', { months: activeMon })}
 </div>
 </div>
 <div className="flex items-center gap-2 shrink-0">
 <input type="number" min={1} max={24} value={activeMon}
 onChange={e => { setActiveMon(Number(e.target.value)); setHasChanges(true); }}
 className="w-16 px-2 py-1.5 text-sm text-center rounded-lg border border-[var(--border-primary,#E5E7EB)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
 />
 <span className="text-xs text-[var(--text-secondary)]">{t('customers.aktivMonate').split('(')[1]?.replace(')', '') || 'months'}</span>
 </div>
 </div>

 {/* Ruhend */}
 <div className="flex items-start justify-between gap-6 pt-4 border-t border-[var(--border-primary,#E5E7EB)]">
 <div>
 <div className="text-sm font-medium text-[var(--text-primary)]">{t('customers.fulfillmentRuhend')}</div>
 <div className="text-xs text-[var(--text-secondary)] mt-0.5">
 {t('customers.ruhendDesc', { from: activeMon, to: inactiveMon })}
 </div>
 </div>
 <div className="flex items-center gap-2 shrink-0">
 <input type="number" min={1} max={24} value={inactiveMon}
 onChange={e => { setInactiveMon(Number(e.target.value)); setHasChanges(true); }}
 className="w-16 px-2 py-1.5 text-sm text-center rounded-lg border border-[var(--border-primary,#E5E7EB)] bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30"
 />
 <span className="text-xs text-[var(--text-secondary)]">{t('customers.ruhendMonate').split('(')[1]?.replace(')', '') || 'months'}</span>
 </div>
 </div>

 {/* Inaktiv — display only */}
 <div className="pt-4 border-t border-[var(--border-primary,#E5E7EB)]">
 <div className="text-sm font-medium text-[var(--text-primary)]">{t('customers.fulfillmentInaktiv')}</div>
 <div className="text-xs text-[var(--text-secondary)] mt-0.5">
 {t('customers.inaktivDesc', { months: inactiveMon })}
 </div>
 </div>
 </div>

 {/* Save button */}
 <div className="flex justify-end mt-6 pt-4 border-t border-[var(--border-primary,#E5E7EB)]">
 <button onClick={handleSave} disabled={!hasChanges || saving}
 className="px-5 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors">
 {saving ? '...' : t('common.save', 'Save')}
 </button>
 </div>
 </div>
 </div>
 );
}

function AdressklaerungTab({ t, showToast }) {
 const [config, setConfig] = useState({});
 const [loading, setLoading] = useState(true);
 const [savingGroup, setSavingGroup] = useState(null);
 const [testResult, setTestResult] = useState(null);
 const [testing, setTesting] = useState(null);

 useEffect(() => {
 fetch(`${API}/api/adressklaerungen/config`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : {})
 .then(d => setConfig(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const updateField = (key, value) => {
 setConfig(prev => ({ ...prev, [key]: value }));
 };

 const saveGroup = async (keys) => {
 const group = keys[0]?.split('_')[1] || 'config';
 setSavingGroup(group);
 try {
 const body = {};
 for (const k of keys) body[k] = config[k] ?? '';
 const res = await fetch(`${API}/api/adressklaerungen/config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(body),
 });
 if (res.ok) {
 showToast(t('common.saved'));
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('common.saveError'));
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSavingGroup(null);
 }
 };

 const testConnection = async (type) => {
 setTesting(type);
 setTestResult(null);
 try {
 const res = await fetch(`${API}/api/adressklaerungen/test-${type}`, {
 method: 'POST',
 credentials: 'include',
 });
 const data = await res.json();
 if (data.success) {
 setTestResult({ type, success: true, message: t('adressklaerung.testSuccess') });
 } else {
 setTestResult({ type, success: false, message: data.error || 'Unknown error' });
 }
 setTimeout(() => setTestResult(prev => prev?.type === type ? null : prev), 5000);
 } catch (err) {
 setTestResult({ type, success: false, message: err.message });
 setTimeout(() => setTestResult(prev => prev?.type === type ? null : prev), 5000);
 } finally {
 setTesting(null);
 }
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-48">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 const cronKeys = ['ai_agent_name', 'adressklaerung_cronjob_interval', 'adressklaerung_oms_tag', 'adressklaerung_oms_stage', 'adressklaerung_max_versuche', 'adressklaerung_retry_stunden'];
 const smtpKeys = ['adressklaerung_smtp_host', 'adressklaerung_smtp_port', 'adressklaerung_smtp_user', 'adressklaerung_smtp_password', 'adressklaerung_smtp_from', 'adressklaerung_smtp_secure'];
 const imapKeys = ['adressklaerung_imap_host', 'adressklaerung_imap_port', 'adressklaerung_imap_user', 'adressklaerung_imap_password', 'adressklaerung_imap_tls'];

 return (
 <div className="space-y-4">
 {/* Card 1: Cronjob */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <RefreshCw className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('adressklaerung.cronjob')}</h2>
 </div>
 <div className="px-5">
 <ConfigInput
 label={t('adressklaerung.aiAgentName')}
 value={config.ai_agent_name}
 onChange={(v) => updateField('ai_agent_name', v)}
 placeholder="FlowAI"
 />
 <p className="text-[11px] text-[var(--text-tertiary)] -mt-1 mb-1 px-0">{t('adressklaerung.aiAgentNameHint')}</p>
 <ConfigInput
 label={t('adressklaerung.intervalMinutes')}
 value={config.adressklaerung_cronjob_interval}
 onChange={(v) => updateField('adressklaerung_cronjob_interval', v)}
 type="number"
 min={5}
 max={1440}
 />
 <ConfigInput
 label={t('adressklaerung.omsTag')}
 value={config.adressklaerung_oms_tag}
 onChange={(v) => updateField('adressklaerung_oms_tag', v)}
 placeholder="missing_nr"
 />
 <p className="text-[11px] text-[var(--text-tertiary)] -mt-1 mb-1 px-0">{t('adressklaerung.omsTagHint')}</p>
 <ConfigInput
 label={t('adressklaerung.omsStage')}
 value={config.adressklaerung_oms_stage}
 onChange={(v) => updateField('adressklaerung_oms_stage', v)}
 placeholder="Lösung vorgeschlagen"
 />
 <p className="text-[11px] text-[var(--text-tertiary)] -mt-1 mb-1 px-0">{t('adressklaerung.omsStageHint')}</p>
 <div className="flex gap-4 mt-2">
 <div className="flex-1">
 <ConfigInput
 label={t('adressklaerung.maxVersuche')}
 value={config.adressklaerung_max_versuche}
 onChange={(v) => updateField('adressklaerung_max_versuche', v)}
 placeholder="2"
 type="number"
 min={1}
 max={5}
 />
 <p className="text-[11px] text-[var(--text-tertiary)] -mt-1 mb-1 px-0">{t('adressklaerung.maxVersucheHint')}</p>
 </div>
 <div className="flex-1">
 <ConfigInput
 label={t('adressklaerung.retryStunden')}
 value={config.adressklaerung_retry_stunden}
 onChange={(v) => updateField('adressklaerung_retry_stunden', v)}
 placeholder="48"
 type="number"
 min={1}
 max={168}
 />
 <p className="text-[11px] text-[var(--text-tertiary)] -mt-1 mb-1 px-0">{t('adressklaerung.retryStundenHint')}</p>
 </div>
 </div>
 </div>
 <div className="px-5 pb-4">
 <p className="text-xs text-[var(--text-tertiary)] mb-3">
 {t('adressklaerung.cronjobHint')}
 </p>
 <button
 onClick={() => saveGroup(cronKeys)}
 disabled={savingGroup === 'cronjob'}
 className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]"
 >
 {savingGroup === 'cronjob' ? t('common.saving') : t('common.save')}
 </button>
 </div>
 </div>

 {/* Card 2: SMTP */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <MapPin className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('adressklaerung.smtpSettings')}</h2>
 </div>
 <div className="px-5">
 <ConfigInput label="SMTP Host" value={config.adressklaerung_smtp_host} onChange={(v) => updateField('adressklaerung_smtp_host', v)} placeholder="mail.example.com" />
 <ConfigInput label="SMTP Port" value={config.adressklaerung_smtp_port} onChange={(v) => updateField('adressklaerung_smtp_port', v)} type="number" />
 <div className="flex items-center justify-between py-3 border-b border-line">
 <span className="text-sm text-content">{t('adressklaerung.smtpSecure')}</span>
 <div className="flex rounded-button border border-line overflow-hidden text-xs">
 <button
 type="button"
 onClick={() => { updateField('adressklaerung_smtp_secure', 'true'); updateField('adressklaerung_smtp_port', '465'); }}
 className={`px-3 py-1.5 transition-colors ${config.adressklaerung_smtp_secure === 'true' ? 'bg-white dark:bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}
 >
 {t('adressklaerung.smtpSsl')}
 </button>
 <button
 type="button"
 onClick={() => { updateField('adressklaerung_smtp_secure', 'false'); updateField('adressklaerung_smtp_port', '587'); }}
 className={`px-3 py-1.5 transition-colors ${config.adressklaerung_smtp_secure !== 'true' ? 'bg-white dark:bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}
 >
 {t('adressklaerung.smtpStarttls')}
 </button>
 </div>
 </div>
 <ConfigInput label={t('adressklaerung.username')} value={config.adressklaerung_smtp_user} onChange={(v) => updateField('adressklaerung_smtp_user', v)} />
 <ConfigInput label={t('adressklaerung.password')} value={config.adressklaerung_smtp_password} onChange={(v) => updateField('adressklaerung_smtp_password', v)} type="password" />
 <ConfigInput label={t('adressklaerung.senderEmail')} value={config.adressklaerung_smtp_from} onChange={(v) => updateField('adressklaerung_smtp_from', v)} placeholder="adressen@weship.eu" />
 </div>
 <div className="px-5 pb-4 flex items-center gap-3">
 <button
 onClick={() => testConnection('smtp')}
 disabled={testing === 'smtp'}
 className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border border-line bg-surface-elevated text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover disabled:opacity-50"
 >
 {testing === 'smtp' ? (
 <span className="inline-flex items-center gap-1.5">
 <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
 {t('adressklaerung.testConnection')}
 </span>
 ) : t('adressklaerung.testConnection')}
 </button>
 <button
 onClick={() => saveGroup(smtpKeys)}
 disabled={savingGroup === 'smtp'}
 className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]"
 >
 {savingGroup === 'smtp' ? t('common.saving') : t('common.save')}
 </button>
 </div>
 {testResult?.type === 'smtp' && (
 <div className={`mx-5 mb-4 px-3 py-2 rounded-lg text-xs font-medium ${
 testResult.success
 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
 : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
 }`}>
 {testResult.success ? '✓ ' : '✗ '}{testResult.message}
 </div>
 )}
 </div>

 {/* Card 3: IMAP */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <MapPin className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('adressklaerung.imapSettings')}</h2>
 </div>
 <div className="px-5">
 <ConfigInput label="IMAP Host" value={config.adressklaerung_imap_host} onChange={(v) => updateField('adressklaerung_imap_host', v)} placeholder="imap.example.com" />
 <ConfigInput label="IMAP Port" value={config.adressklaerung_imap_port} onChange={(v) => updateField('adressklaerung_imap_port', v)} type="number" />
 <div className="flex items-center justify-between py-3 border-b border-line">
 <span className="text-sm text-content">{t('adressklaerung.imapTls')}</span>
 <ToggleSwitch
 checked={config.adressklaerung_imap_tls !== 'false'}
 onChange={() => updateField('adressklaerung_imap_tls', config.adressklaerung_imap_tls === 'false' ? 'true' : 'false')}
 />
 </div>
 <ConfigInput label={t('adressklaerung.username')} value={config.adressklaerung_imap_user} onChange={(v) => updateField('adressklaerung_imap_user', v)} />
 <ConfigInput label={t('adressklaerung.password')} value={config.adressklaerung_imap_password} onChange={(v) => updateField('adressklaerung_imap_password', v)} type="password" />
 </div>
 <div className="px-5 pb-4">
 <p className="text-xs text-[var(--text-tertiary)] mb-3">
 {t('adressklaerung.imapHint')}
 </p>
 <div className="flex items-center gap-3">
 <button
 onClick={() => testConnection('imap')}
 disabled={testing === 'imap'}
 className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border border-line bg-surface-elevated text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover disabled:opacity-50"
 >
 {testing === 'imap' ? (
 <span className="inline-flex items-center gap-1.5">
 <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
 {t('adressklaerung.testConnection')}
 </span>
 ) : t('adressklaerung.testConnection')}
 </button>
 <button
 onClick={() => saveGroup(imapKeys)}
 disabled={savingGroup === 'imap'}
 className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]"
 >
 {savingGroup === 'imap' ? t('common.saving') : t('common.save')}
 </button>
 </div>
 </div>
 {testResult?.type === 'imap' && (
 <div className={`mx-5 mb-4 px-3 py-2 rounded-lg text-xs font-medium ${
 testResult.success
 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
 : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
 }`}>
 {testResult.success ? '✓ ' : '✗ '}{testResult.message}
 </div>
 )}
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// FORECAST QUALITY — Score Weights, Thresholds, AI Config
// ════════════════════════════════════════════════════════════════

const FQ_DEFAULTS = {
 fq_weight_accuracy: 0.5, fq_weight_bias: 0.3, fq_weight_consistency: 0.2,
 fq_threshold_excellent: 90, fq_threshold_good: 70, fq_threshold_moderate: 50,
 fq_cutoff_hour: 12, fq_min_days: 5,
 fq_consistency_window: 5, fq_consistency_multiplier: 2,
 fq_ai_report_enabled: true, fq_ai_report_day: 1, fq_ai_report_hour: 7,
 fq_ai_report_email: false, fq_ai_report_recipients: '',
 fq_warning_enabled: true, fq_warning_threshold: 50, fq_warning_channel: 'notification',
 fq_show_grid_badges: true, fq_badge_format: 'score',
};

/** Mini-Donut SVG showing 3 weight segments */
function WeightDonut({ accuracy, bias, consistency }) {
 const total = accuracy + bias + consistency;
 if (total === 0) return null;
 const r = 34, cx = 40, cy = 40, sw = 10;
 const circ = 2 * Math.PI * r;
 const colors = ['#338edc', '#22D3EE', '#6366F1'];
 const vals = [accuracy / total, bias / total, consistency / total];
 let offset = -circ / 4; // Start at top
 const arcs = vals.map((v, i) => {
 const len = v * circ;
 const el = (
 <circle key={i} cx={cx} cy={cy} r={r} fill="none"
 stroke={colors[i]} strokeWidth={sw}
 strokeDasharray={`${len} ${circ - len}`}
 strokeDashoffset={-offset}
 style={{ transition: 'all 0.3s ease' }} />
 );
 offset += len;
 return el;
 });
 return (
 <svg width="80" height="80" viewBox="0 0 80 80">
 <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E5E7EB" strokeWidth={sw} />
 {arcs}
 </svg>
 );
}

/** Threshold color bar visualization */
function ThresholdBar({ excellent, good, moderate }) {
 const zones = [
 { from: 0, to: moderate, color: '#EF4444', label: `<${moderate}` },
 { from: moderate, to: good, color: '#F59E0B', label: `${moderate}–${good - 1}` },
 { from: good, to: excellent, color: '#338edc', label: `${good}–${excellent - 1}` },
 { from: excellent, to: 100, color: '#10B981', label: `${excellent}+` },
 ];
 return (
 <div className="flex rounded-lg overflow-hidden h-6 border border-line">
 {zones.map((z, i) => (
 <div key={i} style={{ width: `${z.to - z.from}%`, backgroundColor: z.color }}
 className="flex items-center justify-center text-[10px] font-semibold text-white transition-all duration-300" title={z.label}>
 {(z.to - z.from) >= 12 ? z.label : ''}
 </div>
 ))}
 </div>
 );
}

/** Collapsible card wrapper */
function ConfigCard({ icon: Icon, title, defaultOpen = true, children }) {
 const [open, setOpen] = useState(defaultOpen);
 return (
 <div className="bg-surface-elevated rounded-2xl border border-[var(--border-default,#E5E7EB)]">
 <button onClick={() => setOpen(!open)}
 className="w-full px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-surface-hover rounded-t-2xl transition-colors">
 <div className="flex items-center gap-2.5">
 <Icon className="w-4 h-4 text-[var(--text-primary)]" />
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
 </div>
 <svg className={`w-4 h-4 text-[var(--text-secondary)] transition-transform ${open ? 'rotate-180' : ''}`}
 fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
 <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
 </svg>
 </button>
 {open && <div className="px-6 pb-5 border-t border-[var(--border-default,#E5E7EB)]">{children}</div>}
 </div>
 );
}

function ForecastQualityTab({ t, showToast }) {
 const [config, setConfig] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [hasChanges, setHasChanges] = useState(false);
 const [resetConfirm, setResetConfirm] = useState(false);

 useEffect(() => {
 fetch(`${API}/api/forecast/quality/config`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : {})
 .then(d => {
 const cfg = {};
 if (Array.isArray(d.config)) {
 for (const row of d.config) cfg[row.key] = row.value;
 } else if (d.config) {
 Object.assign(cfg, d.config);
 }
 for (const [k, v] of Object.entries(cfg)) {
 if (v === 'true') cfg[k] = true;
 else if (v === 'false') cfg[k] = false;
 else if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) cfg[k] = Number(v);
 }
 setConfig({ ...FQ_DEFAULTS, ...cfg });
 })
 .catch(() => setConfig({ ...FQ_DEFAULTS }))
 .finally(() => setLoading(false));
 }, []);

 const update = (key, val) => {
 setConfig(prev => ({ ...prev, [key]: val }));
 setHasChanges(true);
 };

 const handleSave = async () => {
 // Frontend validation
 const te = Number(config.fq_threshold_excellent);
 const tg = Number(config.fq_threshold_good);
 const tm = Number(config.fq_threshold_moderate);
 if (te <= tg || tg <= tm) {
 showToast(t('forecastQuality.validationError') + ': ' + t('forecastQuality.thresholdExcellent') + ' > ' + t('forecastQuality.thresholdGood') + ' > ' + t('forecastQuality.thresholdModerate'));
 return;
 }
 setSaving(true);
 try {
 const updates = {};
 for (const [k, v] of Object.entries(config)) {
 if (k.startsWith('fq_')) updates[k] = v;
 }
 const res = await fetch(`${API}/api/forecast/quality/config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ updates }),
 });
 if (res.ok) {
 showToast(t('forecastQuality.saved'));
 setHasChanges(false);
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('common.saveError'));
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSaving(false);
 }
 };

 const handleReset = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/forecast/quality/config/reset`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 });
 if (res.ok) {
 const data = await res.json();
 const cfg = {};
 for (const [k, v] of Object.entries(data.defaults || {})) {
 if (v === 'true') cfg[k] = true;
 else if (v === 'false') cfg[k] = false;
 else if (/^-?\d+(\.\d+)?$/.test(v)) cfg[k] = Number(v);
 else cfg[k] = v;
 }
 setConfig({ ...FQ_DEFAULTS, ...cfg });
 setHasChanges(false);
 showToast(t('forecastQuality.saved'));
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSaving(false);
 setResetConfirm(false);
 }
 };

 if (loading || !config) {
 return (
 <div className="flex items-center justify-center h-48">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 const wA = config.fq_weight_accuracy || 0;
 const wB = config.fq_weight_bias || 0;
 const wC = config.fq_weight_consistency || 0;
 const weightSum = Math.round((wA + wB + wC) * 100);
 const weightValid = weightSum === 100;
 const weekdays = t('forecastQuality.weekdays').split(',');

 const selectCls = 'px-3 py-1.5 text-sm rounded-[10px] border border-[var(--border-default,#E5E7EB)] bg-surface-elevated text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20';
 const inputCls = 'w-20 px-2 py-1.5 text-sm text-right rounded-[10px] border border-[var(--border-default,#E5E7EB)] bg-surface-elevated text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20';

 return (
 <div className="space-y-4">
 {/* Reset Confirmation Dialog */}
 {resetConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
 <div className="bg-surface-elevated rounded-2xl border border-[var(--border-default,#E5E7EB)] p-6 max-w-sm mx-4 shadow-elevated">
 <div className="flex items-center gap-2 mb-3">
 <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('forecastQuality.resetDefaults')}</h3>
 </div>
 <p className="text-sm text-[var(--text-secondary)] mb-5">{t('forecastQuality.resetConfirm')}</p>
 <div className="flex items-center justify-end gap-2">
 <button onClick={() => setResetConfirm(false)}
 className="px-4 py-2 rounded-[10px] text-sm font-medium border border-[var(--border-default,#E5E7EB)] bg-surface-elevated text-[var(--text-primary)] hover:bg-surface-hover transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleReset} disabled={saving}
 className="px-4 py-2 rounded-[10px] text-sm font-semibold text-white bg-[#EF4444] hover:bg-[#DC2626] transition-colors disabled:opacity-50">
 {t('forecastQuality.resetDefaults')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* ─── Section 1: Score Weights ─── */}
 <ConfigCard icon={BarChart3} title={t('forecastQuality.sectionWeights')}>
 <div className="mt-4 flex gap-6">
 <div className="flex-1 space-y-1">
 {[
 { key: 'fq_weight_accuracy', label: t('forecastQuality.weightAccuracy'), color: '#338edc' },
 { key: 'fq_weight_bias', label: t('forecastQuality.weightBias'), color: '#22D3EE' },
 { key: 'fq_weight_consistency', label: t('forecastQuality.weightConsistency'), color: '#6366F1' },
 ].map(({ key, label, color }) => {
 const val = config[key] ?? 0;
 return (
 <div key={key} className="flex items-center gap-3 py-2.5">
 <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
 <div className="w-36 text-sm font-medium text-[var(--text-primary)]">{label}</div>
 <div className="flex-1 relative h-6 flex items-center">
 <div className="absolute inset-x-0 h-1.5 rounded-full bg-[#E5E7EB]" />
 <div className="absolute left-0 h-1.5 rounded-full transition-all duration-200" style={{ width: `${val * 100}%`, backgroundColor: color }} />
 <input type="range" min={0} max={1} step={0.05} value={val}
 onChange={e => update(key, Number(e.target.value))}
 className="absolute inset-x-0 w-full h-6 opacity-0 cursor-pointer" />
 <div className="absolute h-4 w-4 rounded-full bg-white border-2 shadow-sm pointer-events-none transition-all duration-200"
 style={{ left: `calc(${val * 100}% - 8px)`, borderColor: color }} />
 </div>
 <div className="w-12 text-right text-sm font-semibold text-[var(--text-primary)] tabular-nums">{Math.round(val * 100)}%</div>
 </div>
 );
 })}
 <div className="flex items-center justify-between pt-2 border-t border-[var(--border-default,#E5E7EB)]">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('forecastQuality.weightSum')}</span>
 <span className={`text-xs font-semibold ${weightValid ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
 {weightSum}% {weightValid ? '\u2713' : `\u2014 ${t('forecastQuality.weightSumError')}`}
 </span>
 </div>
 </div>
 <div className="flex flex-col items-center gap-1">
 <span className="text-[11px] font-medium text-[var(--text-secondary)]">{t('forecastQuality.preview')}</span>
 <WeightDonut accuracy={wA} bias={wB} consistency={wC} />
 </div>
 </div>
 </ConfigCard>

 {/* ─── Section 2: Thresholds & Calculations ─── */}
 <ConfigCard icon={Calculator} title={t('forecastQuality.sectionThresholds')}>
 <div className="mt-4 space-y-5">
 {/* Color threshold bar */}
 <div>
 <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">{t('forecastQuality.thresholds')}</div>
 <ThresholdBar
 excellent={Number(config.fq_threshold_excellent)}
 good={Number(config.fq_threshold_good)}
 moderate={Number(config.fq_threshold_moderate)}
 />
 </div>

 {/* Threshold inputs */}
 <div className="space-y-0">
 {[
 { key: 'fq_threshold_excellent', label: t('forecastQuality.thresholdExcellent'), color: '#10B981' },
 { key: 'fq_threshold_good', label: t('forecastQuality.thresholdGood'), color: '#338edc' },
 { key: 'fq_threshold_moderate', label: t('forecastQuality.thresholdModerate'), color: '#F59E0B' },
 ].map(({ key, label, color }) => (
 <div key={key} className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)] last:border-0">
 <div className="flex items-center gap-2">
 <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
 <span className="text-sm font-medium text-[var(--text-primary)]">{label}</span>
 </div>
 <input type="number" min={0} max={100} value={config[key] ?? ''} onChange={e => update(key, Number(e.target.value))} className={inputCls} />
 </div>
 ))}
 </div>

 {/* Calculation parameters */}
 <div>
 <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] pb-2 border-b border-[var(--border-default,#E5E7EB)]">
 {t('forecastQuality.sectionThresholds').split('&').pop()?.trim() || t('forecastQuality.calculation')}
 </div>
 {[
 { key: 'fq_cutoff_hour', label: t('forecastQuality.cutoffHour'), desc: t('forecastQuality.cutoffHourDesc'), min: 0, max: 23, suffix: t('forecastQuality.hourSuffix', 'h') },
 { key: 'fq_min_days', label: t('forecastQuality.minDays'), desc: t('forecastQuality.minDaysDesc'), min: 1, max: 30 },
 { key: 'fq_consistency_window', label: t('forecastQuality.consistencyWindow'), desc: t('forecastQuality.consistencyWindowDesc'), min: 3, max: 60 },
 { key: 'fq_consistency_multiplier', label: t('forecastQuality.consistencyMultiplier'), desc: t('forecastQuality.consistencyMultiplierDesc'), min: 0.5, max: 10, step: 0.5 },
 ].map(({ key, label, desc, min, max, step, suffix }) => (
 <div key={key} className="flex items-start justify-between py-3 border-b border-[var(--border-default,#E5E7EB)] last:border-0">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-[var(--text-primary)]">{label}</div>
 {desc && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{desc}</div>}
 </div>
 <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
 <input type="number" min={min} max={max} step={step || 1} value={config[key] ?? ''} onChange={e => update(key, Number(e.target.value))} className={inputCls} />
 {suffix && <span className="text-xs text-[var(--text-secondary)]">{suffix}</span>}
 </div>
 </div>
 ))}
 </div>
 </div>
 </ConfigCard>

 {/* ─── Section 3: AI Report & Warnings ─── */}
 <ConfigCard icon={Bell} title={t('forecastQuality.sectionReports')}>
 <div className="mt-4 space-y-5">
 {/* Weekly Report */}
 <div>
 <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] pb-2 mb-1">{t('forecastQuality.weeklyReport')}</div>

 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.aiReportEnabled')}</span>
 <ToggleSwitch checked={!!config.fq_ai_report_enabled} onChange={() => update('fq_ai_report_enabled', !config.fq_ai_report_enabled)} />
 </div>

 <div className={`space-y-0 transition-opacity ${config.fq_ai_report_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.aiReportDay')}</span>
 <select value={config.fq_ai_report_day || 1} onChange={e => update('fq_ai_report_day', Number(e.target.value))} className={selectCls}>
 {weekdays.map((d, i) => <option key={i} value={i + 1}>{d}</option>)}
 </select>
 </div>
 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.aiReportHour')}</span>
 <div className="flex items-center gap-1.5">
 <input type="number" min={0} max={23} value={config.fq_ai_report_hour ?? 7} onChange={e => update('fq_ai_report_hour', Number(e.target.value))} className={inputCls} />
 <span className="text-xs text-[var(--text-secondary)]">{t('forecastQuality.hourSuffix', 'h')}</span>
 </div>
 </div>
 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.emailEnabled')}</span>
 <ToggleSwitch checked={!!config.fq_ai_report_email} onChange={() => update('fq_ai_report_email', !config.fq_ai_report_email)} />
 </div>
 <div className={`py-3 border-b border-[var(--border-default,#E5E7EB)] transition-opacity ${config.fq_ai_report_email ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
 <div className="flex items-start justify-between">
 <div>
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.emailRecipients')}</span>
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5">email1@test.com, email2@test.com</div>
 </div>
 <input type="text" value={config.fq_ai_report_recipients || ''}
 onChange={e => update('fq_ai_report_recipients', e.target.value)}
 placeholder="email@example.com"
 className="w-64 px-3 py-1.5 text-sm rounded-[10px] border border-[var(--border-default,#E5E7EB)] bg-surface-elevated text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20" />
 </div>
 </div>
 </div>
 </div>

 {/* Warnings */}
 <div>
 <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] pb-2 mb-1">{t('forecastQuality.warnings')}</div>

 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.warningEnabled')}</span>
 <ToggleSwitch checked={!!config.fq_warning_enabled} onChange={() => update('fq_warning_enabled', !config.fq_warning_enabled)} />
 </div>

 <div className={`space-y-0 transition-opacity ${config.fq_warning_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
 <div className="flex items-start justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <div>
 <div className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.warningThreshold')}</div>
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{t('forecastQuality.warningThresholdDesc')}</div>
 </div>
 <input type="number" min={0} max={100} value={config.fq_warning_threshold ?? 50} onChange={e => update('fq_warning_threshold', Number(e.target.value))} className={inputCls} />
 </div>
 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.warningChannel')}</span>
 <select value={config.fq_warning_channel || 'notification'} onChange={e => update('fq_warning_channel', e.target.value)} className={selectCls}>
 <option value="notification">{t('forecastQuality.channelNotification')}</option>
 <option value="email">{t('forecastQuality.channelEmail')}</option>
 <option value="both">{t('forecastQuality.channelBoth')}</option>
 </select>
 </div>
 </div>
 </div>

 {/* Display / Grid */}
 <div>
 <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] pb-2 mb-1">{t('forecastQuality.gridColumn')}</div>

 <div className="flex items-center justify-between py-3 border-b border-[var(--border-default,#E5E7EB)]">
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.gridBadges')}</span>
 <ToggleSwitch checked={!!config.fq_show_grid_badges} onChange={() => update('fq_show_grid_badges', !config.fq_show_grid_badges)} />
 </div>
 <div className={`flex items-center justify-between py-3 transition-opacity ${config.fq_show_grid_badges ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
 <span className="text-sm font-medium text-[var(--text-primary)]">{t('forecastQuality.badgeFormat')}</span>
 <select value={config.fq_badge_format || 'score'} onChange={e => update('fq_badge_format', e.target.value)} className={selectCls}>
 <option value="score">{t('forecastQuality.badgeScore')}</option>
 <option value="label">{t('forecastQuality.badgeLabel')}</option>
 <option value="both">{t('forecastQuality.badgeBoth')}</option>
 </select>
 </div>
 </div>
 </div>
 </ConfigCard>

 {/* ─── Footer Actions ─── */}
 <div className="flex items-center justify-between">
 <button onClick={() => setResetConfirm(true)} disabled={saving}
 className="flex items-center gap-1.5 px-4 py-2 rounded-[10px] text-sm font-medium border border-[var(--border-default,#E5E7EB)] bg-transparent text-[var(--text-primary)] hover:bg-surface-hover transition-colors disabled:opacity-50">
 <RotateCcw className="w-3.5 h-3.5" />
 {t('forecastQuality.resetDefaults')}
 </button>
 <button onClick={handleSave} disabled={saving || !hasChanges || !weightValid}
 className="px-6 py-2 rounded-[10px] text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]">
 {saving ? t('common.saving') : t('forecastQuality.save')}
 </button>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// STEMPELUHR TAB — Regeln-Verwaltung
// ════════════════════════════════════════════════════════════════
function RegelInput({ regelKey, label, suffix = 'Min.', regeln, setRegeln, saveRegel, saving, t, toggleKey }) {
 // Optional: linker Toggle schaltet Input aktiv/inaktiv (via zweitem stempel_regeln Key)
 // WICHTIG: regeln[toggleKey] kann String (pg numeric) oder Number sein → Number() für robusten Vergleich
 const toggleActive = toggleKey ? Number(regeln[toggleKey] ?? 1) !== 0 : true;
 const disabled = !toggleActive;
 return (
 <div className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <div className="flex items-center gap-3">
 {toggleKey && (
 <button
 onClick={() => saveRegel(toggleKey, toggleActive ? 0 : 1)}
 disabled={saving === toggleKey}
 role="switch"
 aria-checked={toggleActive}
 className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
 style={{ background: toggleActive ? '#338edc' : '#E5E7EB', opacity: saving === toggleKey ? 0.5 : 1 }}
 >
 <span
 className="inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform"
 style={{ transform: toggleActive ? 'translateX(18px)' : 'translateX(2px)' }}
 />
 </button>
 )}
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</span>
 </div>
 <div className="flex items-center gap-2">
 <input
 type="number"
 value={regeln[regelKey] ?? ''}
 onChange={e => setRegeln(prev => ({ ...prev, [regelKey]: e.target.value }))}
 disabled={disabled}
 className="w-20 px-3 py-1.5 text-sm text-right rounded-lg border"
 style={{
 borderColor: 'var(--border-line, #E5E7EB)',
 background: disabled ? 'var(--bg-inset, #F8F9FC)' : 'var(--bg-surface, white)',
 color: disabled ? 'var(--text-muted, #9CA3AF)' : 'var(--text-primary, #1A1A2E)',
 }}
 />
 <span className="text-xs w-8" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{suffix}</span>
 <button
 onClick={() => saveRegel(regelKey, regeln[regelKey])}
 disabled={saving === regelKey || disabled}
 className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
 style={{ background: '#338edc', color: 'white', opacity: (saving === regelKey || disabled) ? 0.5 : 1 }}
 >
 {saving === regelKey ? '...' : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 );
}

function StempeluhrTab({ t, showToast }) {
 const [regeln, setRegeln] = useState({});
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(null);
 const [apiKey, setApiKey] = useState('');
 const [showKey, setShowKey] = useState(false);
 const [appConfig, setAppConfig] = useState({});
 // #879 — Pause-Reminder-SMS (Master-Toggle + Vorlauf)
 const [pauseSms, setPauseSms] = useState({ aktiv: false, vorlauf: 0 });
 const [pauseSmsVorlaufDraft, setPauseSmsVorlaufDraft] = useState('0');
 // #848 — HR-Notify für modellabweichendes Stempeln
 const [hrNotifyIds, setHrNotifyIds] = useState([]);
 const [hrUsers, setHrUsers] = useState([]);
 const [hrDropdownOpen, setHrDropdownOpen] = useState(false);
 const hrRef = useRef(null);
 const [kioskPinSet, setKioskPinSet] = useState(false);
 const [kioskPinNeu, setKioskPinNeu] = useState('');
 const [kioskPinEdit, setKioskPinEdit] = useState('');
 const [kioskPinEditing, setKioskPinEditing] = useState(false);
 const [kioskPinSaving, setKioskPinSaving] = useState(false);
 const [standorte, setStandorte] = useState([]);
 const [barcodeSync, setBarcodeSync] = useState({ loading: false, result: null });
 const [voiceEnabled, setVoiceEnabled] = useState(false);
 const [voiceRandom, setVoiceRandom] = useState(true);
 const [stimmen, setStimmen] = useState([]);
 const [editStimme, setEditStimme] = useState(null);
 const [editSprueche, setEditSprueche] = useState([]);
 const [editSaving, setEditSaving] = useState(false);
 const [editTab, setEditTab] = useState('einstempeln');
 const [newSpruch, setNewSpruch] = useState('');
 const [testPlaying, setTestPlaying] = useState(null);

 useEffect(() => {
 // Load app config
 fetch(`${API}/api/einstellungen`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 const all = Object.values(d.kategorien || {}).flat();
 const cfg = {};
 for (const s of all) {
 if (s.key?.startsWith('stempel_app_') || s.key?.startsWith('stempel_gps_')) {
 cfg[s.key] = String(s.value || '').replace(/"/g, '') === 'true';
 }
 if (s.key === 'kiosk_voice_enabled') {
 setVoiceEnabled(String(s.value || '').replace(/"/g, '') === 'true');
 }
 if (s.key === 'kiosk_voice_random') {
 setVoiceRandom(String(s.value || '').replace(/"/g, '') !== 'false');
 }
 // #879 — Pause-SMS Master-Toggle + Vorlauf
 if (s.key === 'pause_sms_aktiv') {
 const aktiv = String(s.value || '').replace(/"/g, '') === 'true';
 setPauseSms(prev => ({ ...prev, aktiv }));
 }
 if (s.key === 'pause_sms_vorlauf_min') {
 const v = parseInt(String(s.value || '').replace(/"/g, ''), 10);
 const vorlauf = Number.isFinite(v) ? Math.max(0, Math.min(30, v)) : 0;
 setPauseSms(prev => ({ ...prev, vorlauf }));
 setPauseSmsVorlaufDraft(String(vorlauf));
 }
 }
 setAppConfig(cfg);
 }).catch(() => {});
 // Load standorte
 fetch(`${API}/api/stempel/standorte`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(d => setStandorte(d.standorte || []))
 .catch(() => {});
 // Load kiosk PIN — nur noch der Gesetzt-Zustand, nie der Wert (#1816)
 fetch(`${API}/api/einstellungen/kiosk-pin`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(d => setKioskPinSet(!!d.pin_set))
 .catch(() => {});
 // Load stimmen
 loadStimmen();
 }, []);

 const loadStimmen = () => {
 fetch(`${API}/api/einstellungen/kiosk-stimmen`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(d => setStimmen(d.data || []))
 .catch(() => {});
 };

 const openEditStimme = async (stimme) => {
 setEditStimme({ ...stimme });
 setEditTab('einstempeln');
 setNewSpruch('');
 try {
 const r = await fetch(`${API}/api/einstellungen/kiosk-stimmen/${stimme.id}/sprueche`, { credentials: 'include' });
 const d = await r.json();
 setEditSprueche(d.data || []);
 } catch { setEditSprueche([]); }
 };

 const saveStimme = async () => {
 if (!editStimme) return;
 setEditSaving(true);
 try {
 const isNew = !editStimme.id;
 const url = isNew ? `${API}/api/einstellungen/kiosk-stimmen` : `${API}/api/einstellungen/kiosk-stimmen/${editStimme.id}`;
 const r = await fetch(url, {
 method: isNew ? 'POST' : 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ name: editStimme.name, beschreibung: editStimme.beschreibung, elevenlabs_voice_id: editStimme.elevenlabs_voice_id, emoji: editStimme.emoji, aktiv: editStimme.aktiv }),
 });
 if (r.ok) { showToast(t('common.saved', 'Gespeichert')); loadStimmen(); if (isNew) { const d = await r.json(); setEditStimme(d.data); } }
 } catch { /* ignore */ }
 setEditSaving(false);
 };

 const deleteStimme = async (id) => {
 if (!confirm(t('kiosk.stimmen.deleteConfirm', 'Stimme und alle zugehörigen Sprüche löschen?'))) return;
 await fetch(`${API}/api/einstellungen/kiosk-stimmen/${id}`, { method: 'DELETE', credentials: 'include' });
 setEditStimme(null);
 loadStimmen();
 };

 const addSpruch = async () => {
 if (!newSpruch.trim() || !editStimme?.id) return;
 const r = await fetch(`${API}/api/einstellungen/kiosk-stimmen/${editStimme.id}/sprueche`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ typ: editTab, spruch: newSpruch.trim() }),
 });
 if (r.ok) {
 const d = await r.json();
 setEditSprueche(prev => [...prev, d.data]);
 setNewSpruch('');
 loadStimmen();
 }
 };

 const updateSpruch = async (spruchId, updates) => {
 const r = await fetch(`${API}/api/einstellungen/kiosk-sprueche/${spruchId}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(updates),
 });
 if (r.ok) {
 const d = await r.json();
 setEditSprueche(prev => prev.map(s => s.id === spruchId ? d.data : s));
 }
 };

 const deleteSpruch = async (spruchId) => {
 await fetch(`${API}/api/einstellungen/kiosk-sprueche/${spruchId}`, { method: 'DELETE', credentials: 'include' });
 setEditSprueche(prev => prev.filter(s => s.id !== spruchId));
 loadStimmen();
 };

 const testStimme = async (stimmeId) => {
 setTestPlaying(stimmeId);
 try {
 const r = await fetch(`${API}/api/einstellungen/kiosk-stimmen/${stimmeId}/test`, { method: 'POST', credentials: 'include' });
 if (r.ok) {
 const blob = await r.blob();
 const url = URL.createObjectURL(blob);
 const audio = new Audio(url);
 audio.onended = () => { setTestPlaying(null); URL.revokeObjectURL(url); };
 audio.onerror = () => { setTestPlaying(null); URL.revokeObjectURL(url); };
 audio.play();
 } else {
 const d = await r.json().catch(() => ({}));
 showToast(d.error || 'Fehler', 'error');
 setTestPlaying(null);
 }
 } catch { setTestPlaying(null); }
 };

 const toggleAppConfig = async (key) => {
 const newVal = !appConfig[key];
 setAppConfig(prev => ({ ...prev, [key]: newVal }));
 try {
 await fetch(`${API}/api/einstellungen/${key}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ value: newVal ? 'true' : 'false' }),
 });
 showToast(t('stempel.regeln.gespeichert', 'Gespeichert'));
 } catch { setAppConfig(prev => ({ ...prev, [key]: !newVal })); }
 };

 // #879 — Pause-SMS Master-Toggle + Vorlauf speichern
 const togglePauseSmsAktiv = async () => {
 const newVal = !pauseSms.aktiv;
 setPauseSms(prev => ({ ...prev, aktiv: newVal }));
 try {
 await fetch(`${API}/api/einstellungen/pause_sms_aktiv`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ value: newVal ? 'true' : 'false' }),
 });
 showToast(t('stempel.regeln.gespeichert', 'Gespeichert'));
 } catch { setPauseSms(prev => ({ ...prev, aktiv: !newVal })); }
 };
 const savePauseSmsVorlauf = async () => {
 const parsed = parseInt(pauseSmsVorlaufDraft, 10);
 const clamped = Number.isFinite(parsed) ? Math.max(0, Math.min(30, parsed)) : 0;
 setPauseSms(prev => ({ ...prev, vorlauf: clamped }));
 setPauseSmsVorlaufDraft(String(clamped));
 try {
 await fetch(`${API}/api/einstellungen/pause_sms_vorlauf_min`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ value: String(clamped) }),
 });
 showToast(t('stempel.regeln.gespeichert', 'Gespeichert'));
 } catch { /* */ }
 };

 const saveRadius = async (id, radius) => {
 try {
 await fetch(`${API}/api/stempel/standorte/${id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ radius_meter: parseInt(radius) }),
 });
 showToast(t('stempel.regeln.gespeichert', 'Gespeichert'));
 } catch { /* */ }
 };

 useEffect(() => {
 fetch(`${API}/api/stempel/regeln`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(data => {
 const map = {};
 for (const r of (data.regeln || data || [])) map[r.regel_key] = r.wert;
 setRegeln(map);
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 // Load API key + hr_notify_user_ids
 fetch(`${API}/api/einstellungen`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 const all = Object.values(d.kategorien || {}).flat();
 const key = all.find(s => s.key === 'stempel_api_key');
 if (key) setApiKey(String(key.value || '').replace(/"/g, ''));
 const hr = all.find(s => s.key === 'hr_notify_user_ids');
 if (hr) {
 let arr = [];
 try {
 const raw = hr.value;
 if (Array.isArray(raw)) arr = raw;
 else if (typeof raw === 'string') arr = JSON.parse(raw);
 } catch { arr = []; }
 setHrNotifyIds(Array.isArray(arr) ? arr : []);
 }
 })
 .catch(() => {});
 // #848 — Admin users for HR multi-select
 fetch(`${API}/api/users`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(d => {
 const arr = Array.isArray(d) ? d : (d.users || []);
 setHrUsers(arr.filter(u => ['administrator', 'ops_manager', 'cs_teamlead'].includes(u.rolle)));
 })
 .catch(() => {});
 }, []);

 // #848 — Click-outside handler for HR dropdown
 useEffect(() => {
 const handler = (e) => { if (hrRef.current && !hrRef.current.contains(e.target)) setHrDropdownOpen(false); };
 document.addEventListener('mousedown', handler);
 return () => document.removeEventListener('mousedown', handler);
 }, []);

 const toggleHrUser = async (uid) => {
 const next = hrNotifyIds.includes(uid) ? hrNotifyIds.filter(id => id !== uid) : [...hrNotifyIds, uid];
 setHrNotifyIds(next);
 try {
 await fetch(`${API}/api/einstellungen/hr_notify_user_ids`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ value: next }),
 });
 } catch { /* revert on error */ setHrNotifyIds(hrNotifyIds); }
 };

 const saveRegel = async (key, value) => {
 setSaving(key);
 try {
 const res = await fetch(`${API}/api/stempel/regeln/${key}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ wert: parseFloat(value) }),
 });
 if (res.ok) {
 showToast(t('stempel.regeln.gespeichert', 'Regeln gespeichert'));
 setRegeln(prev => ({ ...prev, [key]: parseFloat(value) }));
 } else {
 const errData = await res.json().catch(() => ({}));
 showToast(errData.error || t('stempel.regeln.fehler', 'Fehler beim Speichern'));
 }
 } catch {
 showToast(t('stempel.regeln.fehler', 'Fehler beim Speichern'));
 }
 setSaving(null);
 };

 if (loading) return <div className="py-12 text-center text-sm" style={{ color: 'var(--text-tertiary)' }}>{t('common.loading', 'Laden...')}</div>;

 return (
 <div className="space-y-4">
 {/* Card 1: Kurze Pause */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{'\u2615'} {t('stempel.regeln.kurzePause', 'Kurze Pause')}</h2>
 </div>
 <div className="px-5">
 <RegelInput regelKey="kurze_pause_max_minuten" label={t('stempel.regeln.maxDauer', 'Max. Dauer')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="kurze_pause_fruehestens_nach" label={t('stempel.regeln.fruehestensNach', 'Frühestens nach Arbeitsbeginn')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="kurze_pause_abstand_minuten" toggleKey="abstand_zwischen_pausen_aktiv" label={t('stempel.regeln.abstand', 'Abstand zwischen Pausen')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="kurze_pause_max_anzahl" label={t('stempel.regeln.maxAnzahl', 'Max. Anzahl pro Tag')} suffix="" regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 </div>
 </div>

 {/* Card 2: Mittagspause */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{'\u{1F37D}\uFE0F'} {t('stempel.regeln.mittagspause', 'Mittagspause')}</h2>
 </div>
 <div className="px-5">
 <RegelInput regelKey="mittag_min_minuten" label={t('stempel.regeln.mindestdauer', 'Mindestdauer')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="mittag_max_minuten" label={t('stempel.regeln.maxDauerWarnung', 'Maximaldauer (Warnung)')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="mittag_fruehestens_nach" label={t('stempel.regeln.fruehestensNach', 'Frühestens nach Arbeitsbeginn')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="mittag_pflicht_ab_stunden" label={t('stempel.regeln.pflichtAb', 'Pflicht ab Arbeitszeit')} suffix="Std." regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 </div>
 </div>

 {/* Card 3: Allgemein */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{'\u2699\uFE0F'} {t('stempel.regeln.allgemein', 'Allgemein')}</h2>
 </div>
 <div className="px-5">
 <RegelInput regelKey="max_arbeitszeit_ohne_pause" label={t('stempel.regeln.maxOhnePause', 'Max. AZ ohne Pause')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 <RegelInput regelKey="auto_ausstempel_nach" label={t('stempel.regeln.autoAusstempel', 'Auto-Ausstempel nach')} regeln={regeln} setRegeln={setRegeln} saveRegel={saveRegel} saving={saving} t={t} />
 {/* API Key display */}
 <div className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>API-Key</span>
 <div className="flex items-center gap-2">
 <code className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-primary, #1A1A2E)' }}>
 {showKey ? apiKey : (apiKey ? `${apiKey.substring(0, 12)}${'*'.repeat(20)}` : '–')}
 </code>
 <button onClick={() => setShowKey(!showKey)} className="text-xs underline" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {showKey ? t('common.hide', 'Ausblenden') : t('common.show', 'Anzeigen')}
 </button>
 <button
 onClick={() => { navigator.clipboard.writeText(apiKey); showToast('Kopiert!'); }}
 className="text-xs underline" style={{ color: '#338edc' }}
 >
 {t('common.copy', 'Kopieren')}
 </button>
 </div>
 </div>
 </div>
 </div>

 {/* Card: HR-Benachrichtigungen (#848) */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">👥 {t('stempel.regeln.hrNotify', 'HR-Benachrichtigungen')}</h2>
 </div>
 <div className="px-5 py-4">
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
 {t('stempel.regeln.hrNotifyDesc', 'User, die bei Stempel außerhalb Arbeitszeitmodell (Sa/So/Feiertag) benachrichtigt werden')}
 </label>
 <div className="relative" ref={hrRef}>
 <button
 type="button"
 onClick={() => setHrDropdownOpen(!hrDropdownOpen)}
 className="w-full px-3 py-2 rounded-lg border border-[var(--border-line,#E5E7EB)] bg-[var(--bg-surface,#fff)] text-sm text-left flex items-center justify-between min-h-[38px]"
 >
 <span className="flex flex-wrap gap-1">
 {hrNotifyIds.length === 0 ? (
 <span className="text-[var(--text-tertiary,#6B7280)]">{t('stempel.regeln.selectUsers', 'User auswählen…')}</span>
 ) : (
 hrNotifyIds.map(uid => {
 const u = hrUsers.find(u => u.id === uid);
 return u ? (
 <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[var(--bg-inset,#F8F9FC)] text-xs font-medium text-[var(--text-primary,#1A1A2E)]">
 {u.vorname || u.username} {u.nachname || ''}
 <button type="button" onClick={e => { e.stopPropagation(); toggleHrUser(uid); }} className="text-[var(--text-tertiary)] hover:text-red-500">×</button>
 </span>
 ) : (
 <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[var(--bg-inset,#F8F9FC)] text-xs text-[var(--text-tertiary)]">
 ID {uid}
 <button type="button" onClick={e => { e.stopPropagation(); toggleHrUser(uid); }} className="hover:text-red-500">×</button>
 </span>
 );
 })
 )}
 </span>
 <svg className={`w-4 h-4 text-[var(--text-tertiary)] transition-transform ${hrDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
 </button>
 {hrDropdownOpen && (
 <div className="absolute z-20 mt-1 w-full bg-[var(--bg-surface,#fff)] border border-[var(--border-line,#E5E7EB)] rounded-lg shadow-sm max-h-48 overflow-y-auto">
 {hrUsers.length === 0 ? (
 <div className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{t('stempel.regeln.noUsers', 'Keine User verfügbar')}</div>
 ) : (
 hrUsers.map(u => (
 <button
 key={u.id}
 type="button"
 onClick={() => toggleHrUser(u.id)}
 className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
 >
 <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${hrNotifyIds.includes(u.id) ? 'bg-[#338edc] border-[#338edc] text-white' : 'border-[var(--border-line,#E5E7EB)]'}`}>
 {hrNotifyIds.includes(u.id) && '✓'}
 </span>
 <span className="text-[var(--text-primary,#1A1A2E)]">{u.vorname || u.username} {u.nachname || ''}</span>
 <span className="text-xs text-[var(--text-tertiary,#6B7280)] ml-auto">{u.rolle}</span>
 </button>
 ))
 )}
 </div>
 )}
 </div>
 <p className="mt-2 text-xs text-[var(--text-tertiary,#6B7280)]">
 {t('stempel.regeln.hrNotifyInfo', 'Pro Event (arbeit_start / arbeitsende) sofort: In-App-Notification + E-Mail. Leere Liste → nur Warn-Log.')}
 </p>
 </div>
 </div>

 {/* Card: Terminals — Kiosk = registriertes Geraet (#1809-Fix) */}
 <KioskGeraeteVerwaltung t={t} showToast={showToast} />

 {/* Card: Terminal-PIN (#1816 — nur Zustand, nie Klartext) */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{t('stempel.einstellungen.kioskPin', 'Terminal-PIN')}</h2>
 </div>
 <div className="px-5">
 <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <div>
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('stempel.einstellungen.adminPin', 'Terminal-PIN')}</span>
 <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{t('stempel.einstellungen.adminPinDesc', 'Entsperrt ein deaktiviertes Terminal und dient als Notweg für nicht registrierte Geräte (4–6 Ziffern). Wird nur als Hash gespeichert und nach dem Setzen nicht mehr angezeigt.')}</p>
 </div>
 <div className="flex items-center gap-2">
 {kioskPinEditing ? (
 <>
 <input
 type="text"
 inputMode="numeric"
 pattern="[0-9]*"
 maxLength={6}
 value={kioskPinEdit}
 onChange={e => setKioskPinEdit(e.target.value.replace(/\D/g, '').slice(0, 6))}
 className="w-24 px-3 py-1.5 text-sm text-center rounded-lg border font-mono tracking-widest"
 style={{ borderColor: 'var(--border-line, #E5E7EB)', background: 'var(--bg-surface, white)', color: 'var(--text-primary, #1A1A2E)' }}
 autoFocus
 />
 <button
 onClick={async () => {
 if (!/^\d{4,6}$/.test(kioskPinEdit)) return;
 setKioskPinSaving(true);
 try {
 const r = await fetch(`${API}/api/einstellungen/kiosk-pin`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ pin: kioskPinEdit }),
 });
 if (r.ok) { setKioskPinSet(true); setKioskPinNeu(''); setKioskPinEdit(''); setKioskPinEditing(false); showToast(t('common.saved', 'Gespeichert')); }
 } catch { /* */ }
 setKioskPinSaving(false);
 }}
 disabled={kioskPinSaving || !/^\d{4,6}$/.test(kioskPinEdit)}
 className="px-3 py-1.5 text-xs font-medium rounded-lg text-white"
 style={{ background: '#338edc', opacity: kioskPinSaving || !/^\d{4,6}$/.test(kioskPinEdit) ? 0.5 : 1 }}
 >
 {kioskPinSaving ? '...' : t('common.save', 'Speichern')}
 </button>
 <button onClick={() => setKioskPinEditing(false)} className="text-xs underline" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('common.cancel', 'Abbrechen')}
 </button>
 </>
 ) : (
 <>
 <span className="text-xs font-medium px-3 py-1.5 rounded-lg"
 style={kioskPinSet ? { background: '#DCFCE7', color: '#166534' } : { background: '#FEF3C7', color: '#92400E' }}>
 {kioskPinSet
 ? t('stempel.einstellungen.pinGesetzt', 'PIN gesetzt')
 : t('stempel.einstellungen.pinFehlt', 'Keine PIN gesetzt')}
 </span>
 <button onClick={() => { setKioskPinEdit(''); setKioskPinNeu(''); setKioskPinEditing(true); }} className="text-xs underline" style={{ color: '#338edc' }}>
 {t('common.edit', 'Ändern')}
 </button>
 </>
 )}
 </div>
 </div>
 <div className="flex items-center justify-between py-3" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>{t('stempel.einstellungen.pinGenerieren', 'Neue PIN generieren')}</span>
 <button
 onClick={async () => {
 try {
 const r = await fetch(`${API}/api/einstellungen/kiosk-pin/generieren`, { method: 'POST', credentials: 'include' });
 const d = await r.json();
 if (r.ok && d.pin) { setKioskPinNeu(d.pin); setKioskPinSet(true); }
 } catch { /* */ }
 }}
 className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
 style={{ background: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary, #4B5563)', border: '1px solid var(--border-line, #E5E7EB)' }}
 >
 {t('stempel.einstellungen.generieren', 'Generieren')}
 </button>
 </div>
 {kioskPinNeu && (
 <div className="mb-4 px-4 py-3 rounded-lg" style={{ background: '#FEF3C7', color: '#92400E' }}>
 <p className="text-[11px] font-semibold mb-1">
 {t('stempel.einstellungen.pinEinmalig', 'Jetzt notieren — die PIN wird nicht wieder angezeigt.')}
 </p>
 <code className="text-lg font-mono tracking-[0.3em]">{kioskPinNeu}</code>
 </div>
 )}
 </div>
 </div>

 {/* Card: Stimmen-Profile */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <h2 className="text-sm font-semibold text-content">{t('kiosk.stimmen.title', 'Stimmen-Profile')}</h2>
 <div className="flex items-center gap-3">
 {/* Voice enabled toggle */}
 <ToggleSwitch
 checked={voiceEnabled}
 onChange={async () => {
 const newVal = !voiceEnabled;
 setVoiceEnabled(newVal);
 try {
 await fetch(`${API}/api/einstellungen`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ key: 'kiosk_voice_enabled', value: String(newVal) }),
 });
 } catch { setVoiceEnabled(!newVal); }
 }}
 />
 <button
 onClick={() => setEditStimme({ name: '', beschreibung: '', elevenlabs_voice_id: '', emoji: '🎤', aktiv: true })}
 className="px-3 py-1.5 text-xs font-medium rounded-lg text-white"
 style={{ background: '#338edc' }}
 >
 + {t('kiosk.stimmen.new', 'Neue Stimme')}
 </button>
 </div>
 </div>
 <div className="p-4 space-y-3">
 {stimmen.map(s => (
 <div key={s.id} className="flex items-center justify-between p-4 rounded-xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-surface,#fff)]">
 <div className="flex items-center gap-3 min-w-0">
 <span className="text-2xl">{s.emoji || '🎤'}</span>
 <div className="min-w-0">
 <div className="flex items-center gap-2">
 <span className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{s.name}</span>
 <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${s.aktiv ? 'bg-green-100 text-green-700' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-muted,#9CA3AF)]'}`}>
 {s.aktiv ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}
 </span>
 </div>
 <p className="text-xs text-[var(--text-tertiary,#6B7280)] truncate">{s.beschreibung}</p>
 <p className="text-[10px] text-[var(--text-muted,#9CA3AF)] mt-0.5 font-mono">
 Voice-ID: {s.elevenlabs_voice_id ? s.elevenlabs_voice_id.slice(0, 12) + '...' : t('kiosk.stimmen.noVoiceId', '(nicht gesetzt)')} | {s.spruch_count || 0} {t('kiosk.stimmen.sayings', 'Sprüche')}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-2 ml-3">
 <button onClick={() => openEditStimme(s)} className="px-2.5 py-1 text-xs rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)]">
 {t('common.edit', 'Bearbeiten')}
 </button>
 <button
 onClick={() => testStimme(s.id)}
 disabled={testPlaying === s.id || !s.elevenlabs_voice_id}
 className="px-2.5 py-1 text-xs rounded-lg border border-[var(--border-card,#E5E7EB)] text-[#338edc] hover:bg-blue-50 disabled:opacity-40"
 >
 {testPlaying === s.id ? '...' : '🔊 Test'}
 </button>
 </div>
 </div>
 ))}
 {!stimmen.length && <p className="text-sm text-center py-4 text-[var(--text-muted,#9CA3AF)]">{t('kiosk.stimmen.empty', 'Keine Stimmen angelegt')}</p>}

 {/* Random mode toggle */}
 <div className="flex items-center justify-between pt-3 border-t border-[var(--border-card,#E5E7EB)]">
 <div>
 <span className="text-sm text-[var(--text-secondary,#4B5563)]">{t('kiosk.stimmen.randomMode', 'Zufallsmodus')}</span>
 <p className="text-xs text-[var(--text-tertiary,#6B7280)]">{t('kiosk.stimmen.randomDesc', 'Bei jedem Stempeln wird zufällig eine aktive Stimme ausgewählt')}</p>
 </div>
 <ToggleSwitch
 checked={voiceRandom}
 onChange={async () => {
 const newVal = !voiceRandom;
 setVoiceRandom(newVal);
 try {
 await fetch(`${API}/api/einstellungen`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ key: 'kiosk_voice_random', value: String(newVal) }),
 });
 } catch { setVoiceRandom(!newVal); }
 }}
 />
 </div>
 </div>
 </div>

 {/* ── Stimme Edit Modal ── */}
 {editStimme && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditStimme(null)}>
 <div className="bg-[var(--bg-surface,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
 <div className="px-6 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center justify-between">
 <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
 {editStimme.id ? t('kiosk.stimmen.edit', 'Stimme bearbeiten') : t('kiosk.stimmen.create', 'Neue Stimme')}
 </h3>
 <button onClick={() => setEditStimme(null)} className="text-[var(--text-muted,#9CA3AF)] hover:text-[var(--text-secondary,#4B5563)]">
 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>
 <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
 {/* Name + Emoji */}
 <div className="grid grid-cols-[1fr_80px] gap-3">
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('kiosk.stimmen.name', 'Name')}</label>
 <input value={editStimme.name} onChange={e => setEditStimme(p => ({ ...p, name: e.target.value }))} className="w-full border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary,#1A1A2E)] bg-[var(--bg-surface,#fff)]" placeholder="z.B. Osterhase" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">Emoji</label>
 <input value={editStimme.emoji || ''} onChange={e => setEditStimme(p => ({ ...p, emoji: e.target.value }))} className="w-full border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-center bg-[var(--bg-surface,#fff)]" />
 </div>
 </div>
 {/* Description */}
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('kiosk.stimmen.desc', 'Beschreibung')}</label>
 <input value={editStimme.beschreibung || ''} onChange={e => setEditStimme(p => ({ ...p, beschreibung: e.target.value }))} className="w-full border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary,#1A1A2E)] bg-[var(--bg-surface,#fff)]" />
 </div>
 {/* Voice ID */}
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">ElevenLabs Voice-ID</label>
 <input value={editStimme.elevenlabs_voice_id || ''} onChange={e => setEditStimme(p => ({ ...p, elevenlabs_voice_id: e.target.value }))} className="w-full border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-2 text-sm font-mono text-[var(--text-primary,#1A1A2E)] bg-[var(--bg-surface,#fff)]" placeholder="z.B. MOOG1hZESAxDt4UaletY" />
 </div>
 {/* Active toggle */}
 <div className="flex items-center justify-between">
 <span className="text-sm text-[var(--text-secondary,#4B5563)]">{t('common.active', 'Aktiv')}</span>
 <ToggleSwitch
 checked={editStimme.aktiv}
 onChange={() => setEditStimme(p => ({ ...p, aktiv: !p.aktiv }))}
 />
 </div>

 {/* Sprüche Section — only for saved stimmen */}
 {editStimme.id && (
 <div className="border-t border-[var(--border-card,#E5E7EB)] pt-4">
 <h4 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3">{t('kiosk.stimmen.sayings', 'Sprüche')}</h4>
 {/* Typ Tabs */}
 <div className="flex gap-1 p-[3px] rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] mb-3">
 {['einstempeln', 'ausstempeln', 'pause_start', 'pause_ende'].map(tab => (
 <button
 key={tab}
 onClick={() => setEditTab(tab)}
 className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-[8px] transition-all ${editTab === tab ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'}`}
 >
 {{ einstempeln: t('kiosk.stimmen.tabEin', 'Einstempeln'), ausstempeln: t('kiosk.stimmen.tabAus', 'Ausstempeln'), pause_start: t('kiosk.stimmen.tabPauseStart', 'Pause Start'), pause_ende: t('kiosk.stimmen.tabPauseEnd', 'Pause Ende') }[tab]}
 </button>
 ))}
 </div>
 {/* Sprüche list */}
 <div className="space-y-2 max-h-60 overflow-y-auto">
 {editSprueche.filter(s => s.typ === editTab).map(s => (
 <div key={s.id} className="flex items-start gap-2 group">
 <input
 defaultValue={s.spruch}
 onBlur={e => { if (e.target.value !== s.spruch) updateSpruch(s.id, { spruch: e.target.value }); }}
 className="flex-1 border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-xs text-[var(--text-primary,#1A1A2E)] bg-[var(--bg-surface,#fff)]"
 />
 <button onClick={() => deleteSpruch(s.id)} className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 </button>
 </div>
 ))}
 {editSprueche.filter(s => s.typ === editTab).length === 0 && (
 <p className="text-xs text-[var(--text-muted,#9CA3AF)] text-center py-2">{t('kiosk.stimmen.noSayings', 'Keine Sprüche für diesen Typ')}</p>
 )}
 </div>
 {/* Add spruch */}
 <div className="flex gap-2 mt-3">
 <input
 value={newSpruch}
 onChange={e => setNewSpruch(e.target.value)}
 onKeyDown={e => e.key === 'Enter' && addSpruch()}
 placeholder={t('kiosk.stimmen.addPlaceholder', 'Neuer Spruch... ({{name}} = Vorname)')}
 className="flex-1 border border-[var(--border-card,#E5E7EB)] rounded-lg px-3 py-1.5 text-xs text-[var(--text-primary,#1A1A2E)] bg-[var(--bg-surface,#fff)]"
 />
 <button onClick={addSpruch} disabled={!newSpruch.trim()} className="px-3 py-1.5 text-xs rounded-lg text-white disabled:opacity-40" style={{ background: '#338edc' }}>+</button>
 </div>
 </div>
 )}
 </div>
 <div className="px-6 py-3 border-t border-[var(--border-card,#E5E7EB)] flex items-center justify-between">
 <div>
 {editStimme.id && (
 <button onClick={() => deleteStimme(editStimme.id)} className="text-xs text-red-500 hover:underline">
 {t('kiosk.stimmen.delete', 'Stimme löschen')}
 </button>
 )}
 </div>
 <div className="flex gap-2">
 <button onClick={() => setEditStimme(null)} className="px-4 py-2 text-xs rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary,#4B5563)]">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button onClick={saveStimme} disabled={editSaving || !editStimme.name} className="px-4 py-2 text-xs rounded-lg text-white disabled:opacity-40" style={{ background: '#338edc' }}>
 {editSaving ? '...' : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Card 4: App-Stempeln + GPS */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{t('stempel.einstellungen.app', 'App-Stempeln')}</h2>
 </div>
 <div className="px-5">
 {[
 { key: 'stempel_app_enabled', label: t('stempel.einstellungen.appEnabled', 'App-Stempeln aktiviert') },
 { key: 'stempel_gps_pflicht', label: t('stempel.einstellungen.gpsPflicht', 'GPS-Position Pflicht') },
 { key: 'stempel_gps_ausserhalb_erlaubt', label: t('stempel.einstellungen.ausserhalbErlaubt', 'Außerhalb Radius erlaubt (mit Warnung)') },
 { key: 'stempel_gps_ausserhalb_blockieren', label: t('stempel.einstellungen.ausserhalbBlockieren', 'Außerhalb Radius blockieren') },
 ].map(item => (
 <div key={item.key} className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>{item.label}</span>
 <ToggleSwitch
 checked={!!appConfig[item.key]}
 onChange={() => toggleAppConfig(item.key)}
 />
 </div>
 ))}
 </div>
 </div>

 {/* Card 4b: Pause-Reminder-SMS (#879) */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">
 {t('pauseSms.card_title', 'Pause-Reminder-SMS')}
 </h2>
 <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('pauseSms.card_hint', 'SMS an MA kurz vor Auto-Mittagspause. Nur wenn Master aktiv UND MA Opt-in gesetzt UND Mobilnummer hinterlegt.')}
 </p>
 </div>
 <div className="px-5">
 <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <span className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('pauseSms.master_toggle', 'Master-Toggle aktiv')}
 </span>
 <ToggleSwitch checked={!!pauseSms.aktiv} onChange={togglePauseSmsAktiv} />
 </div>
 <div className="flex items-center justify-between py-3" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <div>
 <div className="text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('pauseSms.vorlauf_label', 'Vorlauf (Minuten vor Pause-Start)')}
 </div>
 <div className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
 {t('pauseSms.vorlauf_hint', '0–30 Minuten. 0 = SMS exakt zum Pause-Start.')}
 </div>
 </div>
 <div className="flex items-center gap-2">
 <input
 type="number"
 min={0}
 max={30}
 value={pauseSmsVorlaufDraft}
 onChange={(e) => setPauseSmsVorlaufDraft(e.target.value)}
 onBlur={savePauseSmsVorlauf}
 onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
 className="w-20 px-2 py-1 text-sm text-right border rounded-lg"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 />
 <span className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>min</span>
 </div>
 </div>
 </div>
 </div>

 {/* Card 5: Standort-Radien */}
 {standorte.length > 0 && (
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{t('stempel.einstellungen.standortRadien', 'Standort-Radien')}</h2>
 </div>
 <div className="px-5">
 {standorte.map(s => (
 <div key={s.id} className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: 'var(--border-line, #E5E7EB)' }}>
 <div>
 <span className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{s.name}</span>
 {s.latitude && <span className="text-xs ml-2" style={{ color: 'var(--text-tertiary, #6B7280)' }}>({s.latitude}, {s.longitude})</span>}
 </div>
 <div className="flex items-center gap-2">
 <input
 type="number"
 defaultValue={s.radius_meter || 200}
 onBlur={(e) => saveRadius(s.id, e.target.value)}
 className="w-20 px-2 py-1 text-sm text-right border rounded-lg"
 style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
 min={50}
 max={5000}
 />
 <span className="text-xs" style={{ color: 'var(--text-tertiary, #6B7280)' }}>m</span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Card 6: Barcode-Sync aus Odoo */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">{t('stempel.einstellungen.barcodeSync', 'Barcode-Sync')}</h2>
 </div>
 <div className="px-5 py-4">
 <p className="text-sm mb-3" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 {t('stempel.einstellungen.barcodeSyncDesc', 'Barcodes aus Odoo HR importieren und lokale Mitarbeiter-Barcodes aktualisieren.')}
 </p>
 <button
 onClick={async () => {
 setBarcodeSync({ loading: true, result: null });
 try {
 const r = await fetch(`${API}/api/admin/sync-barcodes`, { method: 'POST', credentials: 'include' });
 const d = await r.json();
 if (!r.ok) throw new Error(d.error || 'Fehler');
 setBarcodeSync({ loading: false, result: d });
 showToast(t('stempel.einstellungen.barcodeSyncOk', '{{synced}} Barcodes aktualisiert', { synced: d.synced }), 'success');
 } catch (err) {
 setBarcodeSync({ loading: false, result: null });
 showToast(err.message, 'error');
 }
 }}
 disabled={barcodeSync.loading}
 className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
 style={{ background: 'var(--accent-primary, #338edc)' }}
 >
 {barcodeSync.loading
 ? t('stempel.einstellungen.barcodeSyncing', 'Synchronisiere...')
 : t('stempel.einstellungen.barcodeSyncBtn', 'Barcodes von Odoo importieren')}
 </button>
 {barcodeSync.result && (
 <div className="mt-3 text-sm space-y-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
 <div>{t('stempel.einstellungen.barcodeSyncTotal', 'Odoo-Barcodes')}: <span className="font-medium">{barcodeSync.result.total}</span></div>
 <div>{t('stempel.einstellungen.barcodeSyncUpdated', 'Aktualisiert')}: <span className="font-medium text-green-600">{barcodeSync.result.synced}</span></div>
 <div>{t('stempel.einstellungen.barcodeSyncSkipped', 'Unverändert')}: <span className="font-medium">{barcodeSync.result.skipped}</span></div>
 {barcodeSync.result.notFound > 0 && (
 <div>{t('stempel.einstellungen.barcodeSyncNotFound', 'Nicht zugeordnet')}: <span className="font-medium text-amber-600">{barcodeSync.result.notFound}</span></div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// SMS CONFIG CARD — 3-Segment Toggle (Active / Test / Disabled)
// ════════════════════════════════════════════════════════════════
const SMS_MODE_DEFS = [
 { key: 'active', dotColor: 'bg-green-500', labelKey: 'sms.modeActive' },
 { key: 'test', dotColor: 'bg-amber-500', labelKey: 'sms.modeTest' },
 { key: 'disabled', dotColor: 'bg-red-500', labelKey: 'sms.modeDisabled' },
];

function SmsConfigCard({ t, showToast }) {
 const [mode, setMode] = useState('active');
 const [testRedirect, setTestRedirect] = useState('');
 const [savedMode, setSavedMode] = useState('active');
 const [savedRedirect, setSavedRedirect] = useState('');
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [showConfirm, setShowConfirm] = useState(false);

 useEffect(() => {
 fetch(`${API}/api/sms/send-config`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 if (d.config) {
 setMode(d.config.mode || 'active');
 setTestRedirect(d.config.test_redirect || '');
 setSavedMode(d.config.mode || 'active');
 setSavedRedirect(d.config.test_redirect || '');
 }
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const isDirty = mode !== savedMode || testRedirect !== savedRedirect;
 const isValidPhone = (p) => /^\+\d{10,15}$/.test(p.replace(/[\s\-()]/g, ''));
 const canSave = isDirty && !(mode === 'test' && testRedirect && !isValidPhone(testRedirect));

 const handleModeSelect = (newMode) => {
 if (newMode === 'disabled' && mode !== 'disabled') {
 setShowConfirm(true);
 } else {
 setMode(newMode);
 }
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/sms/send-config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ mode, test_redirect: testRedirect }),
 });
 if (res.ok) {
 setSavedMode(mode);
 setSavedRedirect(testRedirect);
 showToast(t('common.saved'));
 } else showToast(t('common.error'));
 } catch { showToast(t('common.error')); }
 setSaving(false);
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-32">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {/* Send Config Card */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
 <MessageSquare className="w-4 h-4" /> {t('sms.sendConfig')}
 </h2>
 </div>
 <div className="px-5 py-4 space-y-4">
 {/* 3-Segment Toggle */}
 <div className="inline-flex gap-0.5 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
 {SMS_MODE_DEFS.map(md => (
 <button
 key={md.key}
 onClick={() => handleModeSelect(md.key)}
 className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-medium transition-all ${
 mode === md.key
 ? 'bg-white dark:bg-surface-elevated text-content font-semibold shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 <span className={`inline-block w-2 h-2 rounded-full ${md.dotColor}`} />
 {t(md.labelKey)}
 </button>
 ))}
 </div>

 {/* Mode Info */}
 {mode === 'active' && (
 <p className="text-xs text-[var(--text-secondary)]">{t('sms.modeActiveDesc')}</p>
 )}
 {mode === 'test' && (
 <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 text-xs">
 <TestTube2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
 <span>{t('sms.modeTestDesc')}</span>
 </div>
 )}
 {mode === 'disabled' && (
 <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs">
 <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
 <span>{t('sms.modeDisabledDesc')}</span>
 </div>
 )}

 {/* Test Redirect Number */}
 {mode === 'test' && (
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('sms.testRedirect')}</span>
 <input
 value={testRedirect}
 onChange={e => setTestRedirect(e.target.value)}
 className="mt-1 w-full px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 placeholder={t('sms.testRedirectPlaceholder')}
 type="tel"
 />
 {testRedirect && !isValidPhone(testRedirect) && (
 <span className="text-xs text-red-500 mt-1 block">{t('sms.invalidPhone')}</span>
 )}
 </label>
 )}

 {/* Save */}
 <button
 onClick={handleSave}
 disabled={saving || !canSave}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
 >
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </div>

 {/* Disable Confirmation */}
 {showConfirm && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl shadow-sm w-full max-w-md border border-[var(--border-default)] p-6 space-y-4">
 <h3 className="text-base font-semibold text-content">{t('sms.confirmDisableTitle')}</h3>
 <p className="text-sm text-[var(--text-secondary)]">{t('sms.confirmDisableText')}</p>
 <div className="flex justify-end gap-2">
 <button onClick={() => setShowConfirm(false)}
 className="px-4 py-2 rounded-lg text-sm font-medium border border-line hover:bg-surface-hover transition-colors text-content">
 {t('common.cancel')}
 </button>
 <button onClick={() => { setMode('disabled'); setShowConfirm(false); }}
 className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors">
 {t('sms.confirmDisableBtn')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// SMS LOG — Versand-Protokoll
// ════════════════════════════════════════════════════════════════
const ZEITRAUM_OPTS = [
 { key: 'heute', labelKey: 'sms.log.today' },
 { key: '7tage', labelKey: 'sms.log.7days' },
 { key: '30tage', labelKey: 'sms.log.30days' },
 { key: 'monat', labelKey: 'sms.log.month' },
];

const STATUS_COLORS = {
 sent: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
 sent_test: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
 failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
 blocked: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
 blocked_test: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

function SmsDetailModal({ row, t, onClose, onResent }) {
 const [resending, setResending] = useState(null);
 const [tgCheck, setTgCheck] = useState(null);
 const [tgLoading, setTgLoading] = useState(true);

 useEffect(() => {
 if (!row) return;
 setTgLoading(true);
 fetch(`${API}/api/einstellungen/sms-log/${row.id}/telegram-check`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { setTgCheck(d); setTgLoading(false); })
 .catch(() => setTgLoading(false));
 }, [row]);

 if (!row) return null;

 const fmtFull = (d) => {
 if (!d) return '—';
 const dt = new Date(d);
 return dt.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
 ' ' + dt.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
 };

 const handleResendSms = async () => {
 setResending('sms');
 try {
 const res = await fetch(`${API}/api/einstellungen/sms-log/${row.id}/resend-sms`, {
 method: 'POST', credentials: 'include',
 });
 const data = await res.json();
 if (data.ok) { onResent?.('sms'); } else { alert(data.error || 'Fehler'); }
 } catch { alert('Netzwerkfehler'); }
 setResending(null);
 };

 const handleResendTelegram = async () => {
 setResending('telegram');
 try {
 const res = await fetch(`${API}/api/einstellungen/sms-log/${row.id}/resend-telegram`, {
 method: 'POST', credentials: 'include',
 });
 const data = await res.json();
 if (data.ok) { onResent?.('telegram'); } else { alert(data.error || 'Fehler'); }
 } catch { alert('Netzwerkfehler'); }
 setResending(null);
 };

 const fields = [
 { label: t('sms.log.colDate', 'Datum'), value: fmtFull(row.created_at) },
 { label: t('sms.log.colRecipient', 'Empfänger'), value: row.empfaenger || '—', mono: true },
 { label: t('sms.log.colCategory', 'Modul'), value: row.kategorie || '—' },
 { label: t('sms.log.colUser', 'User'), value: row.user_name || '—' },
 { label: 'Twilio SID', value: row.provider_id || '—', mono: true },
 ];

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
 <div className="bg-surface-elevated rounded-xl border border-line w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-line">
 <h3 className="text-sm font-semibold text-content">{t('sms.log.detailTitle', 'SMS-Detail')}</h3>
 <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-hover"><X className="w-4 h-4" /></button>
 </div>

 <div className="px-5 py-4 space-y-3">
 {/* Status badge */}
 <div className="flex items-center gap-2">
 <span className="text-xs text-[var(--text-tertiary)]">Status</span>
 <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[row.status] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary,#1A1A2E)]'}`}>
 {row.status}
 </span>
 </div>

 {/* Fields */}
 {fields.map(f => (
 <div key={f.label} className="flex items-start gap-3">
 <span className="text-xs text-[var(--text-tertiary)] w-24 flex-shrink-0 pt-0.5">{f.label}</span>
 <span className={`text-sm text-content ${f.mono ? 'font-mono' : ''}`}>{f.value}</span>
 </div>
 ))}

 {/* Full message */}
 <div>
 <span className="text-xs text-[var(--text-tertiary)]">{t('sms.log.colText', 'Nachricht')}</span>
 <div className="mt-1 p-3 rounded-lg bg-[var(--bg-inset,#F8F9FC)] text-sm text-content whitespace-pre-wrap break-words border border-line">
 {row.text || '—'}
 </div>
 </div>

 {/* Error details */}
 {row.fehler && (
 <div>
 <span className="text-xs text-red-500 font-medium">{t('sms.log.errorDetail', 'Fehler')}</span>
 <div className="mt-1 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap break-words border border-red-200 dark:border-red-800">
 {row.fehler}
 </div>
 </div>
 )}

 {/* MA match info */}
 {!tgLoading && tgCheck?.ma_name && (
 <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
 <Users className="w-3.5 h-3.5" />
 <span>{t('sms.log.matchedMa', 'Zugeordnet zu:')} {tgCheck.ma_name}</span>
 </div>
 )}
 </div>

 {/* Resend buttons */}
 <div className="flex items-center gap-2 px-5 py-3 border-t border-line">
 <button
 onClick={handleResendSms}
 disabled={resending === 'sms'}
 className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-line bg-surface-elevated text-content hover:bg-surface-hover disabled:opacity-50"
 >
 {resending === 'sms' ? <div className="animate-spin h-3.5 w-3.5 border-2 border-[#338edc] border-t-transparent rounded-full" /> : <Phone className="w-3.5 h-3.5" />}
 {t('sms.log.resendSms', 'SMS erneut senden')}
 </button>
 <button
 onClick={handleResendTelegram}
 disabled={resending === 'telegram' || tgLoading || !tgCheck?.has_telegram}
 title={!tgCheck?.has_telegram ? t('sms.log.noTelegram', 'Kein Telegram-Chat für diesen MA konfiguriert') : ''}
 className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-line bg-surface-elevated text-content hover:bg-surface-hover disabled:opacity-50"
 >
 {resending === 'telegram' ? <div className="animate-spin h-3.5 w-3.5 border-2 border-[#338edc] border-t-transparent rounded-full" /> : <Send className="w-3.5 h-3.5" />}
 {t('sms.log.resendTelegram', 'Per Telegram senden')}
 </button>
 </div>
 </div>
 </div>
 );
}

function SmsLogCard({ t, showToast }) {
 const [logs, setLogs] = useState([]);
 const [stats, setStats] = useState({});
 const [kategorien, setKategorien] = useState([]);
 const [loading, setLoading] = useState(true);
 const [zeitraum, setZeitraum] = useState('30tage');
 const [filterKat, setFilterKat] = useState('');
 const [filterStatus, setFilterStatus] = useState('');
 const [selectedRow, setSelectedRow] = useState(null);

 const loadData = useCallback(async () => {
 setLoading(true);
 const params = new URLSearchParams({ zeitraum });
 if (filterKat) params.set('kategorie', filterKat);
 if (filterStatus) params.set('status', filterStatus);
 try {
 const [logRes, statsRes, katRes] = await Promise.all([
 fetch(`${API}/api/einstellungen/sms-log?${params}`, { credentials: 'include' }),
 fetch(`${API}/api/einstellungen/sms-log/stats?${params}`, { credentials: 'include' }),
 fetch(`${API}/api/einstellungen/sms-log/kategorien`, { credentials: 'include' }),
 ]);
 if (logRes.ok) setLogs(await logRes.json());
 if (statsRes.ok) setStats(await statsRes.json());
 if (katRes.ok) setKategorien(await katRes.json());
 } catch {}
 setLoading(false);
 }, [zeitraum, filterKat, filterStatus]);

 useEffect(() => { loadData(); }, [loadData]);

 const fmtDate = (d) => {
 if (!d) return '—';
 const dt = new Date(d);
 return dt.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
 ' ' + dt.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
 };

 const handleResent = (via) => {
 showToast?.(via === 'telegram'
 ? t('sms.log.resentTelegram', 'Per Telegram gesendet')
 : t('sms.log.resentSms', 'SMS erneut gesendet'));
 setSelectedRow(null);
 loadData();
 };

 return (
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center gap-2">
 <FileText className="w-4 h-4" />
 <h2 className="text-sm font-semibold text-content">{t('sms.log.title', 'SMS-Protokoll')}</h2>
 <span className="text-xs text-[var(--text-tertiary)] ml-auto">{logs.length} {t('sms.log.entries', 'Einträge')}</span>
 </div>

 {/* KPI Bar */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 border-b border-line">
 <div>
 <div className="text-xs text-[var(--text-tertiary)]">{t('sms.log.kpiToday', 'Heute')}</div>
 <div className="text-lg font-bold text-[var(--text-primary)]">{stats.heute ?? 0}</div>
 </div>
 <div>
 <div className="text-xs text-[var(--text-tertiary)]">{t('sms.log.kpiMonth', 'Dieser Monat')}</div>
 <div className="text-lg font-bold text-[var(--text-primary)]">{stats.monat ?? 0}</div>
 </div>
 <div>
 <div className="text-xs text-[var(--text-tertiary)]">{t('sms.log.kpiCost', 'Kosten (Monat)')}</div>
 <div className="text-lg font-bold text-[var(--text-primary)]">{stats.kosten_monat ? `€${stats.kosten_monat.toFixed(2)}` : '—'}</div>
 </div>
 <div>
 <div className="text-xs text-[var(--text-tertiary)]">{t('sms.log.kpiErrorRate', 'Fehlerquote')}</div>
 <div className={`text-lg font-bold ${stats.fehler_pct > 10 ? 'text-red-500' : 'text-[var(--text-primary)]'}`}>{stats.fehler_pct ?? 0}%</div>
 </div>
 </div>

 {/* Filters */}
 <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-line">
 <div className="inline-flex gap-0.5 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
 {ZEITRAUM_OPTS.map(z => (
 <button
 key={z.key}
 onClick={() => setZeitraum(z.key)}
 className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
 zeitraum === z.key
 ? 'bg-white dark:bg-surface-elevated text-content font-semibold shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 {t(z.labelKey)}
 </button>
 ))}
 </div>
 <select
 value={filterKat}
 onChange={e => setFilterKat(e.target.value)}
 className="px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-elevated text-content"
 >
 <option value="">{t('sms.log.allCategories', 'Alle Module')}</option>
 {kategorien.map(k => <option key={k} value={k}>{k}</option>)}
 </select>
 <select
 value={filterStatus}
 onChange={e => setFilterStatus(e.target.value)}
 className="px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-elevated text-content"
 >
 <option value="">{t('sms.log.allStatuses', 'Alle Status')}</option>
 <option value="sent">{t('sms.log.statusSent', 'Gesendet')}</option>
 <option value="sent_test">{t('sms.log.statusTest', 'Test')}</option>
 <option value="failed">{t('sms.log.statusFailed', 'Fehlgeschlagen')}</option>
 <option value="blocked">{t('sms.log.statusBlocked', 'Blockiert')}</option>
 </select>
 </div>

 {/* Table */}
 {loading ? (
 <div className="flex items-center justify-center h-32">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 ) : logs.length === 0 ? (
 <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('sms.log.empty', 'Keine SMS im gewählten Zeitraum')}</div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="border-b border-line text-left text-[var(--text-tertiary)]">
 <th className="px-5 py-2 font-medium">{t('sms.log.colDate', 'Datum')}</th>
 <th className="px-3 py-2 font-medium">{t('sms.log.colRecipient', 'Empfänger')}</th>
 <th className="px-3 py-2 font-medium">{t('sms.log.colCategory', 'Modul')}</th>
 <th className="px-3 py-2 font-medium">{t('sms.log.colStatus', 'Status')}</th>
 <th className="px-3 py-2 font-medium">{t('sms.log.colText', 'Nachricht')}</th>
 <th className="px-3 py-2 font-medium">{t('sms.log.colUser', 'User')}</th>
 </tr>
 </thead>
 <tbody>
 {logs.map(row => (
 <tr
 key={row.id}
 onClick={() => setSelectedRow(row)}
 className={`border-b border-line hover:bg-surface-hover cursor-pointer ${row.status === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
 >
 <td className="px-5 py-2.5 whitespace-nowrap text-content">{fmtDate(row.created_at)}</td>
 <td className="px-3 py-2.5 font-mono text-content">{row.empfaenger || '—'}</td>
 <td className="px-3 py-2.5">
 <span className="px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">{row.kategorie || '—'}</span>
 </td>
 <td className="px-3 py-2.5">
 <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[row.status] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary,#1A1A2E)]'}`}>
 {row.status}
 </span>
 </td>
 <td className="px-3 py-2.5 max-w-[280px] truncate text-[var(--text-secondary)]" title={row.text}>{row.text || '—'}</td>
 <td className="px-3 py-2.5 text-[var(--text-secondary)]">{row.user_name || '—'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 {selectedRow && (
 <SmsDetailModal row={selectedRow} t={t} onClose={() => setSelectedRow(null)} onResent={handleResent} />
 )}
 </div>
 );
}

function SmsTab({ t, showToast }) {
 return (
 <div className="space-y-6">
 <SmsConfigCard t={t} showToast={showToast} />
 <SmsLogCard t={t} showToast={showToast} />
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// HOME-DESKTOP KACHELN ADMIN
// ════════════════════════════════════════════════════════════════

const FARBE_OPTIONS = ['blue', 'purple', 'indigo', 'green', 'emerald', 'cyan', 'amber', 'teal', 'orange', 'pink', 'sky', 'slate', 'gray', 'red'];
const FARBE_HEX = { blue: '#3B82F6', purple: '#A855F7', indigo: '#6366F1', green: '#22C55E', emerald: '#10B981', cyan: '#06B6D4', amber: '#F59E0B', teal: '#14B8A6', orange: '#F97316', pink: '#EC4899', sky: '#0EA5E9', slate: '#64748B', gray: '#6B7280', red: '#EF4444' };
const FARBE_BG = { blue: 'bg-blue-100', purple: 'bg-purple-100', indigo: 'bg-indigo-100', green: 'bg-green-100', emerald: 'bg-emerald-100', cyan: 'bg-cyan-100', amber: 'bg-amber-100', teal: 'bg-teal-100', orange: 'bg-orange-100', pink: 'bg-pink-100', sky: 'bg-sky-100', slate: 'bg-slate-100', gray: 'bg-[var(--bg-inset,#F8F9FC)]', red: 'bg-red-100' };

function FarbeDot({ farbe, size = 10 }) {
 return <span className="inline-block rounded-full flex-shrink-0" style={{ width: size, height: size, backgroundColor: FARBE_HEX[farbe] || FARBE_HEX.blue }} />;
}

function FarbeSelect({ value, onChange, className }) {
 return (
 <select value={value} onChange={onChange} className={className}>
 {FARBE_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
 </select>
 );
}

// ════════════════════════════════════════════════════════════════
// ABTEILUNGEN + KV-EINSTUFUNGEN TAB
// ════════════════════════════════════════════════════════════════

function AbteilungenKvTab({ t, showToast }) {
 const [abteilungen, setAbteilungen] = useState([]);
 const [kvList, setKvList] = useState([]);
 const [mitarbeiter, setMitarbeiter] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editAbt, setEditAbt] = useState(null);
 const [editKv, setEditKv] = useState(null);
 const [addAbt, setAddAbt] = useState(false);
 const [addKv, setAddKv] = useState(false);
 const [form, setForm] = useState({});
 const [saving, setSaving] = useState(false);
 const [subTab, setSubTab] = useState('abteilungen'); // abteilungen | kv

 const load = useCallback(() => {
 setLoading(true);
 Promise.all([
 fetch(`${API}/api/abteilungen`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/kv-einstufungen/alle`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/mitarbeiter?status=alle`, { credentials: 'include' }).then(r => r.json()),
 ]).then(([abt, kv, ma]) => {
 setAbteilungen(Array.isArray(abt) ? abt : []);
 setKvList(Array.isArray(kv) ? kv : []);
 setMitarbeiter(Array.isArray(ma) ? ma.filter(m => m.aktiv && m.typ === 'person') : []);
 }).finally(() => setLoading(false));
 }, []);

 useEffect(() => { load(); }, [load]);

 const saveAbt = async (id, data) => {
 setSaving(true);
 const method = id ? 'PUT' : 'POST';
 const url = id ? `${API}/api/abteilungen/${id}` : `${API}/api/abteilungen`;
 const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
 if (r.ok) { showToast(t('common.saved')); setEditAbt(null); setAddAbt(false); load(); }
 else { const d = await r.json(); showToast(d.error || 'Error', 'error'); }
 setSaving(false);
 };

 const deleteAbt = async (id) => {
 if (!window.confirm(t('settings.departments.confirmDelete'))) return;
 const r = await fetch(`${API}/api/abteilungen/${id}`, { method: 'DELETE', credentials: 'include' });
 if (r.ok) { showToast(t('common.deleted')); load(); }
 else { const d = await r.json(); showToast(d.error || 'Error', 'error'); }
 };

 const saveKv = async (id, data) => {
 setSaving(true);
 const method = id ? 'PUT' : 'POST';
 const url = id ? `${API}/api/kv-einstufungen/${id}` : `${API}/api/kv-einstufungen`;
 const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
 if (r.ok) { showToast(t('common.saved')); setEditKv(null); setAddKv(false); load(); }
 else { const d = await r.json(); showToast(d.error || 'Error', 'error'); }
 setSaving(false);
 };

 const deleteKv = async (id) => {
 if (!window.confirm(t('settings.departments.confirmDeleteKv'))) return;
 const r = await fetch(`${API}/api/kv-einstufungen/${id}`, { method: 'DELETE', credentials: 'include' });
 if (r.ok) { showToast(t('common.deleted')); load(); }
 else { const d = await r.json(); showToast(d.error || 'Error', 'error'); }
 };

 const buero = abteilungen.filter(a => a.bereich === 'buero');
 const lager = abteilungen.filter(a => a.bereich === 'lager');

 const AbtForm = ({ initial, onSave, onCancel }) => {
 const [f, setF] = useState(initial || { name: '', bereich: 'lager', vorgesetzter_id: '', sort_order: 0 });
 return (
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 space-y-3 bg-[var(--bg-card,#fff)]">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.name')}</label>
 <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.area')}</label>
 <select value={f.bereich} onChange={e => setF({ ...f, bereich: e.target.value })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 <option value="buero">{t('settings.departments.office')}</option>
 <option value="lager">{t('settings.departments.warehouse')}</option>
 </select>
 </div>
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.supervisor')}</label>
 <select value={f.vorgesetzter_id || ''} onChange={e => setF({ ...f, vorgesetzter_id: e.target.value ? parseInt(e.target.value) : null })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 <option value="">–</option>
 {mitarbeiter.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
 </select>
 </div>
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.sortOrder')}</label>
 <input type="number" value={f.sort_order} onChange={e => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 </div>
 <div className="flex justify-end gap-2">
 <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)]">{t('common.cancel')}</button>
 <button onClick={() => onSave(f)} disabled={saving || !f.name}
 className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-semibold disabled:opacity-50">{t('common.save')}</button>
 </div>
 </div>
 );
 };

 const AbtSection = ({ title, items, bereich }) => (
 <div className="mb-6">
 <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-3">{title}</div>
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[var(--bg-inset,#F8F9FC)]">
 <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.name')}</th>
 <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.supervisor')}</th>
 <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.employees')}</th>
 <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]"></th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
 {items.map(a => (
 editAbt === a.id ? (
 <tr key={a.id}><td colSpan={4} className="p-3">
 <AbtForm initial={{ name: a.name, bereich: a.bereich, vorgesetzter_id: a.vorgesetzter_id, sort_order: a.sort_order }}
 onSave={data => saveAbt(a.id, data)} onCancel={() => setEditAbt(null)} />
 </td></tr>
 ) : (
 <tr key={a.id} className="hover:bg-[var(--bg-inset,#F8F9FC)]">
 <td className="px-4 py-3 text-[var(--text-primary)] font-medium">{a.name}</td>
 <td className="px-4 py-3 text-[var(--text-secondary)]">{a.vorgesetzter_name || '–'}</td>
 <td className="px-4 py-3 text-center">
 <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">{a.mitarbeiter_count}</span>
 </td>
 <td className="px-4 py-3 text-right">
 <div className="flex items-center justify-end gap-1">
 <button onClick={() => setEditAbt(a.id)} className="p-1.5 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><Pencil size={14} /></button>
 <button onClick={() => deleteAbt(a.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-500"><Trash2 size={14} /></button>
 </div>
 </td>
 </tr>
 )
 ))}
 {items.length === 0 && (
 <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[var(--text-tertiary)]">{t('settings.departments.empty')}</td></tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 );

 if (loading) return <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>;

 return (
 <div className="space-y-6">
 {/* Sub-tab toggle */}
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1">
 {[
 { key: 'abteilungen', label: t('settings.departments.title') },
 { key: 'kv', label: t('settings.departments.kvTitle') },
 ].map(tab => (
 <button key={tab.key} onClick={() => setSubTab(tab.key)}
 className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${subTab === tab.key ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
 {tab.label}
 </button>
 ))}
 </div>

 {subTab === 'abteilungen' ? (
 <>
 <div className="flex items-center justify-between">
 <p className="text-sm text-[var(--text-secondary)]">{t('settings.departments.subtitle')}</p>
 <button onClick={() => { setAddAbt(true); setForm({ name: '', bereich: 'lager', vorgesetzter_id: '', sort_order: 0 }); }}
 className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:opacity-90">
 <Plus size={16} /> {t('settings.departments.add')}
 </button>
 </div>
 {addAbt && <AbtForm initial={{ name: '', bereich: 'lager', vorgesetzter_id: '', sort_order: 0 }}
 onSave={data => saveAbt(null, data)} onCancel={() => setAddAbt(false)} />}
 <AbtSection title={t('settings.departments.office')} items={buero} bereich="buero" />
 <AbtSection title={t('settings.departments.warehouse')} items={lager} bereich="lager" />
 </>
 ) : (
 <>
 <div className="flex items-center justify-between">
 <p className="text-sm text-[var(--text-secondary)]">{t('settings.departments.kvSubtitle')}</p>
 <button onClick={() => { setAddKv(true); setForm({ code: '', name: '', sort_order: 0 }); }}
 className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:opacity-90">
 <Plus size={16} /> {t('settings.departments.kvAdd')}
 </button>
 </div>
 {addKv && (
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 space-y-3 bg-[var(--bg-card,#fff)]">
 <div className="grid grid-cols-3 gap-3">
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.kvCode')}</label>
 <input value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" placeholder="B005" />
 </div>
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.kvName')}</label>
 <input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('settings.departments.sortOrder')}</label>
 <input type="number" value={form.sort_order || 0} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
 className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 </div>
 <div className="flex justify-end gap-2">
 <button onClick={() => setAddKv(false)} className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)]">{t('common.cancel')}</button>
 <button onClick={() => saveKv(null, form)} disabled={saving || !form.code || !form.name}
 className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-semibold disabled:opacity-50">{t('common.save')}</button>
 </div>
 </div>
 )}
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[var(--bg-inset,#F8F9FC)]">
 <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.kvCode')}</th>
 <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.kvName')}</th>
 <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('settings.departments.employees')}</th>
 <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]"></th>
 </tr>
 </thead>
 <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
 {kvList.map(kv => (
 <tr key={kv.id} className="hover:bg-[var(--bg-inset,#F8F9FC)]">
 <td className="px-4 py-3 font-mono text-[var(--text-primary)] font-medium">{kv.code}</td>
 <td className="px-4 py-3 text-[var(--text-primary)]">{kv.name}</td>
 <td className="px-4 py-3 text-center">
 <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">{kv.mitarbeiter_count}</span>
 </td>
 <td className="px-4 py-3 text-right">
 <div className="flex items-center justify-end gap-1">
 <button onClick={() => deleteKv(kv.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-500"><Trash2 size={14} /></button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </>
 )}
 </div>
 );
}

function HomeDesktopTab({ t, showToast }) {
 const [kacheln, setKacheln] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editId, setEditId] = useState(null);
 const [editForm, setEditForm] = useState({});
 const [addMode, setAddMode] = useState(false);
 const [addForm, setAddForm] = useState({ key: '', name_en: '', name_de: '', icon: 'Package', farbe: 'blue', route: '/', badge_key: '', modul: '' });
 const [saving, setSaving] = useState(false);

 const load = useCallback(() => {
 fetch(`${API}/api/admin/home-kacheln`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setKacheln(d.kacheln || []))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { load(); }, [load]);

 const handleToggle = async (kachel) => {
 try {
 await fetch(`${API}/api/admin/home-kacheln/${kachel.id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ aktiv: !kachel.aktiv }),
 });
 load();
 } catch { showToast(t('common.saveError')); }
 };

 const handleMove = async (kachel, direction) => {
 const idx = kacheln.findIndex(k => k.id === kachel.id);
 const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
 if (swapIdx < 0 || swapIdx >= kacheln.length) return;
 const order = kacheln.map((k, i) => {
 if (i === idx) return { id: k.id, position: kacheln[swapIdx].position };
 if (i === swapIdx) return { id: k.id, position: kacheln[idx].position };
 return { id: k.id, position: k.position };
 });
 try {
 await fetch(`${API}/api/admin/home-kacheln/reorder`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ order }),
 });
 load();
 } catch { showToast(t('common.saveError')); }
 };

 const handleEdit = (kachel) => {
 setEditId(kachel.id);
 setEditForm({ name_en: kachel.name_en, name_de: kachel.name_de, icon: kachel.icon, farbe: kachel.farbe, route: kachel.route, badge_key: kachel.badge_key || '', modul: kachel.modul || '' });
 };

 const handleSaveEdit = async () => {
 setSaving(true);
 try {
 await fetch(`${API}/api/admin/home-kacheln/${editId}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(editForm),
 });
 setEditId(null);
 showToast(t('common.saved'));
 load();
 } catch { showToast(t('common.saveError')); }
 setSaving(false);
 };

 const handleAdd = async () => {
 if (!addForm.key || !addForm.name_en || !addForm.name_de || !addForm.route) return;
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/admin/home-kacheln`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(addForm),
 });
 if (!res.ok) { const err = await res.json().catch(() => ({})); showToast(err.error || 'Error'); setSaving(false); return; }
 setAddMode(false);
 setAddForm({ key: '', name_en: '', name_de: '', icon: 'Package', farbe: 'blue', route: '/', badge_key: '', modul: '' });
 showToast(t('common.saved'));
 load();
 } catch { showToast(t('common.saveError')); }
 setSaving(false);
 };

 const handleDelete = async (id) => {
 if (!confirm(t('common.deleteConfirm'))) return;
 try {
 await fetch(`${API}/api/admin/home-kacheln/${id}`, { method: 'DELETE', credentials: 'include' });
 showToast(t('common.deleted'));
 load();
 } catch { showToast(t('common.saveError')); }
 };

 const inputCls = 'px-2 py-1.5 text-xs rounded-lg border border-line bg-surface-elevated text-content';

 if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <div className="space-y-4">
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <div className="flex items-center gap-2">
 <LayoutDashboard className="w-4 h-4 text-content" />
 <h2 className="text-sm font-semibold text-content">{t('settings.homeDesktop.title')}</h2>
 <span className="text-[10px] text-[var(--text-tertiary)]">{kacheln.length} {t('settings.homeDesktop.tiles')}</span>
 </div>
 <button onClick={() => setAddMode(!addMode)}
 className="text-xs font-medium text-[#338edc] hover:text-[#2b7bc4] flex items-center gap-1 transition-colors">
 <Plus className="w-3.5 h-3.5" /> {t('settings.homeDesktop.addTile')}
 </button>
 </div>
 <div className="divide-y divide-line">
 {/* Add form */}
 {addMode && (
 <div className="px-5 py-3 bg-[var(--bg-inset)]">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
 <input value={addForm.key} onChange={e => setAddForm(f => ({ ...f, key: e.target.value }))} placeholder="key *"
 className={inputCls} />
 <input value={addForm.name_en} onChange={e => setAddForm(f => ({ ...f, name_en: e.target.value }))} placeholder="Name EN *"
 className={inputCls} />
 <input value={addForm.name_de} onChange={e => setAddForm(f => ({ ...f, name_de: e.target.value }))} placeholder="Name DE *"
 className={inputCls} />
 <input value={addForm.route} onChange={e => setAddForm(f => ({ ...f, route: e.target.value }))} placeholder="/route *"
 className={inputCls} />
 </div>
 <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
 <input value={addForm.icon} onChange={e => setAddForm(f => ({ ...f, icon: e.target.value }))} placeholder="Icon (Lucide)"
 className={inputCls} />
 <div className="flex items-center gap-1.5">
 <FarbeDot farbe={addForm.farbe} />
 <FarbeSelect value={addForm.farbe} onChange={e => setAddForm(f => ({ ...f, farbe: e.target.value }))} className={inputCls + ' flex-1'} />
 </div>
 <input value={addForm.badge_key} onChange={e => setAddForm(f => ({ ...f, badge_key: e.target.value }))} placeholder="Badge Key"
 className={inputCls} />
 <input value={addForm.modul} onChange={e => setAddForm(f => ({ ...f, modul: e.target.value }))} placeholder="Modul"
 className={inputCls} />
 <div className="flex gap-1">
 <button onClick={handleAdd} disabled={saving}
 className="flex-1 bg-[#338edc] text-white text-xs rounded-lg px-2 py-1.5 hover:bg-[#2b7bc4] disabled:opacity-50">
 <Check className="w-3.5 h-3.5 mx-auto" />
 </button>
 <button onClick={() => setAddMode(false)}
 className="flex-1 border border-line text-[var(--text-secondary)] text-xs rounded-lg px-2 py-1.5 hover:bg-surface-hover">
 <X className="w-3.5 h-3.5 mx-auto" />
 </button>
 </div>
 </div>
 </div>
 )}
 {/* Tile list */}
 {kacheln.map((k, idx) => (
 <div key={k.id} className={`px-5 py-3 flex items-center gap-3 ${!k.aktiv ? 'opacity-50' : ''}`}>
 <div className="flex flex-col gap-0.5">
 <button onClick={() => handleMove(k, 'up')} disabled={idx === 0}
 className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-20 transition-colors">
 <ChevronUp className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => handleMove(k, 'down')} disabled={idx === kacheln.length - 1}
 className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-20 transition-colors">
 <ChevronDown className="w-3.5 h-3.5" />
 </button>
 </div>
 <span className="text-[10px] font-mono text-[var(--text-tertiary)] w-4 text-center flex-shrink-0">{idx + 1}</span>
 <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${FARBE_BG[k.farbe] || 'bg-blue-100'}`}>
 <span className="text-xs font-bold" style={{ color: FARBE_HEX[k.farbe] || FARBE_HEX.blue }}>{k.icon?.charAt(0)}</span>
 </div>
 {editId === k.id ? (
 <div className="flex-1 space-y-1.5">
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
 <input value={editForm.name_en} onChange={e => setEditForm(f => ({ ...f, name_en: e.target.value }))} placeholder="Name EN"
 className={inputCls} />
 <input value={editForm.name_de} onChange={e => setEditForm(f => ({ ...f, name_de: e.target.value }))} placeholder="Name DE"
 className={inputCls} />
 <input value={editForm.icon} onChange={e => setEditForm(f => ({ ...f, icon: e.target.value }))} placeholder="Icon"
 className={inputCls} />
 <div className="flex items-center gap-1.5">
 <FarbeDot farbe={editForm.farbe} />
 <FarbeSelect value={editForm.farbe} onChange={e => setEditForm(f => ({ ...f, farbe: e.target.value }))} className={inputCls + ' flex-1'} />
 </div>
 </div>
 <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
 <input value={editForm.route} onChange={e => setEditForm(f => ({ ...f, route: e.target.value }))} placeholder="/route"
 className={inputCls} />
 <input value={editForm.badge_key} onChange={e => setEditForm(f => ({ ...f, badge_key: e.target.value }))} placeholder="Badge Key"
 className={inputCls} />
 <input value={editForm.modul} onChange={e => setEditForm(f => ({ ...f, modul: e.target.value }))} placeholder="Modul"
 className={inputCls} />
 </div>
 </div>
 ) : (
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <span className="text-xs font-medium text-content">{k.name_de}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">({k.name_en})</span>
 <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-tertiary)] font-mono">{k.key}</span>
 </div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 flex items-center gap-1">
 {k.route} · {k.icon} · <FarbeDot farbe={k.farbe} size={8} /> {k.farbe}
 {k.badge_key && <span> · badge: {k.badge_key}</span>}
 {k.modul && <span> · modul: {k.modul}</span>}
 </div>
 </div>
 )}
 <div className="flex items-center gap-2 flex-shrink-0">
 <ToggleSwitch size="sm" checked={k.aktiv} onChange={() => handleToggle(k)} />
 {editId === k.id ? (
 <div className="flex gap-1">
 <button onClick={handleSaveEdit} disabled={saving}
 className="p-1 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors"><Check className="w-3.5 h-3.5" /></button>
 <button onClick={() => setEditId(null)}
 className="p-1 rounded text-[var(--text-tertiary)] hover:bg-surface-hover transition-colors"><X className="w-3.5 h-3.5" /></button>
 </div>
 ) : (
 <div className="flex gap-1">
 <button onClick={() => handleEdit(k)}
 className="p-1 rounded text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
 <button onClick={() => handleDelete(k.id)}
 className="p-1 rounded text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// MAIN EINSTELLUNGEN COMPONENT
// ════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════
// SYSTEM-AUDIT TAB — Page-Index + 7-Check Audit-Engine
// ════════════════════════════════════════════════════════════════

function SystemAuditTab({ t, showToast }) {
 const [subTab, setSubTab] = useState('audit');
 const [audit, setAudit] = useState(null);
 const [audits, setAudits] = useState([]);
 const [running, setRunning] = useState(false);
 const [pageIndex, setPageIndex] = useState(null);
 const [searchPI, setSearchPI] = useState('');
 const [expandedModules, setExpandedModules] = useState({});
 const [selectedPage, setSelectedPage] = useState(null);
 const [filterStatus, setFilterStatus] = useState('all');

 // Load audit history
 const loadAudits = useCallback(async () => {
 try {
 const res = await fetch(`${API}/api/system/audits`, { credentials: 'include' });
 if (res.ok) setAudits(await res.json());
 } catch { /* ignore */ }
 }, []);

 // Load page index
 const loadPageIndex = useCallback(async () => {
 try {
 const res = await fetch(`${API}/api/system/page-index`, { credentials: 'include' });
 if (res.ok) setPageIndex(await res.json());
 } catch { /* ignore */ }
 }, []);

 useEffect(() => { loadAudits(); loadPageIndex(); }, [loadAudits, loadPageIndex]);

 // Run audit
 const runAudit = async () => {
 setRunning(true);
 try {
 const res = await fetch(`${API}/api/system/audit`, { method: 'POST', credentials: 'include' });
 if (res.ok) {
 const data = await res.json();
 setAudit(data);
 loadAudits();
 showToast(t('systemAudit.auditComplete'));
 }
 } catch { showToast(t('common.error'), 'error'); }
 setRunning(false);
 };

 // Load specific audit
 const loadAudit = async (id) => {
 try {
 const res = await fetch(`${API}/api/system/audits/${id}`, { credentials: 'include' });
 if (res.ok) setAudit(await res.json());
 } catch { /* ignore */ }
 };

 // Download fix-report
 const downloadFixReport = async (id) => {
 try {
 const res = await fetch(`${API}/api/system/audits/${id}/fix-report`, { credentials: 'include' });
 if (res.ok) {
 const blob = await res.blob();
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `fix-report-${id}.md`;
 a.click();
 URL.revokeObjectURL(url);
 }
 } catch { /* ignore */ }
 };

 const CHECK_LABELS = {
 layout_width: t('systemAudit.check.layoutWidth'),
 card_background: t('systemAudit.check.cardBackground'),
 icon_colors: t('systemAudit.check.iconColors'),
 i18n: t('systemAudit.check.i18n'),
 demo_data: t('systemAudit.check.demoData'),
 design_compliance: t('systemAudit.check.designCompliance'),
 card_icons: t('systemAudit.check.cardIcons'),
 };

 const STATUS_STYLES = {
 pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
 fail: 'bg-red-50 text-red-700 border-red-200',
 warn: 'bg-amber-50 text-amber-700 border-amber-200',
 skip: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] border-[var(--border-default,#E5E7EB)]',
 error: 'bg-red-50 text-red-700 border-red-200',
 };

 const STATUS_LABELS = {
 pass: t('systemAudit.status.pass'),
 fail: t('systemAudit.status.fail'),
 warn: t('systemAudit.status.warn'),
 skip: t('systemAudit.status.skip'),
 error: t('systemAudit.status.error'),
 };

 // ─── Audit results sub-tab ─────────────────────────
 const renderAuditTab = () => {
 const results = audit?.results || [];
 const filtered = filterStatus === 'all' ? results : results.filter(r => r.status === filterStatus);

 return (
 <div className="space-y-4">
 {/* KPI Bar */}
 {audit && (
 <div className="grid grid-cols-4 gap-3">
 {[
 { label: t('systemAudit.kpi.total'), value: audit.total_pages, color: '#338edc', Icon: FolderTree },
 { label: t('systemAudit.kpi.passed'), value: audit.passed, color: '#10B981', Icon: Check },
 { label: t('systemAudit.kpi.warnings'), value: audit.warnings, color: '#F59E0B', Icon: AlertTriangle },
 { label: t('systemAudit.kpi.failed'), value: audit.failed, color: '#EF4444', Icon: ShieldAlert },
 ].map(kpi => (
 <div key={kpi.label} className="bg-surface-elevated rounded-xl border border-line p-4">
 <div className="flex items-center gap-2 mb-1">
 <kpi.Icon size={16} className="flex-shrink-0" style={{ color: kpi.color }} />
 <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">{kpi.label}</span>
 </div>
 <div className="text-2xl font-bold text-[var(--text-primary)]">{kpi.value}</div>
 </div>
 ))}
 </div>
 )}

 {/* Actions Row */}
 <div className="flex items-center gap-3 flex-wrap">
 <button
 onClick={runAudit}
 disabled={running}
 className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#338edc] hover:bg-[#2b7bc4] transition-colors disabled:opacity-50 flex items-center gap-2"
 >
 {running ? (
 <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {t('systemAudit.running')}</>
 ) : (
 <><ShieldCheck size={16} /> {t('systemAudit.runAudit')}</>
 )}
 </button>

 {audit?.id && (
 <button
 onClick={() => downloadFixReport(audit.id)}
 className="px-4 py-2 rounded-lg text-sm font-medium text-[var(--text-secondary)] bg-surface-elevated border border-line hover:border-[#338edc]/40 transition-colors flex items-center gap-2"
 >
 <Download size={16} /> {t('systemAudit.downloadFixReport')}
 </button>
 )}

 {/* Filter */}
 {audit && (
 <div className="ml-auto flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
 {['all', 'fail', 'warn', 'pass'].map(f => (
 <button
 key={f}
 onClick={() => setFilterStatus(f)}
 className={`px-3 py-1 rounded-[8px] text-xs font-medium transition-all ${
 filterStatus === f
 ? 'bg-white dark:bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 >
 {f === 'all' ? t('common.all') : STATUS_LABELS[f]} {f !== 'all' && `(${results.filter(r => r.status === f).length})`}
 </button>
 ))}
 </div>
 )}
 </div>

 {/* History */}
 {audits.length > 0 && !audit && (
 <div className="bg-surface-elevated rounded-xl border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h3 className="text-sm font-semibold text-content">{t('systemAudit.history')}</h3>
 </div>
 <div className="divide-y divide-line">
 {audits.map(a => (
 <button
 key={a.id}
 onClick={() => loadAudit(a.id)}
 className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-[var(--bg-inset)] transition-colors"
 >
 <span className="text-xs text-[var(--text-secondary)]">#{a.id}</span>
 <span className="text-sm text-content flex-1">
 {new Date(a.created_at).toLocaleString('de-AT')}
 </span>
 <span className="text-xs text-emerald-600">{a.pages_passed} {t('systemAudit.status.pass')}</span>
 <span className="text-xs text-amber-600">{a.pages_warned} {t('systemAudit.status.warn')}</span>
 <span className="text-xs text-red-600">{a.pages_failed} {t('systemAudit.status.fail')}</span>
 <ChevronRight size={14} className="text-[var(--text-muted)]" />
 </button>
 ))}
 </div>
 </div>
 )}

 {/* Results Table */}
 {audit && filtered.length > 0 && (
 <div className="bg-surface-elevated rounded-xl border border-line overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-[var(--bg-inset)] border-b border-line">
 <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-secondary)] uppercase">{t('systemAudit.col.page')}</th>
 <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--text-secondary)] uppercase">{t('systemAudit.col.module')}</th>
 <th className="text-center px-2 py-2.5 text-xs font-medium text-[var(--text-secondary)] uppercase">{t('systemAudit.col.status')}</th>
 {Object.keys(CHECK_LABELS).map(ck => (
 <th key={ck} className="text-center px-1.5 py-2.5 text-[10px] font-medium text-[var(--text-secondary)] uppercase" title={CHECK_LABELS[ck]}>
 {CHECK_LABELS[ck].substring(0, 6)}
 </th>
 ))}
 </tr>
 </thead>
 <tbody className="divide-y divide-line">
 {filtered.map(r => (
 <tr
 key={r.path}
 className={`hover:bg-[var(--bg-inset)] cursor-pointer transition-colors ${selectedPage?.path === r.path ? 'bg-[var(--bg-inset)]' : ''}`}
 onClick={() => setSelectedPage(selectedPage?.path === r.path ? null : r)}
 >
 <td className="px-4 py-2.5 font-medium text-content">{r.name}</td>
 <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.modul}</td>
 <td className="text-center px-2 py-2.5">
 <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_STYLES[r.status]}`}>
 {STATUS_LABELS[r.status]}
 </span>
 </td>
 {Object.keys(CHECK_LABELS).map(ck => {
 const s = r.checks?.[ck]?.status || 'skip';
 return (
 <td key={ck} className="text-center px-1.5 py-2.5">
 <span className={`inline-block w-5 h-5 rounded-full text-[10px] leading-5 font-semibold border ${STATUS_STYLES[s]}`}>
 {s === 'pass' ? '✓' : s === 'fail' ? '✗' : s === 'warn' ? '!' : '–'}
 </span>
 </td>
 );
 })}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}

 {/* Detail Panel */}
 {selectedPage && (
 <div className="bg-surface-elevated rounded-xl border border-line p-5">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-sm font-semibold text-content">{selectedPage.name} — {selectedPage.jsxFile}</h3>
 <button onClick={() => setSelectedPage(null)} className="text-[var(--text-muted)] hover:text-content"><X size={16} /></button>
 </div>
 <div className="space-y-3">
 {Object.entries(selectedPage.checks || {}).map(([ck, check]) => {
 if (check.status === 'pass' || check.status === 'skip') return null;
 return (
 <div key={ck} className="border border-line rounded-lg p-3">
 <div className="flex items-center gap-2 mb-2">
 <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_STYLES[check.status]}`}>
 {STATUS_LABELS[check.status]}
 </span>
 <span className="text-sm font-medium text-content">{CHECK_LABELS[ck]}</span>
 {check.detail && <span className="text-xs text-[var(--text-secondary)]">— {check.detail}</span>}
 </div>
 {check.issues?.length > 0 && (
 <div className="space-y-1 mt-2">
 {check.issues.slice(0, 10).map((issue, idx) => (
 <div key={idx} className="text-xs text-[var(--text-secondary)] font-mono bg-[var(--bg-inset)] rounded px-2 py-1">
 {issue.line && <span className="text-[var(--text-muted)]">L{issue.line}: </span>}
 {issue.type === 'missing_en' ? `EN: ${issue.keys?.slice(0, 3).join(', ')}${issue.total > 3 ? '...' : ''}` :
 issue.type === 'missing_de' ? `DE: ${issue.keys?.slice(0, 3).join(', ')}${issue.total > 3 ? '...' : ''}` :
 issue.suggestedIcon ? `${t('systemAudit.suggest')}: <${issue.suggestedIcon.icon} className="text-[${issue.suggestedIcon.color}]" />` :
 (issue.text || '').substring(0, 100)}
 </div>
 ))}
 {check.issues.length > 10 && (
 <div className="text-[10px] text-[var(--text-muted)]">+{check.issues.length - 10} {t('systemAudit.more')}</div>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Empty state */}
 {!audit && audits.length === 0 && (
 <div className="bg-surface-elevated rounded-xl border border-line p-12 text-center">
 <ShieldCheck size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
 <p className="text-sm text-[var(--text-secondary)]">{t('systemAudit.noAudits')}</p>
 <p className="text-xs text-[var(--text-muted)] mt-1">{t('systemAudit.noAuditsHint')}</p>
 </div>
 )}
 </div>
 );
 };

 // ─── Page Index sub-tab ────────────────────────────
 const renderPageIndexTab = () => {
 if (!pageIndex) return <div className="py-8 text-center text-sm text-[var(--text-muted)]">{t('common.loading')}</div>;

 const allPages = pageIndex.pages || [];
 const filteredPages = searchPI
 ? allPages.filter(p => p.name.toLowerCase().includes(searchPI.toLowerCase()) || p.path.toLowerCase().includes(searchPI.toLowerCase()) || p.modul.toLowerCase().includes(searchPI.toLowerCase()))
 : allPages;

 // Group by module
 const byModule = {};
 for (const p of filteredPages) {
 if (!byModule[p.modul]) byModule[p.modul] = [];
 byModule[p.modul].push(p);
 }

 const toggleModule = (mod) => setExpandedModules(prev => ({ ...prev, [mod]: !prev[mod] }));

 return (
 <div className="space-y-4">
 {/* Summary */}
 <div className="grid grid-cols-3 gap-3">
 <div className="bg-surface-elevated rounded-xl border border-line p-4">
 <div className="flex items-center gap-2 mb-1">
 <FolderTree size={16} className="text-[#338edc]" />
 <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">{t('systemAudit.pi.totalPages')}</span>
 </div>
 <div className="text-2xl font-bold text-[var(--text-primary)]">{pageIndex.total}</div>
 </div>
 <div className="bg-surface-elevated rounded-xl border border-line p-4">
 <div className="flex items-center gap-2 mb-1">
 <BarChart3 size={16} className="text-[#6366F1]" />
 <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">{t('systemAudit.pi.modules')}</span>
 </div>
 <div className="text-2xl font-bold text-[var(--text-primary)]">{pageIndex.modules?.length || 0}</div>
 </div>
 <div className="bg-surface-elevated rounded-xl border border-line p-4">
 <div className="flex items-center gap-2 mb-1">
 <ExternalLink size={16} className="text-[#22D3EE]" />
 <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">{t('systemAudit.pi.portalPages')}</span>
 </div>
 <div className="text-2xl font-bold text-[var(--text-primary)]">{allPages.filter(p => p.isPortal).length}</div>
 </div>
 </div>

 {/* Search */}
 <div className="relative">
 <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
 <input
 type="text"
 value={searchPI}
 onChange={e => setSearchPI(e.target.value)}
 placeholder={t('systemAudit.pi.search')}
 className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
 />
 </div>

 {/* Module tree */}
 <div className="bg-surface-elevated rounded-xl border border-line overflow-hidden divide-y divide-line">
 {Object.entries(byModule).sort((a, b) => a[0].localeCompare(b[0])).map(([mod, pages]) => {
 const expanded = expandedModules[mod] ?? (searchPI.length > 0);
 return (
 <div key={mod}>
 <button
 onClick={() => toggleModule(mod)}
 className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-inset)] transition-colors text-left"
 >
 <ChevronRight size={14} className={`text-[var(--text-muted)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
 <span className="text-sm font-medium text-content flex-1">{mod}</span>
 <span className="text-xs text-[var(--text-secondary)] bg-[var(--bg-inset)] px-2 py-0.5 rounded-full">{pages.length}</span>
 </button>
 {expanded && (
 <div className="border-t border-line bg-[var(--bg-inset)]">
 {pages.map(p => (
 <div key={p.path} className="flex items-center gap-4 px-4 py-2 pl-10 text-xs border-b border-line last:border-0">
 <span className="font-mono text-[var(--text-secondary)] w-48 truncate" title={p.path}>{p.path}</span>
 <span className="text-content flex-1 truncate">{p.name}</span>
 <span className="text-[var(--text-muted)] truncate w-40">{p.jsxFile}</span>
 {p.tabs?.length > 0 && <span className="text-[10px] text-[#338edc] bg-[#338edc]/10 px-1.5 py-0.5 rounded">{p.tabs.length} Tabs</span>}
 {p.isPortal && <span className="text-[10px] text-[#6366F1] bg-[#6366F1]/10 px-1.5 py-0.5 rounded">Portal</span>}
 {p.isDetail && <span className="text-[10px] text-[#22D3EE] bg-[#22D3EE]/10 px-1.5 py-0.5 rounded">Detail</span>}
 </div>
 ))}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 );
 };

 const SUB_TABS = [
 { key: 'audit', label: t('systemAudit.subTab.audit') },
 { key: 'pageIndex', label: t('systemAudit.subTab.pageIndex') },
 ];

 return (
 <div className="space-y-4">
 {/* Sub-Tab Bar */}
 <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line w-fit">
 {SUB_TABS.map(st => (
 <button
 key={st.key}
 onClick={() => setSubTab(st.key)}
 className={`px-4 py-1.5 rounded-[8px] text-sm font-medium transition-all ${
 subTab === st.key
 ? 'bg-white dark:bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 >
 {st.label}
 </button>
 ))}
 </div>

 {subTab === 'audit' ? renderAuditTab() : renderPageIndexTab()}
 </div>
 );
}

// ════════════════════════════════════════════════════════════════
// PACK-TIMER — Stationen CRUD + Schwellwerte + Config
// ════════════════════════════════════════════════════════════════

function PackTimerTab({ t, showToast }) {
 const [config, setConfig] = useState({});
 const [stationen, setStationen] = useState([]);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(null);
 const [editStation, setEditStation] = useState(null);
 const [editSaving, setEditSaving] = useState(false);

 const API = import.meta.env.VITE_API_URL || '';

 useEffect(() => {
 Promise.all([
 fetch(`${API}/api/einstellungen`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/pack-timer/stationen`, { credentials: 'include' }).then(r => r.json()),
 ]).then(([settingsData, stData]) => {
 const all = Object.values(settingsData.kategorien || {}).flat();
 const cfg = {};
 for (const s of all) {
 if (s.key?.startsWith('pack_timer_')) {
 cfg[s.key] = typeof s.value === 'string' ? s.value.replace(/"/g, '') : String(s.value ?? '');
 }
 }
 setConfig(cfg);
 setStationen(stData.stationen || []);
 }).catch(() => {}).finally(() => setLoading(false));
 }, []);

 const saveConfig = async (key, value) => {
 setSaving(key);
 try {
 await fetch(`${API}/api/einstellungen/${key}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ value }),
 });
 setConfig(prev => ({ ...prev, [key]: String(value) }));
 showToast(t('common.saved', 'Gespeichert'));
 } catch { showToast(t('common.error', 'Fehler'), 'error'); }
 setSaving(null);
 };

 const saveStation = async () => {
 if (!editStation) return;
 setEditSaving(true);
 try {
 const isNew = !editStation.id;
 const url = isNew ? `${API}/api/pack-timer/stationen` : `${API}/api/pack-timer/stationen/${editStation.id}`;
 const res = await fetch(url, {
 method: isNew ? 'POST' : 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ name: editStation.name, station_nr: editStation.station_nr, display_modus: editStation.display_modus, aktiv: editStation.aktiv, admin_pin: editStation.admin_pin }),
 });
 if (res.ok) {
 showToast(t('common.saved', 'Gespeichert'));
 // Reload
 const stRes = await fetch(`${API}/api/pack-timer/stationen`, { credentials: 'include' }).then(r => r.json());
 setStationen(stRes.stationen || []);
 setEditStation(null);
 }
 } catch { showToast(t('common.error', 'Fehler'), 'error'); }
 setEditSaving(false);
 };

 const deleteStation = async (id) => {
 if (!confirm(t('packTimer.settings.deleteConfirm', 'Station wirklich deaktivieren?'))) return;
 try {
 await fetch(`${API}/api/pack-timer/stationen/${id}`, { method: 'DELETE', credentials: 'include' });
 setStationen(prev => prev.map(s => s.id === id ? { ...s, aktiv: false } : s));
 showToast(t('common.saved', 'Gespeichert'));
 } catch { /* */ }
 };

 if (loading) return <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Laden...')}</div>;

 return (
 <div className="space-y-5">
 {/* General Config */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h3 className="text-sm font-semibold text-content">{t('packTimer.settings.general', 'Allgemein')}</h3>
 </div>
 <div className="px-5 divide-y divide-line">
 <SettingRow setting={{ key: 'pack_timer_enabled', value: config.pack_timer_enabled === 'true', label: t('packTimer.settings.enabled', 'PackTimer aktiviert'), type: 'toggle' }} onChange={(k, v) => saveConfig(k, v ? 'true' : 'false')} saving={saving} t={t} />
 <SettingRow setting={{ key: 'pack_timer_mode', value: config.pack_timer_mode || 'simulator', label: t('packTimer.settings.mode', 'Modus'), type: 'select', options: ['simulator', 'live'], description: t('packTimer.settings.modeDesc', 'Simulator = Demo-Daten, Live = echte Scans') }} onChange={(k, v) => saveConfig(k, v)} saving={saving} t={t} />
 <SettingRow setting={{ key: 'pack_timer_auto_timeout_min', value: config.pack_timer_auto_timeout_min || '15', label: t('packTimer.settings.autoTimeout', 'Auto-Timeout (Minuten)'), type: 'number', description: t('packTimer.settings.autoTimeoutDesc', 'Session wird nach X Minuten ohne Scan automatisch beendet') }} onChange={(k, v) => saveConfig(k, v)} saving={saving} t={t} />
 <SettingRow setting={{ key: 'pack_timer_polling_interval_ms', value: config.pack_timer_polling_interval_ms || '1000', label: t('packTimer.settings.polling', 'Polling-Intervall (ms)'), type: 'number' }} onChange={(k, v) => saveConfig(k, v)} saving={saving} t={t} />
 </div>
 </div>

 {/* Thresholds */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h3 className="text-sm font-semibold text-content">{t('packTimer.settings.thresholds', 'Schwellwerte')}</h3>
 </div>
 <div className="px-5 divide-y divide-line">
 <SettingRow setting={{ key: 'pack_timer_threshold_green', value: config.pack_timer_threshold_green || '50', label: t('packTimer.settings.thresholdGreen', 'Gr\u00fcn-Schwelle (% verbleibend)'), type: 'number', description: t('packTimer.settings.thresholdGreenDesc', 'Timer ist gr\u00fcn wenn mehr als X% der SOLL-Zeit verbleiben') }} onChange={(k, v) => saveConfig(k, v)} saving={saving} t={t} />
 <SettingRow setting={{ key: 'pack_timer_threshold_yellow', value: config.pack_timer_threshold_yellow || '25', label: t('packTimer.settings.thresholdYellow', 'Gelb-Schwelle (% verbleibend)'), type: 'number', description: t('packTimer.settings.thresholdYellowDesc', 'Timer wechselt zu Gelb wenn weniger als X% verbleiben') }} onChange={(k, v) => saveConfig(k, v)} saving={saving} t={t} />
 <SettingRow setting={{ key: 'pack_timer_sound_enabled', value: config.pack_timer_sound_enabled === 'true' || config.pack_timer_sound_enabled === true, label: t('packTimer.settings.sound', 'Sound-Effekte'), type: 'toggle' }} onChange={(k, v) => saveConfig(k, v ? 'true' : 'false')} saving={saving} t={t} />
 </div>
 </div>

 {/* Stations CRUD */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <h3 className="text-sm font-semibold text-content">{t('packTimer.settings.stationen', 'Stationen')}</h3>
 <button
 onClick={() => setEditStation({ name: '', station_nr: (stationen.length + 1), display_modus: 'detail', aktiv: true })}
 className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#338edc] border border-[#338edc]/30 rounded-lg hover:bg-[#338edc]/5"
 >
 + {t('packTimer.settings.neueStation', 'Neue Station')}
 </button>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-line bg-surface">
 <th className="px-4 py-2 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">Nr</th>
 <th className="px-4 py-2 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.settings.name', 'Name')}</th>
 <th className="px-4 py-2 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">PIN</th>
 <th className="px-4 py-2 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">API-Key</th>
 <th className="px-4 py-2 text-left text-[10px] font-medium text-[var(--text-tertiary)] uppercase">{t('packTimer.settings.zugewiesen', 'Zugewiesen')}</th>
 <th className="px-4 py-2 text-center text-[10px] font-medium text-[var(--text-tertiary)] uppercase">Status</th>
 <th className="px-4 py-2 text-right text-[10px] font-medium text-[var(--text-tertiary)] uppercase"></th>
 </tr>
 </thead>
 <tbody>
 {stationen.map(s => (
 <tr key={s.id} className="border-b border-line last:border-0">
 <td className="px-4 py-2.5 text-[var(--text-primary)] font-mono">{s.station_nr}</td>
 <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{s.name}</td>
 <td className="px-4 py-2.5 text-[var(--text-primary)] font-mono font-medium">{s.admin_pin || '-'}</td>
 <td className="px-4 py-2.5 text-[var(--text-tertiary)] font-mono text-xs">{s.api_key ? `${s.api_key.slice(0, 8)}...` : '-'}</td>
 <td className="px-4 py-2.5 text-[var(--text-secondary)]">{s.ma_name || '-'}</td>
 <td className="px-4 py-2.5 text-center">
 <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${s.aktiv ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]'}`}>
 {s.aktiv ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}
 </span>
 </td>
 <td className="px-4 py-2.5 text-right">
 <div className="flex items-center justify-end gap-1">
 <button onClick={() => setEditStation({ ...s })} className="p-1 rounded hover:bg-surface-hover text-[var(--text-tertiary)]" title={t('common.edit', 'Bearbeiten')}>
 <Settings className="w-3.5 h-3.5" />
 </button>
 {s.aktiv && (
 <button onClick={() => deleteStation(s.id)} className="p-1 rounded hover:bg-surface-hover text-red-400" title={t('common.delete', 'L\u00f6schen')}>
 <X className="w-3.5 h-3.5" />
 </button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>

 {/* Edit Station Modal */}
 {editStation && (
 <div className="fixed inset-0 z-50 flex items-center justify-center">
 <div className="absolute inset-0 bg-black/20" onClick={() => setEditStation(null)} />
 <div className="relative bg-surface-elevated rounded-xl border border-line shadow-xl w-full max-w-md p-6 space-y-4">
 <h3 className="text-base font-semibold text-[var(--text-primary)]">
 {editStation.id ? t('packTimer.settings.editStation', 'Station bearbeiten') : t('packTimer.settings.neueStation', 'Neue Station')}
 </h3>
 <div className="space-y-3">
 <div>
 <label className="text-xs text-[var(--text-secondary)]">{t('packTimer.settings.name', 'Name')}</label>
 <input
 value={editStation.name} onChange={(e) => setEditStation(prev => ({ ...prev, name: e.target.value }))}
 className="w-full mt-1 px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30"
 />
 </div>
 <div>
 <label className="text-xs text-[var(--text-secondary)]">Nr</label>
 <input
 type="number" value={editStation.station_nr} onChange={(e) => setEditStation(prev => ({ ...prev, station_nr: parseInt(e.target.value) || 0 }))}
 className="w-full mt-1 px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30"
 />
 </div>
 <div>
 <label className="text-xs text-[var(--text-secondary)]">PIN (4–6 {t('packTimer.settings.digits', 'Ziffern')})</label>
 <input
 value={editStation.admin_pin || ''} onChange={(e) => setEditStation(prev => ({ ...prev, admin_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
 placeholder="1001"
 className="w-full mt-1 px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono"
 />
 </div>
 <div>
 <label className="text-xs text-[var(--text-secondary)]">Display-Modus</label>
 <select
 value={editStation.display_modus} onChange={(e) => setEditStation(prev => ({ ...prev, display_modus: e.target.value }))}
 className="w-full mt-1 px-3 py-1.5 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none"
 >
 <option value="detail">Detail</option>
 <option value="compact">Compact</option>
 </select>
 </div>
 </div>
 <div className="flex justify-end gap-2 pt-2">
 <button onClick={() => setEditStation(null)} className="px-4 py-1.5 text-sm text-[var(--text-secondary)] border border-line rounded-lg hover:bg-surface-hover">{t('common.cancel', 'Abbrechen')}</button>
 <button onClick={saveStation} disabled={editSaving || !editStation.name} className="px-4 py-1.5 text-sm font-medium text-white bg-[#338edc] rounded-lg hover:bg-[#2b7bc4] disabled:opacity-50">{t('common.save', 'Speichern')}</button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════
//  #1041 — Telegram-Routing Tab
// ═══════════════════════════════════════════════════════════════════
function TelegramRoutingTab({ t, showToast }) {
 const [config, setConfig] = useState({ confidence_threshold: 0.75, audit_enabled: true, runtime_threshold: 0.75, runtime_audit: true });
 const [modules, setModules] = useState([]);
 const [log, setLog] = useState({ rows: [], stats: [] });
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [thresholdLocal, setThresholdLocal] = useState(0.75);

 const STAGE_LABELS = {
 callback: t('telegramRouting.stage.callback', 'Callback'),
 active_context: t('telegramRouting.stage.activeContext', 'Active-Context'),
 reply_to: t('telegramRouting.stage.replyTo', 'Reply-To'),
 slash: t('telegramRouting.stage.slash', 'Slash'),
 ai_intent: t('telegramRouting.stage.aiIntent', 'AI-Intent'),
 fallback: t('telegramRouting.stage.fallback', 'Rückfrage'),
 clarify_response: t('telegramRouting.stage.clarifyResponse', 'Rückfrage-Klick'),
 };
 const STAGE_COLORS = {
 callback: 'bg-violet-100 text-violet-700',
 active_context: 'bg-emerald-100 text-emerald-700',
 reply_to: 'bg-cyan-100 text-cyan-700',
 slash: 'bg-blue-100 text-blue-700',
 ai_intent: 'bg-indigo-100 text-indigo-700',
 fallback: 'bg-amber-100 text-amber-700',
 clarify_response: 'bg-amber-100 text-amber-700',
 };

 const loadAll = useCallback(async () => {
 setLoading(true);
 try {
 const [c, m, l] = await Promise.all([
 fetch(`${API}/api/prozesse/telegram/routing-config`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/prozesse/telegram/modules`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/prozesse/telegram/routing-log?limit=50`, { credentials: 'include' }).then(r => r.json()),
 ]);
 setConfig(c);
 setThresholdLocal(c.confidence_threshold);
 setModules(m.modules || []);
 setLog(l);
 } catch {
 showToast(t('common.networkError', 'Netzwerkfehler'));
 } finally {
 setLoading(false);
 }
 }, [showToast, t]);

 useEffect(() => { loadAll(); }, [loadAll]);

 const saveConfig = async (overrides = {}) => {
 setSaving(true);
 try {
 const body = {
 confidence_threshold: overrides.threshold ?? thresholdLocal,
 audit_enabled: overrides.audit ?? config.audit_enabled,
 };
 const res = await fetch(`${API}/api/prozesse/telegram/routing-config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(body),
 });
 if (res.ok) {
 const data = await res.json();
 setConfig(c => ({ ...c, ...body, runtime_threshold: data.runtime_threshold, runtime_audit: data.runtime_audit }));
 showToast(t('common.saved', 'Gespeichert'));
 } else {
 const e = await res.json().catch(() => ({}));
 showToast(e.error || t('common.saveError', 'Speichern fehlgeschlagen'));
 }
 } catch {
 showToast(t('common.networkError', 'Netzwerkfehler'));
 } finally {
 setSaving(false);
 }
 };

 if (loading) {
 return <div className="flex items-center justify-center h-48"><div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;
 }

 return (
 <div className="space-y-4">
 {/* Konfidenz-Schwelle Card */}
 <div className="bg-surface-elevated rounded-card border border-line p-5">
 <div className="flex items-center gap-2 mb-3">
 <Sliders className="w-4 h-4 text-[#338edc]" />
 <h3 className="text-sm font-semibold text-content">{t('telegramRouting.confidenceTitle', 'Konfidenz-Schwelle')}</h3>
 </div>
 <p className="text-xs text-[var(--text-secondary)] mb-4">
 {t('telegramRouting.confidenceDesc', 'AI-Intent muss mindestens diese Konfidenz erreichen. Darunter erscheint ein Rückfrage-Dialog mit Module-Auswahl.')}
 </p>
 <div className="flex items-center gap-4">
 <input
 type="range"
 min="0.5" max="1" step="0.05"
 value={thresholdLocal}
 onChange={e => setThresholdLocal(Number(e.target.value))}
 className="flex-1 accent-[#338edc]"
 />
 <div className="text-sm font-mono w-20 text-center bg-[var(--bg-inset,#F8F9FC)] border border-line rounded-md py-1">{thresholdLocal.toFixed(2)}</div>
 <button
 onClick={() => saveConfig({ threshold: thresholdLocal })}
 disabled={saving || thresholdLocal === config.confidence_threshold}
 className="px-4 py-1.5 text-sm font-medium text-white bg-[#338edc] rounded-lg hover:bg-[#2b7bc4] disabled:opacity-50"
 >{saving ? '…' : t('common.save', 'Speichern')}</button>
 </div>
 <div className="mt-3 text-xs text-[var(--text-tertiary)] flex items-center gap-2">
 <span>Runtime: <strong className="text-content">{config.runtime_threshold?.toFixed?.(2) ?? config.runtime_threshold}</strong></span>
 <span>•</span>
 <span>{t('telegramRouting.defaultHint', 'Default 0.75')}</span>
 </div>
 </div>

 {/* Audit Toggle Card */}
 <div className="bg-surface-elevated rounded-card border border-line p-5">
 <div className="flex items-center justify-between">
 <div>
 <h3 className="text-sm font-semibold text-content mb-1">{t('telegramRouting.auditTitle', 'Audit-Log')}</h3>
 <p className="text-xs text-[var(--text-secondary)]">{t('telegramRouting.auditDesc', 'Alle Routing-Entscheidungen in telegram_routing_log mitschreiben.')}</p>
 </div>
 <ToggleSwitch
 checked={!!config.audit_enabled}
 onChange={(checked) => saveConfig({ audit: checked })}
 disabled={saving}
 />
 </div>
 </div>

 {/* Modules Overview Card */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h3 className="text-sm font-semibold text-content">{t('telegramRouting.modulesTitle', 'Registrierte Module')}</h3>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-[var(--bg-inset,#F8F9FC)] text-left text-[var(--text-tertiary)] uppercase">
 <tr>
 <th className="px-4 py-2 font-medium">{t('telegramRouting.col.module', 'Modul')}</th>
 <th className="px-4 py-2 font-medium">{t('telegramRouting.col.actionTypes', 'Active-Context')}</th>
 <th className="px-4 py-2 font-medium">{t('telegramRouting.col.callbacks', 'Callbacks')}</th>
 <th className="px-4 py-2 font-medium">{t('telegramRouting.col.slash', 'Slash')}</th>
 <th className="px-4 py-2 font-medium">{t('telegramRouting.col.intents', 'Intents')}</th>
 </tr>
 </thead>
 <tbody>
 {modules.map(m => (
 <tr key={m.name} className="border-t border-line">
 <td className="px-4 py-2 font-medium text-content">{m.name}<div className="text-[10px] text-[var(--text-tertiary)]">{m.intentDescription}</div></td>
 <td className="px-4 py-2 font-mono text-[10px]">{(m.actionTypes || []).join(', ') || '—'}</td>
 <td className="px-4 py-2 font-mono text-[10px]">{(m.callbackPrefixes || []).join(', ') || '—'}</td>
 <td className="px-4 py-2 font-mono text-[10px]">{(m.slashCommands || []).join(', ') || '—'}</td>
 <td className="px-4 py-2 font-mono text-[10px]">{(m.intents || []).join(', ') || '—'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>

 {/* Stats Card */}
 {(log.stats || []).length > 0 && (
 <div className="bg-surface-elevated rounded-card border border-line p-5">
 <h3 className="text-sm font-semibold text-content mb-3">{t('telegramRouting.statsTitle', 'Routing-Statistik (7 Tage)')}</h3>
 <div className="flex flex-wrap gap-2">
 {log.stats.map(s => (
 <div key={s.stage} className={`px-3 py-1 rounded-full text-xs font-medium ${STAGE_COLORS[s.stage] || 'bg-gray-100 text-gray-700'}`}>
 {STAGE_LABELS[s.stage] || s.stage}: {s.cnt}
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Routing Log Table */}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <h3 className="text-sm font-semibold text-content">{t('telegramRouting.logTitle', 'Letzte 50 Routing-Entscheidungen')}</h3>
 <button onClick={loadAll} className="text-xs text-[#338edc] hover:underline flex items-center gap-1">
 <RefreshCw className="w-3 h-3" />{t('common.reload', 'Neu laden')}
 </button>
 </div>
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-[var(--bg-inset,#F8F9FC)] text-left text-[var(--text-tertiary)] uppercase">
 <tr>
 <th className="px-3 py-2 font-medium">{t('telegramRouting.col.time', 'Zeit')}</th>
 <th className="px-3 py-2 font-medium">{t('telegramRouting.col.stage', 'Stufe')}</th>
 <th className="px-3 py-2 font-medium">{t('telegramRouting.col.module', 'Modul')}</th>
 <th className="px-3 py-2 font-medium">{t('telegramRouting.col.intent', 'Intent')}</th>
 <th className="px-3 py-2 font-medium text-right">{t('telegramRouting.col.confidence', 'Konfidenz')}</th>
 <th className="px-3 py-2 font-medium">{t('telegramRouting.col.text', 'Text')}</th>
 </tr>
 </thead>
 <tbody>
 {(log.rows || []).map(r => (
 <tr key={r.id} className="border-t border-line">
 <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap">{r.decided_at ? new Date(r.decided_at).toLocaleString('de-AT') : '—'}</td>
 <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STAGE_COLORS[r.stage] || 'bg-gray-100 text-gray-700'}`}>{STAGE_LABELS[r.stage] || r.stage}</span></td>
 <td className="px-3 py-2 text-content">{r.module_name || '—'}</td>
 <td className="px-3 py-2 font-mono text-[10px]">{r.ai_intent || '—'}</td>
 <td className="px-3 py-2 font-mono text-right">{r.confidence != null ? Number(r.confidence).toFixed(2) : '—'}</td>
 <td className="px-3 py-2 max-w-md truncate" title={r.raw_text}>{r.raw_text || '—'}</td>
 </tr>
 ))}
 {(!log.rows || log.rows.length === 0) && (
 <tr><td colSpan={6} className="px-3 py-12 text-center text-[var(--text-tertiary)]">{t('telegramRouting.noLog', 'Noch keine Routing-Entscheidungen.')}</td></tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 </div>
 );
}

export default function Einstellungen() {
 const { t } = useLanguage();
 const [searchParams] = useSearchParams();
 const initialTab = TAB_DEFS.find(td => td.key === searchParams.get('tab'))?.key || 'allgemein';
 const [activeTab, setActiveTab] = useState(initialTab);
 const [kategorien, setKategorien] = useState({});
 const [hiddenTelemetryCount, setHiddenTelemetryCount] = useState(0);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(null);
 const [toast, setToast] = useState(null);
 const TABS = TAB_DEFS.map(tab => ({ ...tab, label: t(tab.labelKey) }));

 const showToast = useCallback((msg) => {
 setToast(msg);
 setTimeout(() => setToast(null), 2500);
 }, []);

 const loadSettings = useCallback(() => {
 fetch(`${API}/api/einstellungen`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 setKategorien(d.kategorien || {});
 setHiddenTelemetryCount(Number(d.hidden_telemetry_count || 0));
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 useEffect(() => { loadSettings(); }, [loadSettings]);

 const handleChange = useCallback(async (key, newValue) => {
 setSaving(key);
 try {
 const res = await fetch(`${API}/api/einstellungen/${key}`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ value: newValue }),
 });
 if (res.ok) {
 const data = await res.json();
 setKategorien(prev => {
 const next = { ...prev };
 for (const cat of Object.keys(next)) {
 next[cat] = next[cat].map(s =>
 s.key === key ? { ...s, value: data.value } : s
 );
 }
 return next;
 });
 showToast(t('common.saved'));
 } else {
 const err = await res.json().catch(() => ({}));
 showToast(err.error || t('common.saveError'));
 }
 } catch {
 showToast(t('common.networkError'));
 } finally {
 setSaving(null);
 }
 }, [showToast, t]);

 if (loading) {
 return (
 <div className="flex items-center justify-center h-64">
 <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 const currentSettings = kategorien[activeTab] || [];

 return (
 <div className="p-6">
 {/* Toast */}
 {toast && (
 <div className="fixed top-3 right-4 z-[100]">
 <div className="bg-surface-elevated border border-line rounded-card px-4 py-2.5 shadow-elevated flex items-center gap-2">
 <span className="text-xs text-content-secondary">{toast}</span>
 </div>
 </div>
 )}

 {/* Header */}
 <div className="mb-6">
 <h1 className="text-xl font-bold text-content">{t('settings.title')}</h1>
 <p className="text-sm text-[var(--text-secondary)] mt-1">{t('settings.subtitle')}</p>
 </div>

 {/* Tabs */}
 <div className="flex gap-1 mb-6 bg-surface-elevated rounded-card p-1 border border-line overflow-x-auto">
 {TABS.map(tab => (
 <button
 key={tab.key}
 onClick={() => setActiveTab(tab.key)}
 className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
 activeTab === tab.key
 ? 'bg-surface text-content shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover'
 }`}
 >
 <tab.Icon className="w-4 h-4" />
 {tab.label}
 </button>
 ))}
 </div>

 {/* Content */}
 {activeTab === 'system' ? (
 <SystemInfoTab t={t} />
 ) : activeTab === 'design' ? (
 <ThemeEditorTab t={t} showToast={showToast} />
 ) : activeTab === 'adressklaerung' ? (
 <AdressklaerungTab t={t} showToast={showToast} />
 ) : activeTab === 'kunden' ? (
 <KundenKlassifizierungTab t={t} showToast={showToast} />
 ) : activeTab === 'abwesenheitsarten' ? (
 <AbwesenheitsartenTab t={t} showToast={showToast} />
 ) : activeTab === 'sms' ? (
 <SmsTab t={t} showToast={showToast} />
 ) : activeTab === 'home_desktop' ? (
 <HomeDesktopTab t={t} showToast={showToast} />
 ) : activeTab === 'stempeluhr' ? (
 <StempeluhrTab t={t} showToast={showToast} />
 ) : activeTab === 'abteilungen' ? (
 <AbteilungenKvTab t={t} showToast={showToast} />
 ) : activeTab === 'system_audit' ? (
 <SystemAuditTab t={t} showToast={showToast} />
 ) : activeTab === 'pack_timer' ? (
 <PackTimerTab t={t} showToast={showToast} />
 ) : activeTab === 'telegram_routing' ? (
 <TelegramRoutingTab t={t} showToast={showToast} />
 ) : (
 <>
 {activeTab === 'allgemein' && <BrandingSection t={t} showToast={showToast} />}
 {activeTab === 'allgemein' && <BerechnungsgrundlagenCard t={t} showToast={showToast} />}
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content">
 {(() => { const T = TABS.find(t => t.key === activeTab); return T ? <><T.Icon className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />{T.label}</> : null; })()}
 </h2>
 </div>
 <div className="px-5">
 {currentSettings.length === 0 ? (
 <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">
 {t('settings.noSettings')}
 </div>
 ) : (
 currentSettings.map(s => (
 <SettingRow
 key={s.key}
 setting={s}
 onChange={handleChange}
 saving={saving}
 t={t}
 />
 ))
 )}
 </div>
 </div>
 {hiddenTelemetryCount > 0 && (
 <div className="mt-3 text-xs text-[var(--text-tertiary)] bg-surface-elevated/60 border border-line rounded-card px-4 py-2.5 flex items-center gap-2">
 <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
 <span>{hiddenTelemetryCount} Cronjob-Telemetrie-Werte ausgeblendet (last_duration_ms / last_status / last_run). Siehe <a href="/einstellungen?tab=system_audit" className="text-[#338edc] hover:underline">System-Audit</a>.</span>
 </div>
 )}
 </>
 )}
 </div>
 );
}
